import axios from "axios";
import { localizedPath } from "../hooks/useLocalizedNavigate";
import { ROUTE_MAP } from "../constants/routes";
import { isCacheableUrl, isDedupOnlyUrl } from "./cacheableUrls";
import { attachApiModules } from "./modules";
// [lista dozwolonych logowań 2026-10-04] Zdanie z odmowy odświeżenia sesji (kod `lista_logowan`) trafia na ekran logowania.
import { zapamietajPowodWylogowania } from "../utils/powodWylogowania";
// [O2 2026-09-13] Wybrany dział i zakładka „Mojego Działu" doklejane do GET-ów modułu (patrz nagłówek pliku).
import { dolozZakresDoZapytania } from "./zakresMojegoDzialu";
// [analityka1005] Każde żądanie mówi serwerowi, czy wysłał je człowiek, czy sama otwarta karta (nagłówek X-Aktywnosc).
import { oznaczZadanie } from "../utils/znacznikAktywnosci";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

// ═══════════════════════════════════════════════════════════════
// Fix pętli przeładowań strony logowania (BRIEF-0014)
// Pełna nawigacja na login TYLKO gdy NIE jesteśmy już na stronie
// logowania (OBIE wersje językowe trasy). Bez tego nieautoryzowane
// wołanie na stronie logowania → 401 → reload → remount → 401 → …
// pętla przeładowań co ~0,5 s.
// ═══════════════════════════════════════════════════════════════
/** [HASLA-LOKALNE 2026-09-27] Kod odmowy serwera (WymuszonaZmianaHaslaMiddleware) i zdarzenie okna, którym go ogłaszamy. */
export const KOD_HASLO_DO_ZMIANY = "haslo_do_zmiany";
export const ZDARZENIE_HASLO_DO_ZMIANY = "password-change-required";

const redirectToLoginIfNeeded = () => {
    const loginPaths = [ROUTE_MAP.login.en, ROUTE_MAP.login.pl];
    if (!loginPaths.includes(window.location.pathname)) {
        window.location.href = localizedPath("login");
    }
};

// Bazowy URL do plików statycznych (bez sufiksu /api) — jedno źródło prawdy dla linków do uploadów.
export const FILES_BASE_URL = API_URL.replace(/\/api\/?$/, "");
export { API_URL };

/**
 * [A15-22] Pełny adres pliku serwowanego przez API (załącznik, obraz, skan).
 *
 * BYŁO ZLE: bazę do linków plikowych liczyło sobie samodzielnie kilkanaście komponentów i stron,
 * na trzy różne sposoby — wyrażeniem regularnym z wartością domyślną, wycięciem pierwszego „/api"
 * BEZ wartości domyślnej, albo inną wartością domyślną niż reszta aplikacji.
 * SKUTEK DLA UŻYTKOWNIKA: uruchomienie frontu bez pliku .env (nowe środowisko, nowa osoba
 * w zespole) dawało w Kanbanie linki do załączników postaci „undefined/uploads/…" — przycisk
 * pobierania po prostu nie działał. Każda przyszła zmiana hosta albo prefiksu API psuła część
 * linków do załączników i obrazów, mimo że samo API działało poprawnie.
 * NAPRAWA: jedna funkcja licząca adres pliku z FILES_BASE_URL. Sklejanie zachowuje się dokładnie
 * jak dotychczasowe `${baza}/${sciezka}` — wiodący ukośnik w ścieżce jest ucinany, żeby nie
 * powstało „//uploads".
 *
 * @param {string} relPath ścieżka zapisana w bazie, np. "uploads/kanban/plik.pdf"
 * @returns {string} pełny adres albo "" gdy ścieżki nie ma
 */
export const fileUrl = (relPath) => {
    if (!relPath) return "";
    const path = String(relPath).replace(/^\/+/, "");
    return `${FILES_BASE_URL}/${path}`;
};

const api = axios.create({
    baseURL: API_URL,
    timeout: 30000,
    withCredentials: true, // Wysyłaj httpOnly cookies z każdym requestem
});

// ═══════════════════════════════════════════════════════════════
// Jednolity kontrakt błędu "brak/wygasłe połączenie Microsoft"
// Backend (GlobalExceptionFilter) zwraca HTTP 409 z ciałem:
// { status:409, code:"ms_connection_required", needsReconnect:true, integrationType }
// Konsument: isMsConnectionRequired(err) → pokaż prompt "Połącz ponownie".
// ═══════════════════════════════════════════════════════════════
export const isMsConnectionRequired = (error) =>
    error?.response?.status === 409 &&
    error?.response?.data?.code === "ms_connection_required";

// ═══════════════════════════════════════════════════════════════
// SignalR token — trzymany w pamięci (NIE w localStorage)
// SignalR nie obsługuje cookies, więc token jest przekazywany
// w query string. Aktualizowany na login i refresh.
// ═══════════════════════════════════════════════════════════════
let _signalRToken = null;
export const setSignalRToken = (token) => { _signalRToken = token; };
export const getSignalRToken = () => _signalRToken;

// ═══════════════════════════════════════════════════════════════
// GET Request Deduplication + Response Cache (prevents 429 cascades)
// ═══════════════════════════════════════════════════════════════
// url → { promise, uniewazniony } — patrz `_wyslijIZarejestruj` i `api.invalidateCache`:
// wpis jest OBIEKTEM, a nie samą obietnicą, bo unieważnienie cache musi umieć oznaczyć żądanie
// będące JUŻ w locie jako „przedawnione" (jego odpowiedź pochodzi sprzed zapisu).
const _inflight = new Map();
const _cache = new Map();      // url → { data, ts }

/**
 * Odpowiedź z GŁĘBOKĄ KOPIĄ pola `data`. Status i nagłówki zostają współdzielone (są niemutowalne
 * w praktyce), kopiujemy wyłącznie dane, bo to one bywają sortowane w miejscu przez strony.
 */
const _odpowiedzZKopiaDanych = (odpowiedz) => ({
    ...odpowiedz,
    data: typeof structuredClone === "function"
        ? structuredClone(odpowiedz.data)
        : JSON.parse(JSON.stringify(odpowiedz.data)),
});
const CACHE_TTL = 60_000;      // 60s default cache TTL

// [A15-23] BYŁO ZLE: stał tu pomocnik `_getCacheKey(config)`, którego nikt nie wołał — klucz cache
// liczy `cachedGet` u siebie (patrz niżej: `const cacheKey = fullUrl + ...`). SKUTEK DLA CZŁOWIEKA:
// dotyka programisty, nie użytkownika — kto poprawiał ten pomocnik (np. żeby uwzględnić nagłówki
// w kluczu), nie widział żadnego efektu i szukał błędu w cache, który był gdzie indziej.
// NAPRAWA: martwy pomocnik usunięty; klucz cache ma jedno, widoczne miejsce powstawania.

// Endpoints that benefit from caching (large, rarely-changing lists)
// [A15-05] Lista dozwolonych ścieżek i samo dopasowanie mieszkają w cacheableUrls.js.
// Dopasowanie idzie po PEŁNEJ ŚCIEŻCE, nie po podciągu adresu — wcześniej wzorzec listy
// (np. "/admin/users") obejmował także jej pod-zasoby ("/admin/users/5/permissions"),
// przez co po zmianie danych ekran przez minutę pokazywał stan sprzed zmiany.
const _isCacheable = (url) => isCacheableUrl(url);

/* ═══════════════════════════════════════════════════════
   [WYDAJNOSC 2026-09-12] SKLEJANIE RÓWNOLEGŁYCH GET-ów JEST TERAZ DOMYŚLNE.

   CO ZMIERZYŁEM NA PRODUKCJI (dziennik, 3 dni, 49 863 żądania): wejście na zakładkę HR wypuszcza
   60 żądań w JEDNEJ sekundzie, w tym OSIEM razy to samo `GET /api/hr-requests/pending`, cztery razy
   `/api/hr-config/request-types`, cztery razy `/api/hr-requests/team`. Wejście na pulpit: 66 żądań
   w sekundzie, prawie każde po 2–3 razy. W całym ruchu 9,3% GET-ów to DOKŁADNE duplikaty w tej samej
   sekundzie. Użytkownicy nazywają to „topornie działa menu”.

   DLACZEGO POPRZEDNIA WERSJA TEGO NIE ŁAPAŁA: sklejanie działało wyłącznie dla adresów z ręcznej listy
   (`DEDUP_ONLY_PATHS`), na której stał JEDEN adres — `/module-visibility`. Każdy inny endpoint dublował
   się do woli. Lista okazała się złym narzędziem: nikt nie dopisuje do niej adresu, którego duplikatu
   jeszcze nie zauważył.

   DLACZEGO TO JEST BEZPIECZNE: sklejamy wyłącznie żądania BĘDĄCE W LOCIE — takie, które i tak wracają
   z serwera z tą samą odpowiedzią, bo dzieli je kilkadziesiąt milisekund. NIC nie jest zapamiętywane:
   każde wywołanie po powrocie poprzedniego idzie do serwera na świeżo, więc zmiana zapisana przez
   człowieka jest widoczna natychmiast. To NIE jest cache (ten dalej działa tylko dla listy adresów
   z `CACHEABLE_PATHS`) — to jest niewysyłanie drugiego pytania, gdy pierwsze jeszcze nie wróciło.

   CZEGO NIE WOLNO SKLEJAĆ (bez tych wyjątków sklejanie psuje zachowanie, a nie tylko wydajność):
   • żądania z `signal`/`cancelToken` — gdy JEDEN konsument przerwie swoje żądanie, przerwałby je
     wszystkim, którzy się do niego dokleili, i dostaliby błąd anulowania bez powodu;
   • pobieranie plików (`responseType` blob/arraybuffer) — Blob nie przechodzi przez kopiowanie danych,
     a dwóch konsumentów tego samego pliku to dwa osobne pobrania;
   • żądania z podpiętym postępem (`onDownloadProgress`/`onUploadProgress`) — wywołania zwrotne należą
     do konkretnego konsumenta;
   • jawna rezygnacja `{ bezSklejania: true }` dla wywołań, które MUSZĄ trafić do serwera osobno.
   ═══════════════════════════════════════════════════════ */

/** Ile razy sklejono równoległe żądanie (diagnostyka + zapadka; nie ma wpływu na zachowanie). */
let _sklejonych = 0;
export const licznikSklejonychZadan = () => _sklejonych;
export const zerujLicznikSklejonychZadan = () => { _sklejonych = 0; };

/** Czy dla tego wywołania wolno skleić równoległe żądanie. Patrz lista wyjątków w komentarzu wyżej. */
const _wolnoSklejac = (config) => !(
    config?.signal
    || config?.cancelToken
    || (config?.responseType && config.responseType !== "json")
    || config?.onDownloadProgress
    || config?.onUploadProgress
    || config?.bezSklejania === true
);

// Wrap GET requests to deduplicate in-flight and cache responses
const _originalGet = api.get.bind(api);

/* ═══════════════════════════════════════════════════════
   [WYDAJNOSC 2026-09-13] UNIEWAŻNIENIE MUSI DOTYCZYĆ TAKŻE ŻĄDAŃ BĘDĄCYCH W LOCIE.

   BYŁO ZLE — i to usterka MOJEJ zmiany z 2026-09-12, która włączyła sklejanie na WSZYSTKICH adresach
   (patrz komentarz o sklejaniu wyżej): `invalidateCache` czyściło `_cache`, ale NIGDY `_inflight`. Skutek:
   1) strona zapisuje zmianę (POST/PUT), 2) zaraz po zapisie odświeża listę — i to odświeżenie
   DOKLEJA SIĘ do GET-a, który poszedł do serwera jeszcze PRZED zapisem, więc dostaje stan SPRZED
   zmiany; 3) dla adresu cache'owalnego tamten przedawniony GET po powrocie zapisywał jeszcze ten
   stary stan do `_cache` ze ŚWIEŻYM znacznikiem czasu — czyli podawał go przez kolejne 60 s.
   SKUTEK DLA CZŁOWIEKA: „zapisałem, a lista pokazuje starą wartość i nie da się tego odświeżyć".
   Im szybciej ktoś klikał Zapisz → Odśwież, tym pewniej w to wchodził.

   NAPRAWA: unieważnienie (1) usuwa pasujące wpisy z `_inflight`, więc NIKT nowy już się do
   przedawnionego żądania nie doklei — każde następne wywołanie idzie do serwera na świeżo, oraz
   (2) oznacza to żądanie flagą `uniewazniony`, dzięki której jego odpowiedź NIE trafia do `_cache`.
   Czego świadomie NIE robimy: nie przerywamy żądania w locie. Konsument, który już na nie czeka,
   dostaje swoją odpowiedź (przerwanie dałoby mu błąd anulowania bez powodu) — traci na tym tylko
   ten jeden, wcześniej wysłany odczyt, a nie nikt późniejszy.
   ═══════════════════════════════════════════════════════ */

/**
 * Wysyła GET i rejestruje go jako „w locie" (pod `cacheKey`), żeby równolegli konsumenci mogli się
 * doklejać. Wpis jest usuwany z rejestru dopiero wtedy, gdy to NADAL jest ten sam wpis — inaczej
 * kończące się przedawnione żądanie wyrzuciłoby z rejestru świeże, wysłane po unieważnieniu.
 *
 * @param {boolean} zapiszDoCache czy UDANĄ odpowiedź zapamiętać w `_cache` (tylko adresy cache'owalne)
 */
const _wyslijIZarejestruj = (url, config, cacheKey, zapiszDoCache) => {
    const wpis = { promise: null, uniewazniony: false };
    const wyrejestruj = () => { if (_inflight.get(cacheKey) === wpis) _inflight.delete(cacheKey); };

    wpis.promise = _originalGet(url, config)
        .then((response) => {
            // BYŁO ŹLE: do cache trafiał TEN SAM obiekt, który dostawał pierwszy konsument. Kopia
            // robiła się wyłącznie przy ODCZYCIE, więc strona wołająca `res.data.sort(...)` albo
            // `.reverse()` (sortowanie w miejscu na liście) zatruwała wpis w cache.
            // SKUTEK DLA UŻYTKOWNIKA: po posortowaniu listy każde następne wejście na tę stronę
            // w oknie 60 s pokazywało dane w cudzej kolejności — i nie dało się tego cofnąć inaczej
            // niż czekając, aż cache wygaśnie.
            // NAPRAWA: kopiujemy przy ZAPISIE. Pierwszy konsument dostaje swój obiekt jak dotąd,
            // a cache trzyma egzemplarz, którego nikt z zewnątrz nie trzyma i nie może zmienić.
            // `uniewazniony` = w trakcie tego żądania ktoś zapisał zmianę, więc ta odpowiedź jest
            // sprzed zapisu; wolno ją oddać temu, kto już czeka, ale NIE WOLNO jej zapamiętać.
            if (zapiszDoCache && !wpis.uniewazniony) {
                _cache.set(cacheKey, { data: _odpowiedzZKopiaDanych(response), ts: Date.now() });
            }
            wyrejestruj();
            return response;
        })
        .catch((err) => {
            wyrejestruj();
            throw err;
        });

    _inflight.set(cacheKey, wpis);
    return wpis.promise;
};

api.get = function cachedGet(url, config = {}) {
    // [O2] Zawężenie do działu/zakładki MUSI wejść PRZED kluczem sklejania i pamięci — inaczej zapytanie o dział B
    // dostałoby odpowiedź wysłaną dla działu A (ten sam adres, inne `params`).
    config = dolozZakresDoZapytania(url, config);
    const fullUrl = `${API_URL}${url}`;
    const cacheKey = fullUrl + (config.params ? JSON.stringify(config.params) : "");

    // Only cache/dedup GET requests to cacheable endpoints
    if (_isCacheable(url)) {
        // Return cached response if fresh
        const cached = _cache.get(cacheKey);
        if (cached && Date.now() - cached.ts < CACHE_TTL) {
            return Promise.resolve(_odpowiedzZKopiaDanych(cached.data));
        }

        // Return existing in-flight promise (dedup) — każdy równoległy konsument dostaje WŁASNĄ kopię,
        // inaczej dwie strony czekające na to samo żądanie mutowałyby sobie nawzajem ten sam obiekt.
        // W rejestrze stoją WYŁĄCZNIE żądania nieprzedawnione: unieważnienie usuwa z niego wpis,
        // więc odczyt po zapisie nie ma się do czego dokleić i idzie do serwera.
        const wLocie = _inflight.get(cacheKey);
        if (wLocie) {
            return wLocie.promise.then(_odpowiedzZKopiaDanych);
        }

        // Make the actual request and store as in-flight
        return _wyslijIZarejestruj(url, config, cacheKey, true);
    }

    // [A15-11] Sklejanie RÓWNOLEGŁYCH żądań bez zapamiętywania odpowiedzi.
    // BYŁO ZLE: trzy niezależne miejsca (AuthContext, Sidebar, UIContext) pobierały widoczność
    // modułów w tej samej chwili, więc każde wejście do aplikacji i każde F5 wysyłało trzy
    // identyczne zapytania. SKUTEK DLA UŻYTKOWNIKA: przy kilkuset zalogowanych osobach to stałe,
    // potrójne obciążenie bazy — aplikacja wolniej odpowiada wszystkim w godzinach szczytu.
    // NAPRAWA: równolegli konsumenci dzielą jedną odpowiedź, ale NIC nie ląduje w cache —
    // następne wywołanie idzie do serwera na świeżo, więc zmiana przełącznika przez administratora
    // jest widoczna natychmiast, dokładnie jak przed naprawą.
    if (isDedupOnlyUrl(url) || _wolnoSklejac(config)) {
        const wLocie = _inflight.get(cacheKey);
        if (wLocie) {
            // Własna kopia dla każdego konsumenta — inaczej jeden mógłby zmienić dane drugiemu.
            _sklejonych += 1;
            return wLocie.promise.then(_odpowiedzZKopiaDanych);
        }
        // `false` = niczego nie zapamiętujemy; sklejamy tylko to, co jest w locie.
        return _wyslijIZarejestruj(url, config, cacheKey, false);
    }

    return _originalGet(url, config);
};

// Expose cache invalidation for manual refresh
api.invalidateCache = (pattern) => {
    for (const key of _cache.keys()) {
        if (!pattern || key.includes(pattern)) _cache.delete(key);
    }
    // Żądania BĘDĄCE W LOCIE są tak samo przedawnione jak wpisy w cache — ich odpowiedź pochodzi
    // sprzed zapisu. Wyrzucamy je z rejestru sklejania (nikt nowy się nie doklei) i oznaczamy, żeby
    // ich odpowiedź nie wpadła do cache. Usuwanie w trakcie iteracji po Map jest bezpieczne —
    // usunięty wpis po prostu nie zostanie odwiedzony drugi raz.
    for (const [key, wpis] of _inflight) {
        if (!pattern || key.includes(pattern)) {
            wpis.uniewazniony = true;
            _inflight.delete(key);
        }
    }
};

api.interceptors.request.use(
    (config) => {
        // Tokeny lecą automatycznie przez httpOnly cookies (withCredentials: true)
        // NIE ustawiamy Authorization headera — cookies obsługują uwierzytelnianie

        if (
            config.data &&
            !(config.data instanceof FormData) &&
            ["post", "put", "patch"].includes(config.method?.toLowerCase())
        ) {
            config.headers["Content-Type"] = "application/json";
        }

        return oznaczZadanie(config);
    },
    (error) => {
        return Promise.reject(error);
    }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

/* Nowa lista uprawnień z udanego /users/refresh: localStorage + zdarzenie okna, gdy się zmieniła (opis przy przechwytywaczu 401,
   [brief 2026-09-03, poz. 10]). Jedno miejsce dla przechwytywacza i dla odświeżenia huba (P5-R5). */
const _zastosujUprawnieniaPoOdswiezeniu = (permissions) => {
    if (!permissions) return;
    const poprzednie = localStorage.getItem("permissions");
    const nowe = JSON.stringify(permissions);
    localStorage.setItem("permissions", nowe);
    if (poprzednie !== nowe) {
        window.dispatchEvent(new CustomEvent("uprawnienia-zmienione", {
            detail: { permissions },
        }));
    }
};

/* ── [P5-R5, przegląd poprawek KA-12 × KA-04 2026-09-26] Zapobiegawcze odświeżenie sesji dla huba SignalR ──
   BYŁO ŹLE: token huba wygasający w ciągu minuty „odświeżaliśmy” przez GET /users/me — a od KA-04 /me wydaje token, który
   wygasa NAJPÓŹNIEJ razem z tokenem wywołującym, czyli z TYM SAMYM terminem. Serwer (CloseOnAuthenticationExpiration) i tak
   zamykał połączenie z `exp`, a powiadomienia wracały dopiero po 401 → /refresh → ponownym /me — z przerwą.
   NAPRAWA: rotacja sesji przez POST /users/refresh (ciasteczko HttpOnly), JEDNA naraz z przechwytywaczem 401: gdy odświeżenie
   już trwa, czekamy na nie w tej samej kolejce, zamiast rotować refresh token drugi raz (drugi obrót = „replay”).
   Zwraca nowy token huba; odrzuca, gdy odświeżenie się nie udało (400/401 = sesja nieważna → jak w przechwytywaczu). */
export const odswiezSesjeDlaHuba = () => {
    if (isRefreshing) {
        return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
        }).then(() => _signalRToken);
    }
    isRefreshing = true;
    return api
        .post("/users/refresh", {})
        .then((res) => {
            const accessToken = res && res.data ? res.data.accessToken : null;
            if (accessToken) _signalRToken = accessToken;
            _zastosujUprawnieniaPoOdswiezeniu(res && res.data ? res.data.permissions : null);
            processQueue(null);
            return _signalRToken;
        })
        .catch((err) => {
            processQueue(err, null);
            const st = err?.response?.status;
            if (st === 400 || st === 401) {
                zapamietajPowodWylogowania(err);
                ["user", "permissions"].forEach(k => localStorage.removeItem(k));
                _signalRToken = null;
                redirectToLoginIfNeeded();
            }
            throw err;
        })
        .finally(() => {
            isRefreshing = false;
        });
};

/* [P2-X6/P2-X7, poprawka 2 przeglądu bezpieczeństwa 2026-09-26] PIN ADMINISTRATORA NA ŻĄDANIE.
   Polecenie właściciela: „wykonaj mi testy w zakresie bezpieczeństwa danych aplikacji ... a jak są błędy, to naprawione”.
   BYŁO ŹLE: serwer wymaga PIN-u (RequireAdminPinFilter, tylko ranga 3) na zapisach nadających dostęp, a PIN dało się podać
   WYŁĄCZNIE na bramce całej strony (AdminPinGate w sekcji „System i integracje”). Ten sam zapis osadzony gdzie indziej —
   skład Zarządu w e-teczce, dostęp do modułu i obsada desku w Konfiguratorze zgłoszeń — kończył się 403 „Odśwież stronę
   i podaj PIN”, a na tym ekranie PIN-u podać się nie dało.
   TERAZ: odpowiedź 403 z kodem `admin_pin_required` otwiera okno PIN-u (PinNaZadanie, zamontowane raz w App). Po poprawnym
   PIN-ie (serwer ustawia to samo ciasteczko, co bramka strony) żądanie ponawia się JEDEN raz; zamknięcie okna = odmowa
   z czytelnym komunikatem. Bez zarejestrowanej obsługi (testy, ekrany publiczne) zachowanie jest jak dotąd. Kod
   `admin_pin_individual_required` NIE otwiera okna — żaden PIN go nie zmieni. */
export const KOD_PIN_WYMAGANY = "admin_pin_required";
export const KOMUNIKAT_PIN_ANULOWANY = "Ten zapis wymaga potwierdzenia PIN-em administratora — okno PIN-u zamknięto bez potwierdzenia.";
let _obslugaPinu = null;
/** Rejestruje obsługę „poproś o PIN” (funkcja zwracająca Promise<boolean>). Zwraca funkcję wyrejestrowania. */
export const zarejestrujObslugePinu = (fn) => {
    _obslugaPinu = typeof fn === "function" ? fn : null;
    return () => {
        if (_obslugaPinu === fn) _obslugaPinu = null;
    };
};

api.interceptors.response.use(
    (response) => {
        // Inwalidacja cache GET po udanych mutacjach (zapobiega stale danym do 60 s po edycji).
        const method = response.config?.method?.toLowerCase();
        if (method && ["post", "put", "patch", "delete"].includes(method)) {
            api.invalidateCache();
        }
        return response;
    },
    async (error) => {
        // ── Backend niedostępny — sygnał do globalnego down-detektora ──
        // Dwa oblicza tej samej awarii:
        //   1) BRAK response  = połączenie odrzucone / DNS / zerwany socket (proxy TEŻ padł).
        //   2) 502/503/504    = nginx żyje, ale usługa .NET za nim padła i zwraca bramkowy błąd
        //      z ciałem — tu error.response ISTNIEJE, więc stary warunek !error.response tego
        //      nie łapał. To był powód „ekran przerwy pojawia się dopiero po F5": event nie
        //      leciał, a poll co 15 s dawał do 15 s zastygłego, zalogowanego UI.
        // Ignorujemy: celowe aborty (ERR_CANCELED), timeout pojedynczego requestu (ECONNABORTED,
        // np. wolny upload — to NIE awaria backendu) oraz sam /health (obsługiwany osobno).
        // Ostateczną decyzję o trybie offline i tak podejmuje re-weryfikacja /health w kontekście —
        // pojedynczy 502 nie wywali UI, jeśli /health odpowie poprawnie.
        const gatewayDown = error.response && [502, 503, 504].includes(error.response.status);
        if ((!error.response || gatewayDown)
            && error.code !== "ERR_CANCELED"
            && error.code !== "ECONNABORTED"
            && !error.config?.url?.includes("/health")) {
            window.dispatchEvent(new Event("backend-network-error"));
            return Promise.reject(error);
        }

        const originalRequest = error.config;

        // ── 409 ms_connection_required — brak/wygasłe połączenie Microsoft (jednolity kontrakt) ──
        // To NIE jest błąd sesji aplikacji (nie odświeżamy JWT — to nie 401) ani konflikt
        // współbieżności (nie emitujemy "concurrency-conflict"). Emitujemy dedykowany event
        // globalny i odrzucamy — strona/kontekst pokaże prompt "Połącz ponownie".
        if (isMsConnectionRequired(error)) {
            window.dispatchEvent(new CustomEvent("ms-connection-required", {
                detail: { integrationType: error.response.data?.integrationType || null },
            }));
            return Promise.reject(error);
        }

        /* [A12-16] 403 terms_not_accepted — serwer odrzucił ZAPIS, bo konto nie przyjęło aktualnego
           regulaminu. W przeglądarce nie powinno się to zdarzyć (okno regulaminu blokuje interfejs),
           ale gdyby pobranie regulaminu przy starcie się nie powiodło, człowiek dostałby surowy błąd
           bez wskazówki, co zrobić. Emitujemy zdarzenie, po którym Layout ponawia sprawdzenie i pokazuje
           okno z regulaminem — czyli dokładnie to, czego brakowało. */
        if (error.response?.status === 403 && error.response?.data?.code === "terms_not_accepted") {
            window.dispatchEvent(new Event("terms-acceptance-required"));
            return Promise.reject(error);
        }

        /* [HASLA-LOKALNE 2026-09-27] 403 haslo_do_zmiany — konto lokalne z hasłem tymczasowym albo po terminie ważności
           (hasło wygasło W TRAKCIE sesji). AuthContext zaznacza w stanie konta „hasło do zmiany”, a ProtectedRoute prowadzi
           na ekran zmiany hasła — jedyne miejsce, któremu serwer jeszcze odpowiada. */
        if (error.response?.status === 403 && error.response?.data?.code === KOD_HASLO_DO_ZMIANY) {
            window.dispatchEvent(new CustomEvent(ZDARZENIE_HASLO_DO_ZMIANY, { detail: { powod: error.response.data?.powod || null } }));
            return Promise.reject(error);
        }

        /* [P2-X6/P2-X7 2026-09-26] 403 admin_pin_required — poproś o PIN i ponów żądanie JEDEN raz (patrz KOD_PIN_WYMAGANY). */
        if (error.response?.status === 403
            && error.response?.data?.code === KOD_PIN_WYMAGANY
            && originalRequest
            && !originalRequest._pinPonowiony
            && typeof _obslugaPinu === "function") {
            originalRequest._pinPonowiony = true;
            let potwierdzony = false;
            try {
                // [PIN-ZARZAD 2026-09-27] Obsługa dostaje treść odmowy — serwer mówi w niej, ile cyfr ma PIN (`dlugoscPinu`).
                potwierdzony = (await _obslugaPinu(error.response?.data)) === true;
            } catch {
                potwierdzony = false;
            }
            if (potwierdzony) return api(originalRequest);
            if (error.response.data && typeof error.response.data === "object") {
                error.response.data = { ...error.response.data, message: KOMUNIKAT_PIN_ANULOWANY };
            }
            return Promise.reject(error);
        }

        // Guard: never try to refresh when the refresh endpoint itself returns 401
        if (
            error.response &&
            error.response.status === 401 &&
            originalRequest.url?.includes("/users/refresh")
        ) {
            zapamietajPowodWylogowania(error);
            ["user", "permissions"].forEach(k => localStorage.removeItem(k));
            _signalRToken = null;
            redirectToLoginIfNeeded();
            return Promise.reject(error);
        }

        // 401 z integracji Microsoft (Teams / SharePoint / OneDrive) = wygasły token MS, a NIE sesja aplikacji.
        // NIE wolno tu odświeżać JWT — to wywoływało burzę odświeżeń (rotacja refresh tokena → wykrycie replay
        // → błędne wylogowanie) oraz niewyłapane 401. Odrzucamy od razu; komponent obsłuży reconnect do Teams.
        if (
            error.response &&
            error.response.status === 401 &&
            originalRequest.url && (
                originalRequest.url.includes("/teams-chat") ||
                originalRequest.url.includes("/microsoft") ||
                originalRequest.url.includes("/sharepoint")
            )
        ) {
            return Promise.reject(error);
        }

        if (
            error.response &&
            error.response.status === 401 &&
            !originalRequest._retry
        ) {
            if (isRefreshing) {
                return new Promise(function (resolve, reject) {
                    failedQueue.push({ resolve, reject });
                })
                    .then(() => {
                        // Cookies zostały odświeżone — po prostu retry
                        return api(originalRequest);
                    })
                    .catch((err) => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            // Cookies (access_token, refresh_token) lecą automatycznie dzięki withCredentials
            // Backend czyta je z Request.Cookies — nie trzeba ich wysyłać w body
            return new Promise(function (resolve, reject) {
                api
                    .post("/users/refresh", {})
                    .then((res) => {
                        // Backend ustawił nowe httpOnly cookies w odpowiedzi
                        const { accessToken, permissions } = res.data;

                        // Aktualizuj SignalR token w pamięci
                        if (accessToken) _signalRToken = accessToken;

                        // Aktualizuj permissions w localStorage (nie są sekretem)
                        //
                        // [brief 2026-09-03, poz. 10] „zmiany wprowadzone w access-control MUSZĄ
                        // AUTOMATYCZNIE BYĆ RESPEKTOWANE W APLIKACJI OD RAZU […] W PRZYPADKU
                        // ODEBRANIA UPRAWNIEŃ".
                        //
                        // CO BYŁO ŹLE: serwerowa połowa działała od dawna — odebranie podbija
                        // SecurityStamp, `OnTokenValidated` odrzuca stary token przy KAŻDYM żądaniu,
                        // a ten cichy /refresh wydaje nowy token z już przeliczonymi uprawnieniami.
                        // Ale nowa lista lądowała WYŁĄCZNIE w localStorage. React trzyma uprawnienia
                        // w stanie `AuthContext`, ustawianym RAZ przy montowaniu aplikacji — więc
                        // menu, kafelki panelu i bramki tras pokazywały odebrany moduł aż do F5.
                        // Kliknięcie kończyło się ekranem „brak uprawnień", czyli aplikacja
                        // zapraszała tam, gdzie nie wolno wejść.
                        //
                        // TERAZ: zdarzenie okna, na które `AuthContext` odpowiada `setPermissions`.
                        // Dlaczego zdarzeniem, a nie importem kontekstu: `apiService` jest warstwą
                        // NIŻSZĄ niż React (używają go też testy i moduły spoza drzewa komponentów),
                        // a import kontekstu zrobiłby z tego cykl.
                        _zastosujUprawnieniaPoOdswiezeniu(permissions);

                        processQueue(null);
                        resolve(api(originalRequest));
                    })
                    .catch((err) => {
                        processQueue(err, null);
                        // Wylogowanie TYLKO gdy sesja jest realnie nieważna (refresh odrzucony 400/401).
                        // 5xx/429/brak sieci = przejściowa awaria backendu (np. restart bazy) — cookies
                        // usera wciąż są ważne; masowe wylogowanie całej firmy przy 30-sekundowym
                        // blipie DB było błędem (audyt 2026-07-18). Żądanie po prostu pada, kolejny
                        // 401 ponowi refresh, a gdy backend wróci — sesja działa dalej bez logowania.
                        const st = err?.response?.status;
                        if (st === 400 || st === 401) {
                            zapamietajPowodWylogowania(err);
                            ["user", "permissions"].forEach(k => localStorage.removeItem(k));
                            _signalRToken = null;
                            redirectToLoginIfNeeded();
                        }
                        reject(err);
                    })
                    .finally(() => {
                        isRefreshing = false;
                    });
            });
        }
        // ── 409 Conflict — concurrent edit detected ──
        if (error.response && error.response.status === 409) {
            const data = error.response.data;
            const msg = data?.message || "Dane zostały zmienione przez innego użytkownika. Odśwież stronę i spróbuj ponownie.";
            // Dispatch custom event so any toast provider can pick it up
            window.dispatchEvent(new CustomEvent("concurrency-conflict", {
                detail: { message: msg, lastEditedBy: data?.lastEditedBy, lastEditedAt: data?.lastEditedAt }
            }));
            // Still reject so calling code can handle it
            return Promise.reject(error);
        }

        // ── 429 Too Many Requests — retry with exponential backoff ──
        if (error.response && error.response.status === 429 && !originalRequest._retryCount) {
            originalRequest._retryCount = 0;
        }
        if (
            error.response &&
            error.response.status === 429 &&
            (originalRequest._retryCount || 0) < 3
        ) {
            originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
            const delay = Math.min(1000 * Math.pow(2, originalRequest._retryCount - 1), 8000);
            // Add jitter to avoid thundering herd
            const jitter = Math.random() * 500;
            await new Promise((r) => setTimeout(r, delay + jitter));
            return api(originalRequest);
        }

        // FIX audytu: udostępnij komunikat błędu z backendu ({error} / {message}) jako error.message — strony
        // pokazujące err.message zobaczą treść (np. „Przekroczono limit urlopu"), a nie generyczne
        // „Request failed with status code 400". friendlyMessage zostaje jako jawne pole dla nowszych call-site'ów.
        const apiMsg = error.response?.data?.error || error.response?.data?.message;
        if (typeof apiMsg === "string" && apiMsg.trim()) {
            error.friendlyMessage = apiMsg;
            try { error.message = apiMsg; } catch { /* w rzadkich środowiskach message bywa read-only */ }
        }

        return Promise.reject(error);
    }
);

export const apiService = {
    // --- AUTH ---
    login: (credentials) => api.post("/users/login", credentials),
    loginOffice365: (tokenDto) => api.post("/users/login-office365", tokenDto),
    getMe: () => api.get("/users/me"),
    refreshToken: (tokens) => api.post("/users/refresh", tokens),
    logout: () => api.post("/users/logout", {}),
    // [KA-05 2026-09-26] Wyloguj ze WSZYSTKICH urządzeń — każda rodzina refresh tokenów konta, stempel, połączenia na żywo.
    logoutAll: () => api.post("/users/logout-all", {}),
    // [KA-14 2026-09-26] Czy konto loguje się hasłem aplikacji (menu pokazuje „Zmień hasło” tylko wtedy) i sama zmiana.
    statusMojegoHasla: () => api.get("/users/me/password"),
    zmienMojeHaslo: (currentPassword, newPassword) => api.post("/users/me/password", { currentPassword, newPassword }),
    // [HASLA-LOKALNE 2026-09-27] /admin/users — hasło KONTA LOKALNEGO: stan, ustawienie/reset (puste = system losuje hasło
    // tymczasowe), ważność haseł (domyślnie 45 dni). Konto Office 365 = 400 z komunikatem.
    statusHaslaKonta: (id) => api.get(`/admin/users/${id}/password`),
    ustawHasloKonta: (id, noweHaslo) => api.post(`/admin/users/${id}/password`, { noweHaslo: noweHaslo || null }),
    zasadyHaselKontLokalnych: () => api.get("/admin/users/password-policy"),
    zapiszZasadyHaselKontLokalnych: (waznoscDni) => api.put("/admin/users/password-policy", { waznoscDni }),

    // --- TICKETS ---
    getTicketList: (params) => api.get("/tickets", { params }),
    getTicketDetails: (id) => api.get(`/tickets/${id}`),
    getTicketFilterCounts: (params) =>
        api.get("/tickets/FilterCounts", { params }),
    getAssignableConfigurations: () => api.get("/tickets/configurations"),
    getAssignableUsers: (id) => api.post(`/tickets/${id}/assignableUsers`),
    // [BRIEF 2026-09-10, B-8] Kartoteka zgłaszającego („typowe bio dla IT") — bramka po stronie
    // serwera jest ta sama, co dla samego zgłoszenia, więc front nie dokłada tu żadnego warunku.
    getReporterCard: (id) => api.get(`/tickets/${id}/reporter-card`),

    /* ── ETAPY REALIZACJI ZGŁOSZENIA („Zaawansowane") ──────────────────────────────────────
       [polecenie właściciela 2026-09-11] „tworzyc TODO oraz zadania po to, by potem pokazywac
       na osi czasu, etapy realizacji zapisane same dla siebie".

       Klucz uprawnień to ISTNIEJĄCY TicketResponse — kto może odpisać w sprawie, może prowadzić jej
       etapy. Nowego klucza NIE zakładamy świadomie: każdy nowy klucz czerwieni liczniki dokumentacji
       i macierz autoryzacji, a tu nie przybywa ani jednej nowej decyzji dostępowej.

       Front NIE liczy uprawnień z tych odpowiedzi — GET oddaje `mozeDodawac` / `powodBraku`, a każdy
       etap `mozeEdytowac` / `mozeZamknac` / `powod`, i to one decydują, czy przycisk jest czynny
       (ten sam wzorzec co `canComplete` w checkliście procesu kadrowego). */
    getTicketKroki: (id) => api.get(`/tickets/${id}/kroki`),
    createTicketKrok: (id, dane) => api.post(`/tickets/${id}/kroki`, dane),
    updateTicketKrok: (id, krokId, dane) => api.put(`/tickets/${id}/kroki/${krokId}`, dane),
    deleteTicketKrok: (id, krokId) => api.delete(`/tickets/${id}/kroki/${krokId}`),
    // Stan: "otwarty" | "wtoku" | "zrobiony" | "odrzucony" (słownik: components/Tickets/KrokiZgloszenia/stanyKroku.js).
    setTicketKrokStan: (id, krokId, stan) => api.post(`/tickets/${id}/kroki/${krokId}/stan`, { stan }),
    // PEŁNA nowa kolejność identyfikatorów, nie „przesuń o jeden" — dwie osoby porządkujące listę
    // równocześnie nadpisują się wtedy całą listą, zamiast składać dwa przesunięcia w trzecią kolejność.
    reorderTicketKroki: (id, kolejnosc) => api.post(`/tickets/${id}/kroki/kolejnosc`, { kolejnosc }),

    changeCategory: (id, payload) => api.post(`/tickets/${id}/category`, payload),

    // Odpowiedź z załącznikami (BRIEF-0011): opcjonalny onUploadProgress (axios) — progress bar wysyłki.
    replyToTicket: (id, formData, onUploadProgress) =>
        api.post(`/tickets/${id}/reply`, formData, onUploadProgress ? { onUploadProgress } : undefined),
    assignTicket: (id, payload) => api.post(`/tickets/${id}/assign`, payload),
    closeTicket: (id, payload) => api.post(`/tickets/${id}/close`, payload),
    /* 🔴 [POLECENIE WŁAŚCICIELA 2026-09-15] PRZYCISK „S". OSOBNA końcówka, a nie `closeTicket`
       z czasem 0 — tamta droga POWIADAMIA nadawcę bezwarunkowo (reguła „zgłaszający dowiaduje się
       ZAWSZE", zamrożona testem) i stoi za pięcioma bramkami, które dla spamu oddają 409/400.
       Ta jest cicha, idempotentna i działa także na sprawie JUŻ oznaczonej jako spam. */
    markTicketAsSpam: (id) => api.post(`/tickets/${id}/spam`),
    reopenTicket: (id) => api.post(`/tickets/${id}/reopen`),
    // [K4] Status per dział — zamknięcie/otwarcie części zgłoszenia przez jeden dział obsługujący.
    closeTicketForDepartment: (id, deptId, payload) => api.post(`/tickets/${id}/department/${deptId}/close`, payload),
    reopenTicketForDepartment: (id, deptId) => api.post(`/tickets/${id}/department/${deptId}/reopen`),
    changePriority: (id, payload) => api.post(`/tickets/${id}/priority`, payload),
    addNoteToTicket: (id, payload) => api.post(`/tickets/${id}/note`, payload),
    createTicket: (formData) => api.post("/tickets/create", formData),
    // Zadania cykliczne (BRIEF-0009) — jeden request JSON tworzy serię zgłoszeń OD-DO.
    createRecurringTickets: (payload) => api.post("/tickets/create-recurring", payload),
    /* [polecenie właściciela 2026-09-06] Obsada JEDNEGO modułu, nie całej platformy: `{ module: "itsm" }`.
       Bez parametru serwer odpowiada obsadą modułów, do których tytuł ma sam pytający. */
    getUserFilters: (params) => api.get("/tickets/UserFilters", { params }),
    setPlannedDate: (id, payload) =>
        api.post(`/tickets/${id}/setPlannedDate`, payload),
    toggleSapCategory: (id) => api.post(`/tickets/${id}/toggle-sap`),
    // Zbiorczy zapis sekcji Klasyfikacja (BRIEF-0003) — kategoria/data/przypisanie/priorytet/firma.
    updateTicketClassification: (id, payload) =>
        api.post(`/tickets/${id}/classification`, payload),

    getPlannedTickets: (params) => api.get("/tickets/planned", { params }),

    // Realny filtr domen (BRIEF-0004) — wpisy {id, domain} w bazie.
    getBlockedDomains: () => api.get("/tickets/blocked-domains"),
    addBlockedDomain: (domain) => api.post("/tickets/blocked-domains", { domain }),
    removeBlockedDomain: (id) => api.delete(`/tickets/blocked-domains/${id}`),

    // Dziennik „Wykluczone" (BRIEF-0006) — maile pominięte przez filtr domen.
    getBlockedEmails: (take = 200) => api.get("/tickets/blocked-emails", { params: { take } }),
    clearBlockedEmails: () => api.delete("/tickets/blocked-emails"),
    removeBlockedEmail: (id) => api.delete(`/tickets/blocked-emails/${id}`),

    // „Specjalne" — rozliczenie prac (BRIEF-0004; korekty z powodem + ewidencja — BRIEF-0018).
    getTicketWorkEntries: (id) => api.get(`/tickets/${id}/work-entries`),
    addTicketWorkEntry: (id, payload) => api.post(`/tickets/${id}/work-entries`, payload),
    // Korekta pozycji (BRIEF-0018) — payload { workType, minutes, reason?, evidenceSeconds? }.
    correctTicketWorkEntry: (id, entryId, payload) =>
        api.put(`/tickets/${id}/work-entries/${entryId}`, payload),
    // Usunięcie: na ZAMKNIĘTYM zgłoszeniu powód wymagany (400 bez) — BRIEF-0018.
    deleteTicketWorkEntry: (id, entryId, reason, evidenceSeconds) =>
        api.delete(`/tickets/${id}/work-entries/${entryId}`, { params: { reason, evidenceSeconds } }),
    // Korekta czasu realizacji zamkniętego zgłoszenia (BRIEF-0018) — powód wymagany.
    correctTicketTimeSpent: (id, payload) => api.put(`/tickets/${id}/time-correction`, payload),

    // Trwałe przypięcia zgłoszeń per użytkownik (BRIEF-0015).
    getPinnedTickets: () => api.get("/tickets/pins"),
    pinTicket: (id) => api.post(`/tickets/${id}/pin`),
    unpinTicket: (id) => api.delete(`/tickets/${id}/pin`),

    /* --- SCALANIE ZGŁOSZEŃ (zlecenie właściciela 2026-09-11) ---
       „można scalić kilka zgłoszeń na raz […] i taka uporządkowana forma może pracować dalej zbiorczo”.
       Kontrakt jest wspólny z backendem co do znaku:
         POST /tickets/scal               { celId, zrodlaIds[], nowyNumer, powodScalenia }
                                          → { celId, celNumer, wchloniete[], powiadomieni, ostrzezenia[] }
         GET  /tickets/scal/kandydaci     ?q&celId&limit → { items: [{ …, mozna, powod }] }
         GET  /tickets/{id}/scalenie      → { scaloneDo, scaloneDoNumer, wchloniete[], adresaciOdpowiedzi[] }
       Klucz uprawnień: `TicketMerge` (explicit-only), sprawdzany po stronie serwera na CELU
       i na KAŻDYM ŹRÓDLE osobno. ⚠️ Kandydat, którego scalić nie wolno, wraca z `mozna:false`
       i POWODEM — front ma go POKAZAĆ, a nie odfiltrować (odmowa ≠ brak wyniku). */
    scalZgloszenia: (payload) => api.post("/tickets/scal", payload),
    getKandydaciScalenia: (params) => api.get("/tickets/scal/kandydaci", { params }),
    getScalenieZgloszenia: (id) => api.get(`/tickets/${id}/scalenie`),
    // [FALA VII 2.5, 2026-09-12] Załączniki zgłoszenia po IDENTYFIKATORZE rekordu (nie po nazwie pliku ze
    // statyków): lista, treść pojedynczego pliku do powłoki podglądu, komplet jako ZIP.
    getTicketAttachments: (ticketId) => api.get(`/ticket-attachments/zgloszenie/${ticketId}`),
    /* [FALA VIII 2026-09-12, okno zgłoszenia] PRZYPINANIE wiadomości w historii: lista przypiętych jako
       plakietki na górze okna, klik przewija do wiadomości. Trwałe (kolumna TicketMessages.IsPinned),
       żeby drugi agent widział te same przypięcia. */
    przypnijWiadomosc: (ticketId, messageId) => api.post(`/tickets/${ticketId}/messages/${messageId}/przypnij`),
    odepnijWiadomosc: (ticketId, messageId) => api.delete(`/tickets/${ticketId}/messages/${messageId}/przypnij`),
    getTicketAttachmentContent: (attachmentId) =>
        api.get(`/ticket-attachments/${attachmentId}/tresc`, { responseType: "blob" }),
    getTicketAttachmentsZip: (ticketId) =>
        api.get(`/ticket-attachments/zgloszenie/${ticketId}/zip`, { responseType: "blob" }),
    // [2026-09-21] Linki do plików POZA aplikacją (SharePoint, OneDrive, Dropbox…) znalezione w mailach sprawy.
    getTicketExternalLinks: (ticketId) =>
        api.get(`/ticket-attachments/zgloszenie/${ticketId}/linki-zewnetrzne`),
    recoverTicketAttachmentsFromMail: (ticketId) =>
        api.post(`/ticket-attachments/zgloszenie/${ticketId}/odzyskaj-z-poczty`),

    // Operacje masowe (BRIEF-0004/0005) — zwracają { processed, skipped }.
    bulkHideTickets: (ids) => api.post("/tickets/bulk/hide", { ids }),
    bulkUnhideTickets: (ids) => api.post("/tickets/bulk/unhide", { ids }),
    bulkCloseTickets: (ids, success) => api.post("/tickets/bulk/close", { ids, success }),
    bulkReopenTickets: (ids) => api.post("/tickets/bulk/reopen", { ids }),
    bulkAssignTickets: (ids, assignedUserId) => api.post("/tickets/bulk/assign", { ids, assignedUserId }),

    // --- ANALYTICS (analityka pracy serwisu, BRIEF-0002) ---
    // Wieloskrzynkowość per dział (H2): każdy wrapper przyjmuje w `params` opcjonalny
    // `configurationId` (Guid skrzynki/działu). Brak = wszystkie skrzynki dostępne użytkownikowi.
    getAnalyticsOverview: (params) => api.get("/analytics/overview", { params }),
    getAnalyticsTimeseries: (params) => api.get("/analytics/timeseries", { params }),
    getAnalyticsByAgent: (params) => api.get("/analytics/by-agent", { params }),
    getAnalyticsByReporter: (params) => api.get("/analytics/by-reporter", { params }),
    getAnalyticsByCompany: (params) => api.get("/analytics/by-company", { params }),
    getAnalyticsByCategory: (params) => api.get("/analytics/by-category", { params }),
    getAnalyticsCompanies: () => api.get("/analytics/companies"),
    // Moduł podsumowań (BRIEF-0018) — płaskie wiersze per porcja firmowa; front grupuje/sortuje.
    getAnalyticsBreakdown: (params) => api.get("/analytics/breakdown", { params }),
    // Macierz firma × pracownik (BRIEF-0019) — płaskie wiersze; front pivotuje do macierzy.
    getAnalyticsCompanyAgentMatrix: (params) => api.get("/analytics/company-agent-matrix", { params }),
    // Otwarte nierozwiązane — status roboczy = ostatnia notatka (BRIEF-0010).
    getAnalyticsOpenTickets: (params) => api.get("/analytics/open-tickets", { params }),
    // [S3] Model raportu zbiorczego „do 100% czasu pracy" — TEN SAM builder co PDF (działy → podgrupy → pracownicy → dni).
    getAnalyticsZbiorczy: (params) => api.get("/analytics/zbiorczy", { params }),
    // [S2] Moduły w zakresie analityki wołającego (globalny / przełożony / imienny) — selektor modułu i kafelek landingu.
    getAnalyticsModulyDostepne: () => api.get("/analytics/moduly-dostepne"),
    // Raport analityczny (XLSX/PDF) dla aktualnych filtrów ekranu — dane z tych samych
    // metod co widok, więc liczby w pliku zgadzają się z tym, co widać.
    downloadAnalyticsReport: (format, params) =>
        api.get("/analytics/report-export", { params: { ...params, format }, responseType: "blob" }),
    downloadWorklogExport: (params) =>
        api.get("/analytics/worklog-export", { params, responseType: "blob" }),
    // Raporty PDF pracownika (BRIEF-0009) — dobowy skrócony i szczegółowy.
    downloadEmployeeDailyReport: (params) =>
        api.get("/analytics/employee-report/daily", { params, responseType: "blob" }),
    downloadEmployeeDetailReport: (params) =>
        api.get("/analytics/employee-report/detail", { params, responseType: "blob" }),

    // --- KONFIGURACJA RAPORTÓW (automatyzacja, BRIEF-0022/0027) ---
    getReportSchedules: () => api.get("/report-schedules"),
    createReportSchedule: (payload) => api.post("/report-schedules", payload),
    updateReportSchedule: (id, payload) => api.put(`/report-schedules/${id}`, payload),
    deleteReportSchedule: (id) => api.delete(`/report-schedules/${id}`),
    sendReportScheduleNow: (id) => api.post(`/report-schedules/${id}/send-now`),
    previewSummaryReport: (params) =>
        api.get("/report-schedules/preview-summary", { params, responseType: "blob" }),
    // Historia wysyłek (BRIEF-0027) — zielona/czerwona kropka, kiedy, kto, wynik.
    getReportScheduleHistory: (params) => api.get("/report-schedules/history", { params }),

    // --- ADDRESS BOOK (TICKETS) ---
    getAddressBook: (search, departmentId, letter) =>
        api.get("/my-tickets/address-book", { params: { search, departmentId, letter } }),
    getAddressBookSearch: (search) =>
        api.get("/my-tickets/address-book-search", { params: { search } }),
    /** [2026-09-23] „Kto gdzie jest” dla widocznej strony książki (identyfikatory wpisów, maks. 100). */
    getObecnoscKsiazki: (ids) => api.post("/my-tickets/address-book/obecnosc", { ids }),
    // Struktura organizacyjna firmy (zakładka "Struktura" w książce adresowej) — dla każdego.
    // [2026-09-23] `companyId` — przełączanie spółki w zakładce „Struktura” książki adresowej.
    getCompanyStructure: (companyId) =>
        api.get("/my-tickets/company-structure", { params: { companyId: companyId || undefined } }),
    // Schemat organizacyjny wg podległości (manager z O365) — osoby + managerEmail; front buduje drzewo.
    getCompanyOrgChart: () =>
        api.get("/my-tickets/company-org-chart"),

    getAddressBookForConfiguration: (params) =>
        api.get("/dynamic-settings/address-book", { params }),
    getAddressBookSearchForConfiguration: (search, isVisible) =>
        api.get("/dynamic-settings/address-book-search", {
            params: { search, isVisible },
        }),

    // --- GRUPY MAILOWE / LISTY DYSTRYBUCYJNE (import z Office 365) ---
    // Odczyt stoi za PERM:MailGroupsView, zapis składu za mocniejszym PERM:MailGroupsMembers.
    // [S9, polecenie właściciela 2026-09-13] Tworzenie i kasowanie CAŁYCH grup Microsoft 365 (za czwartym
    // kluczem PERM:MailGroupsManage) oraz synchronizacja na żądanie — odwrócenie zakazu z briefu 2026-08-30.
    // Klasycznych list dystrybucyjnych Graph nie tworzy ani nie kasuje (te dalej w Exchange).
    getMailGroups: (params) => api.get("/mail-groups", { params }),
    getMailGroupMembers: (id) => api.get(`/mail-groups/${id}/members`),
    addMailGroupMember: (id, data) => api.post(`/mail-groups/${id}/members`, data),
    removeMailGroupMember: (id, memberId) =>
        api.delete(`/mail-groups/${id}/members/${memberId}`),
    createMailGroup: (data) => api.post("/mail-groups", data),
    deleteMailGroup: (id) => api.delete(`/mail-groups/${id}`),
    syncMailGroups: () => api.post("/mail-groups/sync"),

    // --- WYSZUKIWARKA GLOBALNA (S6) ---
    // Serwer oddaje wyłącznie treści, do których pytający ma dostęp (aktualności, dokumenty, baza wiedzy, FAQ).
    szukajGlobalnie: (q, limit) => api.get("/search", { params: { q, limit } }),

    // --- MY TICKETS ---
    getMyTicketList: (params) => api.get("/my-tickets", { params }),
    // Moduły, w których zgłaszający ma zgłoszenia (+ liczniki) — źródło zakładek portalu.
    getMyTicketModuleCounts: () => api.get("/my-tickets/module-counts"),
    // Słownik typów sprzętu do formularza zgłoszenia (portal; /itam/* jest zamknięte dla pracownika).
    /* [2026-09-20] doWykupu odsiewa typy na SERWERZE: lista sprzętu podlegającego wykupowi
       jest regułą firmy, nie ustawieniem ekranu. */
    getEquipmentTypesForTicket: (doWykupu = false) =>
        api.get("/my-tickets/equipment-types", doWykupu ? { params: { doWykupu: true } } : undefined),
    getMyTicketDetails: (id) => api.get(`/my-tickets/${id}`),
    createUserTicket: (formData) => api.post("/my-tickets/create", formData),
    // [BRIEF 2026-09-01 A] Prośba o dostęp z ekranu „Brak uprawnień” — JSON z obowiązkowym uzasadnieniem;
    // serwer odmawia (400), gdy uzasadnienia brakuje, więc to nie jest zwykły createUserTicket.
    createAccessRequestTicket: (dto) => api.post("/my-tickets/access-request", dto),
    replyToMyTicket: (id, formData) =>
        api.post(`/my-tickets/${id}/reply`, formData),
    closeMyTicket: (id) => api.post(`/my-tickets/${id}/close`),
    getEquipmentTypes: () => api.get("/my-tickets/equipment-types"),
    getMyAssignedDevices: () => api.get("/my-tickets/my-devices"),
    getMyDrives: () => api.get("/my-tickets/my-drives"),
    getMyOrgStructure: () => api.get("/my-tickets/my-org-structure"),
    /* [polecenie właściciela 2026-09-21] Widżet „Mój zespół" na pulpicie — JEDNO zbiorcze żądanie
       na cały skład działu: imię i nazwisko, stanowisko, telefony z książki O365, urlop albo
       lokalizacja na dziś i (o ile patrzący ma połączony Teams) status obecności. Odpowiedź ma
       kształt wpisu książki adresowej, więc karmi wprost wizytówkę pracownika. */
    getMojZespolDzisiaj: () => api.get("/moj-zespol/dzisiaj"),
    // Zakres hierarchiczny „Mój Dział" z Core (jedno źródło prawdy): { departmentIds, userIds, isSupervisor }.
    getMyDeptScope: () => api.get("/my-dept/scope"),
    // [zgłoszenie właściciela 2026-09-04, poz. 8-9] Co mam pod sobą: działy główne, ich jednostki
    // wewnętrzne, kierownicy komórek i miejsce patrzącego w drzewie. Zasięg liczy serwer.
    getMyDeptStrukturaPrzelozonego: () => api.get("/my-dept/struktura-przelozonego"),
    // Zbiorczy pulpit przełożonego: liczniki + listy „today" w jednym wywołaniu (zamiast fan-outu).
    getMyDeptDashboardSummary: () => api.get("/my-dept/dashboard-summary"),
    // [polecenie właściciela 2026-10-04] Zespół w strukturze na dziś: jednostki z zakresu odpowiedzialności i w każdej
    // wszyscy jej ludzie ze stanowiskiem i „kto gdzie jest” — jeden widok na pulpicie „Mój Dział”.
    getMyDeptZespolDzisiaj: () => api.get("/my-dept/zespol-dzisiaj"),
    // Członkowie zespołu (zakres „w dół") z NR/MPK — zakresowy odpowiednik admin-only /admin/users.
    getMyDeptMembers: () => api.get("/my-dept/members"),
    // Salda/limity członka zespołu (zakres) — zakresowy odpowiednik admin-only /hr-config/balances/{id}.
    getMyDeptMemberBalances: (userId, year) => api.get(`/my-dept/balances/${userId}`, { params: { year } }),
    // Salda urlopowe/zdalne CAŁEGO zespołu jednym wywołaniem (Remaining vacationDays + remoteOccasional).
    // includeFormer = true TYLKO dla widoków ROZLICZENIOWYCH („co się w tym roku wydarzyło"): dokłada osoby,
    // które w rozliczanym roku jeszcze pracowały, a dziś mają konto zablokowane. Widoki PLANOWANIA
    // (grafik, pulpit, praca zdalna) zostają przy domyślnym false — nie planuje się pracy komuś, kogo nie ma.
    getMyDeptTeamBalances: (year, includeFormer) =>
        api.get("/my-dept/team-balances", { params: { year, includeFormer: includeFormer || undefined } }),
    // Ostatnie logowanie per członek zespołu (kto/kiedy; bez IP — RODO).
    getMyDeptLoginHistory: () => api.get("/my-dept/login-history"),
    // Kalendarz działu na miesiąc: urodziny + rocznice zatrudnienia + zatwierdzone urlopy zespołu.
    getMyDeptCalendar: (year, month) => api.get("/my-dept/calendar", { params: { year, month } }),
    // REALNE urlopy zespołu na ROK (zatwierdzone VacationDateRange) → siatka Gantta zakładki „Urlopy".
    // includeFormer jak wyżej: siatka ROCZNA (rozliczenie) woła z true, grafik (planowanie) z domyślnym false.
    getMyDeptVacations: (year, includeFormer) =>
        api.get("/my-dept/vacations", { params: { year, includeFormer: includeFormer || undefined } }),
    // Projekty działu (Kanban + Listy projektowe) — scoped w backendzie (koniec wycieku).
    getMyDeptProjects: () => api.get("/my-dept/projects"),
    // Raport pracy zdalnej działu na miesiąc (dni approved/pending + per-pracownik).
    getMyDeptRemoteSummary: (year, month) => api.get("/my-dept/remote-summary", { params: { year, month } }),
    // Analityka czasu pracy W ZAKRESIE DZIAŁU (zakresowy odpowiednik admin-only /admin/worktime; RODO: bez warstwy bezpieczeństwa O365).
    getMyDeptWorkTimeOverview: (from, to) => api.get("/my-dept/worktime/overview", { params: { from, to } }),
    getMyDeptWorkTimeUsers: (from, to, search, onlyActive) => api.get("/my-dept/worktime/users", { params: { from, to, search, onlyActive } }),
    getMyDeptWorkTimeUserDetail: (userId, from, to) => api.get(`/my-dept/worktime/users/${userId}`, { params: { from, to } }),
    getMyDeptWorkTimeUserSection: (userId, section, params) =>
        api.get(`/my-dept/worktime/users/${userId}/sections/${section}`, { params }),
    getMyDeptWorkTimeUserWorkLog: (userId, from, to) =>
        api.get(`/my-dept/worktime/users/${userId}/worklog`, { params: { from, to } }),
    // [analityka1005] Office 365 z dziennika audytu — zespół przełożonego (od/do = dni polskie „yyyy-MM-dd”, włącznie).
    getMyDeptWorkTimeO365Przeglad: (od, doDnia) => api.get("/my-dept/worktime/o365/przeglad", { params: { od, do: doDnia } }),
    getMyDeptWorkTimeO365Osoby: (od, doDnia, search) => api.get("/my-dept/worktime/o365/osoby", { params: { od, do: doDnia, search } }),
    getMyDeptWorkTimeO365Osoba: (userId, od, doDnia) => api.get(`/my-dept/worktime/o365/osoby/${userId}`, { params: { od, do: doDnia } }),
    // Zapisy na szkolenia osób w zasięgu przełożonego (zakresowany odpowiednik admin-only enrollments).
    getMyDeptElearningEnrollments: (search, status, courseId) =>
        api.get("/my-dept/elearning-enrollments", { params: { search, status, courseId } }),
    // Dyski sieciowe zespołu (zakresowane) + ich użytkownicy — zamiast admin-only drives.
    getMyDeptDrives: () => api.get("/my-dept/drives"),
    getMyDeptDriveUsers: (id) => api.get(`/my-dept/drives/${id}/users`),
    // Zapis dostępów do dysku w zakresie przełożonego (admin-only /dynamic-settings dawał 403 dla Type1/2).
    addMyDeptDriveUser: (id, data) => api.post(`/my-dept/drives/${id}/users`, data),
    removeMyDeptDriveUser: (id, userId) => api.delete(`/my-dept/drives/${id}/users/${userId}`),
    // [BRIEF 2026-09-01 poz. 10] Odebranie dostępu = ZGŁOSZENIE do IT (nie ciche skasowanie wpisu):
    // aplikacja nie zmienia uprawnień na dysku sieciowym ani w SharePoint, robi to człowiek z IT.
    requestMyDeptDriveRevoke: (id, userId, data) =>
        api.post(`/my-dept/drives/${id}/users/${userId}/revoke-request`, data),
    // [DYSKI 2026-07-27] Wnioski o dostęp do dysków/witryn w zasięgu decyzyjnym (właściciel zasobu
    // lub kierownik działu, na który dysk jest zmapowany) + decyzja z komentarzem.
    // [URLOPY 2026-07-30] Urlopy zaległe zespołu (dodatnie salda lat ubiegłych + terminy KP).
    getMyDeptOverdueLeaves: () => api.get("/my-dept/overdue-leaves"),
    // [RAPORT ZDALNY 2026-07-30] Konfiguracja obowiązku raportu pracy zdalnej + wgląd HR.
    getMyRemoteReportSetting: () => api.get("/hr/remote-reports/settings/my"),
    getRemoteReportSettings: () => api.get("/hr/remote-reports/settings"),
    updateRemoteReportSetting: (userId, data) => api.put(`/hr/remote-reports/settings/${userId}`, data),
    getRemoteReports: (year, month) => api.get("/hr/remote-reports/reports", { params: { year, month } }),
    // [2026-09-15, reklamacja właściciela] RAPORTY pracy zdalnej WE WZORZE (WzorRaportu, QuestPDF): raport jednego dnia
    // (A4 pionowo), zestawienie za okres (A4 poziomo) i Excel — te same filtry roku/miesiąca co lista `reports`.
    // PDF WNIOSKU (getHrRequestPdf) to dokument z szablonu kadrowego, nie raport — na /admin/remote-reports już nie używany.
    getRemoteReportPdf: (id) => api.get(`/hr/remote-reports/${id}/pdf`, { responseType: "blob" }),
    getRemoteReportsPdf: (year, month) => api.get("/hr/remote-reports/pdf", { params: { year, month }, responseType: "blob" }),
    getRemoteReportsXlsx: (year, month) => api.get("/hr/remote-reports/xlsx", { params: { year, month }, responseType: "blob" }),
    getMyDeptDriveRequests: (includeDecided) =>
        api.get("/my-dept/drive-requests", { params: { includeDecided } }),
    decideMyDeptDriveRequest: (id, data) => api.post(`/my-dept/drive-requests/${id}/decide`, data),
    // Zadania CAŁEGO zespołu (właściciel/przypisany w zakresie) — view=team w /api/tasks to co innego („przypisane przeze mnie").
    getMyDeptTasks: () => api.get("/my-dept/tasks"),
    // Sprzęt IT zespołu (zakresowany odpowiednik admin-only /itam/devices).
    getMyDeptEquipment: () => api.get("/my-dept/equipment"),
    // [BRIEF 2026-08-30 pkt 5] Umowy zespołu z terminami — panel „Umowy" w Wnioskach przełożonego.
    getMyDeptContracts: (days) => api.get("/my-dept/contracts", { params: { days } }),
    // [BRIEF 2026-08-30 pkt 4] Wnioski SKIEROWANE przez HR do tego przełożonego (do wypełnienia).
    getMyDeptAssignments: (includeClosed) =>
        api.get("/my-dept/assignments", { params: { includeClosed } }),
    // [BRIEF 2026-08-30 pkt 3] Licznik oceny wdrożeniowej wg INS-ZKL-02 (kotwica = dodanie do działu).
    getMyDeptEvaluationDue: () => api.get("/my-dept/evaluation-due"),

    // --- DASHBOARD ---
    getDashboardStats: (params) =>
        api.get("/tickets/dashboard/stats", { params }),
    getDashboardChart: (params) =>
        api.get("/tickets/dashboard/chart", { params }),

    // --- ITAM ---
    importItamDevices: (formData) =>
        api.post("/itam/import", formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }),
    getItamDevices: (params) => api.get("/itam/devices", { params }),
    /* [zgłoszenie właściciela 2026-09-11 wieczór] „jak pracownik, to powiedzieć jaki sprzęt przypisany
       do niego" — pytanie padło przy licencjach, ale to samo pytanie zadaje kreator protokołu zwrotu.
       Jedno wejście, jeden predykat po stronie serwera (klucz konta ALBO imię i nazwisko dla rekordów
       sprzed wprowadzenia klucza — patrz ItamController.DevicesOfUser). */
    getSprzetPracownika: (userId) => api.get(`/itam/users/${userId}/devices`),
    getItamDevice: (id) => api.get(`/itam/devices/${id}`),
    updateItamDevice: (id, data) => api.put(`/itam/devices/${id}`, data),
    deleteItamDevice: (id) => api.delete(`/itam/devices/${id}`),
    createItamDevices: (devices) => api.post("/itam/devices", devices),
    checkInventoryNumber: (number, excludeId) =>
        api.get("/itam/devices/check-inventory", { params: { number, excludeId } }),
    bulkUpdateDevices: (data) => api.put("/itam/devices/bulk-update", data),

    getItamInvoiceTiles: (search, sortDir = "desc", daysBack) =>
        api.get("/itam/invoices/tiles", { params: { search, sortDir, daysBack } }),
    getItamInvoices: (params) => api.get("/itam/invoices", { params }),
    getItamInvoice: (id) => api.get(`/itam/invoices/${id}`),
    getItamInvoiceDetails: (id) => api.get(`/itam/invoices/${id}`),
    createItamInvoice: (data) => api.post("/itam/invoices", data),
    updateItamInvoice: (id, data) => api.put(`/itam/invoices/${id}`, data),
    // [itam1004] Faktura ze sztukami/licencjami: bez `tryb` serwer odpowiada 409 z listą (nic nie znika);
    // tryb: "usunNiezaksiegowane" | "zostawSztuki" wykonuje świadomy wybór.
    deleteItamInvoice: (id, tryb) => api.delete(`/itam/invoices/${id}`, tryb ? { params: { tryb } } : undefined),
    addDevicesToInvoice: (id, devices) =>
        api.post(`/itam/invoices/${id}/devices`, devices),
    getAvailableItamDevices: (search, includeAssigned) =>
        api.get("/itam/available-for-onboarding", {
            params: { search, includeAssigned },
        }),

    // --- ITAM Device Types ---
    // Plaska projekcja calego magazynu dla /itam/przeglad (bez pageSize=9999 pelnych encji).
    getItamOverview: () => api.get("/itam/overview"),

    /* [FALA VIII, 2026-09-12] EWIDENCJA SPRZĘTU Z FAKTUR — polecenie właściciela: każda pozycja faktury
       rozbita na POJEDYNCZE sztuki w stanie „niezaksięgowane" (nie do wydania), z numerem seryjnym i decyzją
       (magazyn albo osoba/dział) do uzupełnienia; codzienny monit do IT; historia przypisań na zawsze. */
    getEwidencjaDoUzupelnienia: () => api.get("/itam/ewidencja/do-uzupelnienia"),
    zaksiegujSprzet: (data) => api.post("/itam/ewidencja/zaksieguj", data),
    getHistoriaPrzypisan: (itemId) => api.get(`/itam/devices/${itemId}/przypisania`),
    /* Podpowiedź przy PRZEKAZANIU (lustro `zwrot-podpowiedz`): co ta osoba/dział ma już w ewidencji.
       Kreator dokłada resztę z magazynu — tam z `tylkoZaksiegowane: true`, bo sztuki bez numeru
       seryjnego i decyzji NIE są do wydania. */
    getPrzekazaniePodpowiedz: (params) => api.get("/itam/protocols/przekazanie-podpowiedz", { params }),
    /* Decyzja per pozycja faktury (usługa / licencja / sprzęt poza kreatorem / pominięta z uwagą) — „sukcesywnie
       zwalniać pozycje z faktury”; faktura z kompletem decyzji i sztuk jest ROZLICZONA z magazynem. */
    rozliczPozycjeFaktury: (invoiceId, indeks, data) => api.post(`/itam/invoices/${invoiceId}/pozycje/${indeks}/rozlicz`, data),
    cofnijRozliczeniePozycji: (invoiceId, indeks) => api.delete(`/itam/invoices/${invoiceId}/pozycje/${indeks}/rozlicz`),
    getStanFaktur: (ids) => api.get("/itam/ewidencja/stan-faktur", { params: { ids: (ids || []).join(",") } }),
    getItamDeviceTypes: () => api.get("/itam/device-types"),

    // [E-3 / F-1] Zarzadzalne listy podpowiedzi ITAM (producent, model, nazwa oprogramowania,
    // typ licencji, pomieszczenie, lokalizacja). Jeden slownik na wszystkie listy modulu —
    // wlasciciel prosil o JEDEN przycisk „Zaawansowane", w ktorym sa wszystkie.
    // [E-3] Numer ewidencyjny liczy SERWER. Gdyby liczyl formularz, dwie osoby zakladajace sprzet
    // w tej samej minucie dostalyby ten sam numer — a numer trafia na NAKLEJKE i na protokol
    // odpowiedzialnosci materialnej, wiec duplikat jest kosztowny fizycznie.
    // [F-5] Zywy TeamViewer: stan komputera i lista urzadzen z API uslugi.
    getStanTeamViewer: (remotecontrolId) => api.get(`/teamviewer-zdalny/stan/${remotecontrolId}`),
    getUrzadzeniaTeamViewer: (groupId) => api.get("/teamviewer-zdalny/urzadzenia", { params: { groupId } }),
    dodajKomputerTeamViewer: (data) => api.post("/teamviewer-zdalny/urzadzenia", data),
    usunKomputerTeamViewer: (deviceId) => api.delete(`/teamviewer-zdalny/urzadzenia/${deviceId}`),

    // [F-1] Przypisanie licencji pracownikowi i nadchodzace odnowienia.
    // Nazwe osoby zapisuje SERWER z konta — front wysyla wylacznie identyfikator, zeby przypisanie
    // trzymalo sie klucza konta, a nie napisu (napis rozpada sie przy zmianie nazwiska).
    getWolneKluczeLicencji: (search) => api.get("/itam/licenses/keys/available", { params: { search } }),
    /* [zgłoszenie właściciela 2026-09-11 wieczór] „dlaczego dodając licencję nie mogę wybrać komputera
       na którym zainstalowane lub pracownika albo jedno i drugie". Przypisanie przyjmuje więc OBIEKT,
       a nie samo konto: `{ userId, itamItemId }`. Licencja bywa przypięta do sprzętu stojącego
       w magazynie (wtedy nie ma właściciela) i do człowieka bez wskazanego sprzętu — obie drogi są
       poprawne, więc obie muszą dać się wyrazić. */
    przypiszKluczLicencji: (licencjaId, keyId, przypisanie = {}) =>
        api.post(`/itam/licenses/${licencjaId}/keys/${keyId}/assign`, {
            userId: przypisanie.userId ?? null,
            itamItemId: przypisanie.itamItemId ?? null,
        }),
    zwolnijKluczLicencji: (licencjaId, keyId) =>
        api.post(`/itam/licenses/${licencjaId}/keys/${keyId}/assign`, { userId: null, itamItemId: null }),
    getNadchodzaceOdnowieniaLicencji: (dni = 90) =>
        api.get("/itam/licenses/renewals/upcoming", { params: { dni } }),

    // [G-1] Sprzet PRACOWNIKA — jego wlasny, widziany z pulpitu. Osobna sciezka od /itam/*,
    // bo tamta stoi za brama ewidencji majatku, ktorej pracownik nie ma i miec nie powinien.
    getMojSprzet: () => api.get("/moj-sprzet"),
    getPomieszczeniaDoWyboru: () => api.get("/moj-sprzet/pomieszczenia"),
    zmienPomieszczenieMojegoSprzetu: (id, pomieszczenie) =>
        api.put(`/moj-sprzet/${id}/pomieszczenie`, { pomieszczenie }),

    // [E-4] Pomieszczenia zespolu i kwartalne potwierdzenie przelozonego. `status` jest lekkim
    // odczytem dla banera, ktory wisi na kazdej zakladce Mojego Dzialu — nie wolno mu ciagnac
    // calej karty, bo baner pyta przy kazdym wejsciu.
    getPomieszczeniaZespolu: () => api.get("/pomieszczenia/zespol"),
    getStatusPomieszczen: () => api.get("/pomieszczenia/status"),
    ustawPomieszczeniePracownika: (userId, data) =>
        api.put(`/pomieszczenia/pracownik/${userId}`, data),
    potwierdzPomieszczenia: (uwagi) => api.post("/pomieszczenia/potwierdz", { uwagi }),
    getHistoriaPotwierdzenPomieszczen: () => api.get("/pomieszczenia/potwierdzenia"),

    /* [zgłoszenie właściciela 2026-09-11 wieczór] PROTOKOŁ NIE IDZIE DO AKCEPTACJI ELEKTRONICZNEJ.
       „Akceptacja, skan i notatka — to powinno iść na maila, a my możemy dołączyć podpisany protokół
       ręcznie, NIE JEST WYSYŁANY DO AKCEPTACJI". Trzy wywołania ścieżki tokenowej zniknęły razem
       z endpointami; dokument idzie MAILEM z PDF-em w chwili wystawienia. */
    getMojeProtokoly: () => api.get("/protokoly/moje"),
    getStanProtokolu: (id) => api.get(`/protokoly/${id}/stan`),
    // Ręczne ponowienie wysyłki — bez tego nie da się naprawić sytuacji „poczta padła przy wystawieniu".
    // Odpowiedź to 200 TAKŻE przy niepowodzeniu (`wyslano:false` + `powod`): serwer WYKONAŁ polecenie,
    // a front rozstrzyga po TREŚCI, nie po braku wyjątku.
    wyslijProtokolMailem: (id) => api.post(`/itam/protocols/${id}/wyslij-mailem`),
    // Pozycje protokołu PRZEKAZANIA wzbogacone o DZISIEJSZY stan sprzętu — kreator zwrotu.
    getZwrotPodpowiedz: (id) => api.get(`/itam/protocols/${id}/zwrot-podpowiedz`),
    // PDF WŁASNEGO protokołu dla pracownika — bez bramki ewidencji majątku, której pracownik nie ma.
    getMojProtokolPdf: (id) => api.get(`/protokoly/${id}/pdf`, { responseType: "blob" }),
    wgrajSkanProtokolu: (id, plik) => {
        const dane = new FormData();
        dane.append("plik", plik);
        return api.post(`/protokoly/${id}/skan`, dane, { headers: { "Content-Type": "multipart/form-data" } });
    },
    pobierzSkanProtokolu: (id) => api.get(`/protokoly/${id}/skan`, { responseType: "blob" }),
    zapiszNotatkeProtokolu: (id, notatka) => api.put(`/protokoly/${id}/notatka`, { notatka }),
    zapiszNotatkeSprzetu: (itemId, notatka) => api.put(`/protokoly/sprzet/${itemId}/notatka`, { notatka }),

    // [F-8] Menedzer hasel. UWAGA NA KONTRAKT: zaden z tych odczytow NIE oddaje hasla — listy
    // wracaja z maska. Jedyna droga do jawnego hasla to `ujawnijHasloSejfu`, ktore wymaga
    // jednorazowego kodu SMS wydanego DO TEGO WPISU i zostawia slad w dzienniku.
    getSejfMoje: () => api.get("/sejf/moje"),
    getSejfZakladki: () => api.get("/sejf/zakladki"),
    getSejfBankDzialu: (departmentId) => api.get(`/sejf/dzial/${departmentId}`),
    // [zgloszenie wlasciciela 2026-09-11] „modul jest przypisany do dzialu, z ktorego wchodze,
    // sejf jest na dzial w obsludze". Ten adres NIE oddaje juz osobnego banku modulu: oddaje bank
    // DZIALU obslugujacego modul (zakres + departmentId sa w odpowiedzi). `departmentId` podaje sie
    // wylacznie wtedy, gdy modul obsluguje WIECEJ NIZ JEDEN dzial i czlowiek wybral go na ekranie.
    getSejfBankModulu: (kod, departmentId = null) => api.get(
        `/sejf/modul/${encodeURIComponent(kod)}`,
        departmentId ? { params: { departmentId } } : undefined,
    ),
    getSejfCentralny: () => api.get("/sejf/centralny"),
    dodajWpisSejfu: (dane) => api.post("/sejf/wpis", dane),
    zmienWpisSejfu: (id, dane) => api.put(`/sejf/wpis/${id}`, dane),
    usunWpisSejfu: (id) => api.delete(`/sejf/wpis/${id}`),
    wyslijKodSejfu: (id) => api.post(`/sejf/wpis/${id}/kod`),
    // [zgłoszenie właściciela 2026-09-11] Dwie drogi potwierdzenia: kod z SMS-a ALBO hasło do
    // własnego konta (gdy bramka SMS nie działa). Serwer przyjmuje jedno ALBO drugie, nigdy oba.
    ujawnijHasloSejfu: (id, kod) => api.post(`/sejf/wpis/${id}/ujawnij`, { kod }),
    ujawnijHasloSejfuHaslem: (id, haslo) => api.post(`/sejf/wpis/${id}/ujawnij`, { haslo }),
    zglosKopiowanieSejfu: (id) => api.post(`/sejf/wpis/${id}/kopiowanie`),
    getDziennikWpisuSejfu: (id) => api.get(`/sejf/wpis/${id}/log`),
    getRaportSejfu: (dni = 90) => api.get("/sejf/raport", { params: { dni } }),

    // [F-2] Konfigurator i rozliczenie OCR (usluga PLATNA — Google Document AI liczy za strone).
    getUstawieniaOcr: () => api.get("/ocr/ustawienia"),
    zapiszUstawieniaOcr: (data) => api.put("/ocr/ustawienia", data),
    getRozliczenieOcr: (miesiecy = 12) => api.get("/ocr/rozliczenie", { params: { miesiecy } }),

    // [E-3, polecenie właściciela 2026-09-11] "OCR faktury na sprzęt i KSeF".
    // Pozycje faktury -> urządzenia i licencje; rejestr kontrahentów rozpoznawanych po NIP;
    // faktura z Krajowego Systemu e-Faktur (po numerze albo z pliku XML).
    getPozycjeFaktury: (invoiceId) => api.get(`/itam/invoices/${invoiceId}/pozycje`),
    zalozZPozycjiFaktury: (invoiceId, data) => api.post(`/itam/invoices/${invoiceId}/pozycje`, data),
    // [itam1004 2026-10-04] Podgląd kolejnych wolnych numerów ewidencyjnych dla całego formularza (partią) —
    // pozycje: [{ klucz, typUrzadzeniaId, sztuk }] → [{ klucz, numery[], powod, kod }]. Niczego nie rezerwuje.
    podgladNumeracji: (pozycje) => api.post("/itam/devices/podglad-numeracji", { pozycje: pozycje || [] }),
    // [itam1004] Przypięcie istniejącej sztuki bez faktury (np. po usuniętej fakturze) do pozycji faktury.
    przypnijSztukeDoPozycji: (invoiceId, indeks, itamItemId) =>
        api.post(`/itam/invoices/${invoiceId}/pozycje/${indeks}/przypnij`, { itamItemId }),
    // [protokoly1004] „To inny egzemplarz”: odpięcie sztuki przywróconej do faktury dodanej ponownie (czyści ślad pozycji).
    odepnijPrzywroconaSztuke: (invoiceId, indeks, itamItemId) =>
        api.post(`/itam/invoices/${invoiceId}/pozycje/${indeks}/odepnij-przywrocona`, { itamItemId }),
    // [protokoly1004] Scalanie duplikatu sztuki: kandydaci (sztuki bez faktury) i samo scalenie — {id} to sztuka zbędna.
    getKandydaciScaleniaSztuk: (id, szukaj) => api.get(`/itam/devices/${id}/kandydaci-scalenia`, { params: { szukaj: szukaj || undefined } }),
    scalSztuke: (id, docelowaId) => api.post(`/itam/devices/${id}/scal`, { docelowaId }),
    // [protokoly1004] Proforma → faktura końcowa: numer, data i (opcjonalnie) plik faktury końcowej; multipart.
    dolaczFaktureKoncowa: (id, formData) => api.post(`/itam/invoices/${id}/faktura-koncowa`, formData, { headers: { "Content-Type": "multipart/form-data" } }),

    getKontrahenciItam: (params) => api.get("/itam/kontrahenci", { params }),
    getKontrahentPoNipie: (nip) => api.get("/itam/kontrahenci/po-nipie", { params: { nip } }),
    dodajKontrahentaItam: (data) => api.post("/itam/kontrahenci", data),
    zmienKontrahentaItam: (id, data) => api.put(`/itam/kontrahenci/${id}`, data),
    usunKontrahentaItam: (id) => api.delete(`/itam/kontrahenci/${id}`),

    getStanKsef: (companyId) => api.get("/itam/invoices/ksef/stan", { params: { companyId } }),
    pobierzFaktureZKsef: (data) => api.post("/itam/invoices/ksef/pobierz", data),
    wczytajFaktureZPlikuKsef: (formData) =>
        api.post("/itam/invoices/ksef/z-pliku", formData, { headers: { "Content-Type": "multipart/form-data" } }),

    /* `ile` > 1 rezerwuje ciąg kolejnych numerów — potrzebne, odkąd jedno okno zakłada wiele sztuk
       tego samego sprzętu. Numery liczy SERWER: gdyby liczyła je przeglądarka, dwie osoby zakładające
       sprzęt w tej samej minucie dostałyby ten sam numer, a numer ewidencyjny trafia na naklejkę
       i na protokół odpowiedzialności materialnej — duplikat kosztuje fizycznie. */
    getNastepnyNumerEwidencji: (deviceTypeId, ile) =>
        api.get("/itam/devices/next-inventory", {
            params: ile && ile > 1 ? { deviceTypeId, ile } : { deviceTypeId },
        }),

    getSlownikiItam: (params) => api.get("/itam/slowniki", { params }),
    getRodzajeSlownikowItam: () => api.get("/itam/slowniki/rodzaje"),
    dodajWpisSlownikaItam: (data) => api.post("/itam/slowniki", data),

    // ── [W1 2026-09-14] Karty SIM — zestawienia (api/itam/sim) ──
    getKartySim: (params) => api.get("/itam/sim", { params }),
    /* [polecenie właściciela 2026-09-16] TABELA ZBIORCZA: karty SIM + numery komórkowe z Office 365 bez karty +
       rejestr stacjonarnych + numery stacjonarne z O365 bez wiersza w rejestrze — JEDNO zapytanie oddające naraz
       wiersze, liczniki KAŻDEGO widoku i metryczkę. Zastępuje na ekranie pięć osobnych końcówek. */
    getZestawienieTelefonow: (params) => api.get("/itam/sim/zestawienie", { params }),
    getKartaSim: (id) => api.get(`/itam/sim/${id}`),
    dodajKarteSim: (data) => api.post("/itam/sim", data),
    zmienKarteSim: (id, data) => api.put(`/itam/sim/${id}`, data),
    przypiszKarteSim: (id, data) => api.post(`/itam/sim/${id}/przypisz`, data),
    wycofajKarteSim: (id) => api.delete(`/itam/sim/${id}`),
    getHistoriaKartySim: (id) => api.get(`/itam/sim/${id}/historia`),
    /* [polecenie wlasciciela 2026-09-19, BE-4] „gdzie jest historia numeru — dlaczego jak klikam w jakis numer nie widze
       jego historii skad dokad pochodzil”. Historia po NUMERZE, nie po karcie: wiersz zestawienia bywa numerem z Office
       365 albo z rejestru stacjonarnych, czyli bez identyfikatora karty — a pytanie i tak dotyczy numeru. */
    getHistoriaNumeru: (numer) => api.get("/itam/sim/historia-numeru", { params: { numer } }),
    importujKartySim: (formData) => api.post("/itam/sim/import", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    getNumeryStacjonarne: (params) => api.get("/itam/sim/stacjonarne", { params }),
    /* [2026-09-15] Numery stacjonarne z ZESTAWIENIA FIRMOWEGO — drugie źródło zakładki, obok numerów z Office 365.
       Rejestr własny aplikacji (numer wewnętrzny, MPK, osoba, stanowisko, koszt), więc ma pełny CRUD i import pliku. */
    getNumeryStacjonarneFirmowe: (params) => api.get("/itam/sim/stacjonarne-firmowe", { params }),
    dodajNumerStacjonarnyFirmowy: (data) => api.post("/itam/sim/stacjonarne-firmowe", data),
    zmienNumerStacjonarnyFirmowy: (id, data) => api.put(`/itam/sim/stacjonarne-firmowe/${id}`, data),
    usunNumerStacjonarnyFirmowy: (id) => api.delete(`/itam/sim/stacjonarne-firmowe/${id}`),
    importujNumeryStacjonarneFirmowe: (formData) =>
        api.post("/itam/sim/stacjonarne-firmowe/import", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    /* [polecenie właściciela 2026-09-15] „dopisz 13 432, bo to jest cały numer stacjonarny” + „od razu mapował numer
       z kontem w Office 365”: przeliczenie pełnych numerów prefiksem z ustawienia i porównanie rejestru z O365. */
    uzupelnijPelneNumeryStacjonarne: () => api.post("/itam/sim/stacjonarne-firmowe/uzupelnij-pelne"),
    getPorownanieStacjonarnychO365: () => api.get("/itam/sim/stacjonarne-firmowe/porownanie-o365"),
    /* [polecenie właściciela 2026-09-15] Lista jest ZAWĘŻONA na serwerze do spółki zalogowanego + numerów „dla wszystkich
       spółek” — front niczego nie dofiltrowuje i nie da się tego obejść z przeglądarki. */
    getNumeryWazne: () => api.get("/itam/sim/wazne"),
    // [2026-09-15] Spółki do wyboru zakresu numeru ważnego — za bramką kart SIM (KartySimAccess), bez nowego klucza.
    getSpolkiKartSim: () => api.get("/itam/sim/spolki"),
    getPodsumowanieKartSim: () => api.get("/itam/sim/podsumowanie"),
    getPorownanieSimO365: () => api.get("/itam/sim/porownanie-o365"),
    // [2026-09-15] Eksport zestawienia kart SIM (xlsx | pdf) — te same parametry co lista, odpowiedź jako blob.
    eksportKartySim: (params) => api.get("/itam/sim/eksport", { params, responseType: "blob" }),
    // [W2] „stan jako IT” — licencje + karty SIM na przeglądzie ITAM.
    getStanIt: () => api.get("/itam/stan-it"),
    zmienWpisSlownikaItam: (id, data) => api.put(`/itam/slowniki/${id}`, data),
    usunWpisSlownikaItam: (id) => api.delete(`/itam/slowniki/${id}`),
    // [itam1004] Typ bez prefiksu numeracji wymaga potwierdzenia (serwer: 409 TYP_BEZ_PREFIKSU) — `opcje.potwierdzamBrakPrefiksu`.
    createItamDeviceType: (data, opcje) => api.post("/itam/device-types", data,
        opcje?.potwierdzamBrakPrefiksu ? { params: { potwierdzamBrakPrefiksu: true } } : undefined),
    updateItamDeviceType: (id, data, opcje) => api.put(`/itam/device-types/${id}`, data,
        opcje?.potwierdzamBrakPrefiksu ? { params: { potwierdzamBrakPrefiksu: true } } : undefined),
    deleteItamDeviceType: (id) => api.delete(`/itam/device-types/${id}`),

    // --- ITAM: karta SIM w telefonie ---
    // [M15, FALA 3 audytu modelu danych 2026-09-26] Karta SIM w telefonie = karta z REJESTRU (api/itam/sim). Końcówki
    // `/itam/devices/{id}/sim-card` (drugi model karty — ItamSimCards) zniknęły z serwera razem z tym modelem.
    getKartySimUrzadzenia: (deviceId) => api.get(`/itam/sim/urzadzenie/${deviceId}`),
    getKartySimDoUrzadzenia: (szukaj) => api.get("/itam/sim/do-urzadzenia", { params: { szukaj } }),
    umiescKarteSimWUrzadzeniu: (kartaId, itamItemId) => api.put(`/itam/sim/${kartaId}/urzadzenie`, { itamItemId }),

    // --- ITAM Protocols ---
    getItamProtocols: (params) => api.get("/itam/protocols", { params }),
    // [2026-09-14] Sprzęt wystawiony na osobę bez czynnego protokołu przekazania — do wystawienia z listy protokołów.
    getSprzetBezProtokolu: () => api.get("/itam/protocols/bez-protokolu"),
    // [protokoly1004] Liczniki nagłówka listy (cały zbiór, „w tym miesiącu” w czasie polskim) — jedno żądanie zamiast trzech.
    getLicznikiProtokolow: (params) => api.get("/itam/protocols/liczniki", { params }),
    // [protokoly1004] Wszystkie braki w dokumentacji sprzętu (bez protokołu / niezaksięgowane / bez numeru).
    getBrakiItam: () => api.get("/itam/protocols/braki"),
    // [protokoly1004] Cotygodniowe zgłoszenie dla działu IT o brakach w dokumentacji: ustawienia, zapis, ręczny przebieg.
    getPrzypomnienieBrakow: () => api.get("/itam/protocols/przypomnienie-brakow"),
    zapiszPrzypomnienieBrakow: (dane) => api.put("/itam/protocols/przypomnienie-brakow", dane),
    uruchomPrzypomnienieBrakow: () => api.post("/itam/protocols/przypomnienie-brakow/uruchom"),
    getItamProtocol: (id) => api.get(`/itam/protocols/${id}`),
    /** Domyślna wartość checkboxa „Dołącz regulamin” w kreatorze — ustawia HR w /admin/hr-config. */
    getUstawieniaProtokolowDlaKreatora: () => api.get("/itam/protocols/ustawienia"),
    createHandoverProtocol: (data) => api.post("/itam/protocols/handover", data),
    createReturnProtocol: (data) => api.post("/itam/protocols/return", data),
    createAmendmentProtocol: (id, data) => api.post(`/itam/protocols/${id}/amendment`, data),
    getProtocolPdf: (id) => api.get(`/itam/protocols/${id}/pdf`, { responseType: "blob" }),
    getProtocolPreview: (id) => api.get(`/itam/protocols/${id}/preview`, { responseType: "blob" }),
    getProtocolPreviewHtml: (id) => api.get(`/itam/protocols/${id}/preview-html`, { responseType: "text" }),
    getProtocolsByEmployee: (addressBookId) => api.get(`/itam/protocols/by-employee/${addressBookId}`),
    // Protokoły przekazania/zwrotu sprzętu pracownika — ZAKRESOWO dla przełożonego (nie admin-only).
    getMyDeptEquipmentProtocols: (userId) => api.get(`/my-dept/equipment/protocols/${userId}`),
    getMyDeptEquipmentProtocolPdf: (protocolId) => api.get(`/my-dept/equipment/protocols/pdf/${protocolId}`, { responseType: "blob" }),
    getProtocolsByDevice: (itemId) => api.get(`/itam/protocols/by-device/${itemId}`),
    // [A11-30] Wyszukiwanie osob liczy SERWER (fraza + dzial + stanowisko + sortowanie + limit).
    // Drugi argument jest OPCJONALNY, wiec starsze wywolanie searchProtocolUsers("Kowalska") dziala dalej.
    searchProtocolUsers: (search, opcje = {}) =>
        api.get("/itam/protocols/users-search", {
            params: {
                search,
                department: opcje.department,
                position: opcje.position,
                sort: opcje.sort,
                limit: opcje.limit,
            },
        }),

    // --- ITAM Licenses ---
    getItamLicenses: (params) => api.get("/itam/licenses", { params }),
    getItamLicense: (id) => api.get(`/itam/licenses/${id}`),
    createItamLicense: (data) => api.post("/itam/licenses", data),
    updateItamLicense: (id, data) => api.put(`/itam/licenses/${id}`, data),
    deleteItamLicense: (id) => api.delete(`/itam/licenses/${id}`),
    createItamLicenseKey: (licenseId, data) => api.post(`/itam/licenses/${licenseId}/keys`, data),
    updateItamLicenseKey: (licenseId, keyId, data) => api.put(`/itam/licenses/${licenseId}/keys/${keyId}`, data),
    deleteItamLicenseKey: (licenseId, keyId) => api.delete(`/itam/licenses/${licenseId}/keys/${keyId}`),
    createItamLicenseRenewal: (licenseId, data) => api.post(`/itam/licenses/${licenseId}/renewals`, data),

    // --- ITAM Licenses — O365 & Helpers ---
    // [S-2] Licencje PER TENANT: companyId = tylko ta spółka; bez — wszystkie spółki (każdy wiersz z nazwą spółki).
    getO365LicenseSkus: (companyId = null) => api.get("/itam/licenses/o365/skus", { params: { companyId: companyId ?? undefined } }),
    getO365SkuUsers: (skuId, companyId = null) =>
        api.get(`/itam/licenses/o365/skus/${skuId}/users`, { params: { companyId: companyId ?? undefined } }),
    getActivationEmails: () => api.get("/itam/licenses/activation-emails"),

    // --- ITAM TeamViewer ---
    getItamTeamViewers: (params) => api.get("/itam/teamviewer", { params }),
    getItamTeamViewer: (id) => api.get(`/itam/teamviewer/${id}`),
    createItamTeamViewer: (data) => api.post("/itam/teamviewer", data),
    updateItamTeamViewer: (id, data) => api.put(`/itam/teamviewer/${id}`, data),
    deleteItamTeamViewer: (id) => api.delete(`/itam/teamviewer/${id}`),
    getTeamViewerByEmail: (email) => api.get(`/itam/teamviewer/by-email`, { params: { email } }),
    /* [C-4 2026-09-10] Chude endpointy przypisania stacji do człowieka.
       ⚠ NIE używać do tego getItamTeamViewer/updateItamTeamViewer: tamten GET odszyfrowuje HASŁO
       i zapisuje w audycie „Odsłonięto hasło", a tamten PUT traktuje puste hasło jako polecenie
       WYCZYSZCZENIA. Przypisanie osoby nie ma prawa dotknąć sekretu stacji. */
    getTeamViewerAssignment: (id) => api.get(`/itam/teamviewer/${id}/assignment`),
    setTeamViewerAssignment: (id, data) => api.put(`/itam/teamviewer/${id}/assignment`, data),
    getTeamViewersByUser: (userId) => api.get(`/itam/teamviewer/by-user/${userId}`),

    /* [polecenie wlasciciela 2026-09-19] DWUKIERUNKOWA EWIDENCJA SPRZET ↔ PRACOWNIK.
       „sprzet rotuje, a my musimy dbac o zestawienie”. Zestawienie niesie sprzet przypisany do kont
       ZABLOKOWANYCH (do odebrania), podpowiedzi wyliczaja sie z nazw urzadzen i NICZEGO nie zapisuja —
       zapis nastepuje dopiero przez potwierdzPropozycjeTeamViewer, czyli klikniecie czlowieka.
       Dodanie sprzetu przyjmuje WYLACZNIE grupe wlasnej spolki (serwer odrzuca cudza z kodem 403). */
    getZestawienieSprzetuTeamViewer: (fraza) =>
        api.get("/itam/teamviewer-ewidencja/zestawienie", { params: { fraza } }),
    getHistoriaPrzypisanTeamViewer: (id) => api.get(`/itam/teamviewer-ewidencja/${id}/historia`),
    /* [BI-3 2026-09-20] „zrob powiazania sprzetu i nazwy sprzetu z userem, bo po to zachowujemy historie
       zmian sprzetu w tym TeamViewer”. Dwa pytania, jedno zrodlo (tabela okresow posiadania):
       karta SPRZETU pyta „kto mial ta stacje”, karta PRACOWNIKA — „co przez niego przeszlo”. */
    getHistoriaStacjiSprzetu: (itamItemId) =>
        api.get(`/itam/teamviewer-ewidencja/sprzet/${itamItemId}/historia`),
    getHistoriaStacjiPracownika: (userId) =>
        api.get(`/itam/teamviewer-ewidencja/pracownik/${userId}/historia`),
    getPropozycjeTeamViewer: (limit) =>
        api.get("/itam/teamviewer-ewidencja/propozycje", { params: { limit } }),
    /* [BI-2 2026-09-20] Ta sama droga obsluguje POTWIERDZENIE podpowiedzi i RECZNE wskazanie osoby
       przy stacji z drzewa (`recznie: true`). Roznica nie jest kosmetyczna — ladnie w `Zrodlo` okresu
       posiadania, czyli w jedynym polu odpowiadajacym na pytanie „wybral to czlowiek czy automat”. */
    potwierdzPropozycjeTeamViewer: (data) => api.post("/itam/teamviewer-ewidencja/propozycje/potwierdz", data),
    /* [BI-2] Zbiorcze zastosowanie trafien PEWNYCH. Serwer przelicza kazda pozycje od nowa i odrzuca te,
       ktore dzis nie sa jednoznaczne — zadanie niesie to, co czlowiek widzial, a nie polecenie „zapisz”. */
    zastosujPodpowiedziTeamViewer: (pozycje) =>
        api.post("/itam/teamviewer-ewidencja/propozycje/zastosuj-wiele", { pozycje }),
    /* [BI-2] Cofniecie partii — zdejmuje przypisania ze wskazanych wpisow. Historii NIE kasuje:
       okres posiadania zostaje domkniety, tak samo jak przy zwrocie sprzetu. */
    cofnijPrzypisaniaTeamViewer: (ids) =>
        api.post("/itam/teamviewer-ewidencja/przypisania/zdejmij-wiele", { ids }),
    getGrupyTeamViewerDoZapisu: () => api.get("/itam/teamviewer-ewidencja/grupy-do-zapisu"),
    dodajSprzetTeamViewer: (data) => api.post("/itam/teamviewer-ewidencja/sprzet", data),
    zapiszNazweWTeamViewerze: (id, data) =>
        api.post(`/itam/teamviewer-ewidencja/${id}/nazwa-w-teamviewerze`, data),

    // --- TeamViewer Config ---
    getTeamViewerApiStatus: () => api.get("/teamviewer-config/status"),
    getTeamViewerRemoteGroups: () => api.get("/teamviewer-config/groups/remote"),
    getTeamViewerGroupMappings: () => api.get("/teamviewer-config/groups/mappings"),
    createTeamViewerGroupMapping: (data) => api.post("/teamviewer-config/groups/mappings", data),
    updateTeamViewerGroupMapping: (id, data) => api.put(`/teamviewer-config/groups/mappings/${id}`, data),
    deleteTeamViewerGroupMapping: (id) => api.delete(`/teamviewer-config/groups/mappings/${id}`),
    /* [polecenie wlasciciela 2026-09-17] „TeamViewer ma u mnie grupy folderow, chce konkretna grupe —
       jedna lub kilka — przypisac do firmy i wyswietlac to odpowiednio”. Jedno zadanie = KOMPLET
       spolek jednej grupy: skladanie tego z trzech zadan w przegladarce zostawiaoby przypisanie
       w polowie przy zerwanym polaczeniu, a polowa przypisania to albo ukryte komputery, albo
       pokazane cudze. */
    getTeamViewerGroupAssignments: () => api.get("/teamviewer-config/groups/assignments"),
    setTeamViewerGroupAssignment: (data) => api.put("/teamviewer-config/groups/assignments", data),
    /* [polecenie wlasciciela 2026-09-19] „aktualizuj je jak ktos zrobi nowa w TeamViewer, tak samo jak
       przeniesie ktos urzadzenie z grupy do grupy automatycznie to aktualizuj”. Drzewo grup idzie z LUSTRA
       w bazie (odswiezanego w tle co 15 min) — ten wpis wymusza odswiezenie TERAZ, bez czekania na cykl.
       Odswiezenie NIE przypisuje nowej grupy do zadnej spolki: nowa grupa pojawia sie ukryta. */
    syncTeamViewerGroups: () => api.post("/teamviewer-config/groups/sync"),
    /* 🔴 [BI-1 2026-09-20] „Importuj grupy z TV” idzie Z LUSTRA, nie z zywego API uslugi.
       BYLO ZLE: przycisk wolal getTeamViewerRemoteGroups() (GET /groups/remote). Nieudane wywolanie
       nie otwieralo nawet okna — zostawal czerwony pasek „Sprawdz token”, a tabela mapowan stala pusta.
       A tabela mapowan jest WLACZNIKIEM podzialu na spolki, wiec jedyna droga do jej wypelnienia
       zalezala od chwilowej dostepnosci uslugi. Teraz lista kandydatow idzie z tego samego drzewa,
       ktore panel i tak pokazuje (getTeamViewerGroupAssignments), a zapis jest zbiorczy i idempotentny:
       para (grupa, spolka), ktora juz jest, zostaje POMINIETA, nie zdublowana. */
    importujGrupyTeamViewerZLustra: (przypisania) =>
        api.post("/teamviewer-config/groups/import-from-mirror", { przypisania }),
    /* [polecenie wlasciciela 2026-09-20, BI-7] „zrob mi tak ze jak wczytam sobie dana grupe, to mi tam
       wypisz urzadzenia ktore wchodza w sklad grupy z opcja ich usuwania i dodawania”. Sklad idzie
       z LUSTRA (nie z odpytania uslugi przy kazdym rozwinieciu grupy): trzynascie grup rozwinietych po
       kolei to trzynascie wywolan TeamViewera, a limit licencji jest wspolny ze stanem online w kartach
       zgloszen. Dodawanie i usuwanie ida juz DO USLUGI (dodajKomputerTeamViewer / usunKomputerTeamViewer)
       — zmiana tylko w lustrze zostalaby zdmuchnieta przy najblizszej synchronizacji. */
    getTeamViewerGrupaUrzadzenia: (groupId) =>
        api.get(`/teamviewer-config/groups/${encodeURIComponent(groupId)}/urzadzenia`),
    getTeamViewerImportPreview: (groupId) => api.post("/teamviewer-config/import/preview", { groupId }),
    /* [OBSZAR 24, 2026-09-18] Polaczenie aplikacji OAuth z TeamViewerem (token skryptowy na serwerze ma pierwszenstwo).
       Stan NIGDY nie niesie sekretu ani tokenow — tylko fakt, ze sa ustawione. */
    getTeamViewerConnection: () => api.get("/integration-hub/teamviewer/connection"),
    saveTeamViewerApplication: (data) => api.put("/integration-hub/teamviewer/connection", data),
    startTeamViewerAuthorization: () => api.post("/integration-hub/teamviewer/connection/authorize"),
    finishTeamViewerAuthorization: ({ code, state }) => api.post("/integration-hub/teamviewer/connection/callback", { code, state }),
    disconnectTeamViewer: () => api.delete("/integration-hub/teamviewer/connection"),
    confirmTeamViewerImport: (entries) => api.post("/teamviewer-config/import/confirm", { entries }),

    // --- OCR ---
    // [F-2] `obszar` trafia do rozliczenia OCR. Faktury IT i poczekalnia akt to rozne budzety,
    // a jedna suma nie pozwala odpowiedziec, skad wzial sie wzrost kosztu.
    ocrExtract: (formData, queryParams = "", obszar = "nieznany") =>
        api.post(`/ocr/extract${queryParams}`, formData, {
            headers: { "Content-Type": "multipart/form-data", "X-Ocr-Obszar": obszar },
        }),

    // --- USERS (Admin) ---
    // Pobranie zalacznika wniosku HR (blob, gated po stronie backendu)
    downloadHrRequestAttachment: (requestId, attachmentId) =>
        api.get(`/hr-requests/${requestId}/attachments/${attachmentId}`, { responseType: "blob" }),
    // Upload zalacznikow do wniosku HR (multipart; fix: wczesniej pliki byly porzucane)
    uploadHrRequestAttachments: (requestId, files) => {
        const fd = new FormData();
        (files || []).forEach((f) => fd.append("files", f));
        return api.post(`/hr-requests/${requestId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    // [POLECENIE DODATKOWE 2026-09-01] `hideTechnical` = przełącznik „Ukryj konta techniczne"
    // z paska filtrów /admin/users. Filtr jedzie DO SERWERA (warunek w SQL na kanonicznym
    // AccountKind), a nie odsiewa wierszy już pobranych do przeglądarki.
    getUsers: (params) => api.get("/admin/users", { params }),
    // [OBSADA 2026-07-27] Kartoteka w trybie ODCZYTU — polityka AdminUsersRead (admin/moderator
    // LUB nadany klucz AdminUsersView). /admin/users jest AdminOnly i dawał 403 obsadzie kadr,
    // przez co kafelek "Kartoteka pracowników" był martwy dla nie-admina.
    getKadryUsers: (search) => api.get("/kadry/users", { params: { search } }),
    getKadryUser: (id) => api.get(`/kadry/users/${id}`),
    /**
     * [brief 2026-09-04, poz. 29] ZAKŁADKA „ZARZĄD" — dwa WEJŚCIA, bo dwa ekrany mają dwie różne
     * bramki (AdminOnly vs AdminUsersRead) i dwa różne zestawy kolumn. To NIE jest ten sam endpoint
     * pod dwoma nazwami: `/admin/users/zarzad` odpowiada danymi o KONTACH (ranga, logowania),
     * `/kadry/users/zarzad` danymi KADROWYMI (stanowisko, przynależność do działu). Zakres pól
     * zacina serwer (WidokZarzadu.Ogranicz), nie przeglądarka.
     */
    getZarzadKonta: () => api.get("/admin/users/zarzad"),
    getZarzadKadry: () => api.get("/kadry/users/zarzad"),
    // Import pracownikow z CSV (front parsuje CSV -> wiersze; commit=false = walidacja/podglad)
    importEmployees: (data) => api.post("/admin/users/import-employees", data),
    // Lekkie wyszukiwanie osób dla pickerów — dostępne dla KAŻDEGO zalogowanego (nie admin-only).
    searchPeople: (q, take = 25) => api.get("/users/search", { params: { q, take } }),
    createUser: (userData) => api.post("/admin/users", userData),
    // !!! DODANO BRAKUJĄCĄ METODĘ !!!
    // [1.3 b] `potwierdzamOsierocenie` = świadoma zgoda na degradację do „Byłego pracownika" mimo
    // tego, że konto jest wpisane jako bezpośredni przełożony innych osób. Bez flagi backend zwraca
    // 409 z listą osieroconych, a panel otwiera okno przepięcia podwładnych.
    updateUser: (id, userData, potwierdzamOsierocenie = false) =>
        api.put(`/admin/users/${id}`, userData, { params: potwierdzamOsierocenie ? { potwierdzamOsierocenie: true } : {} }),

    updateUserPermissions: (id, permissions) =>
        api.put(`/admin/users/${id}/permissions`, permissions),
    // [1.3 b] Bez `potwierdzamOsierocenie` blokada konta będącego przełożonym kończy się 409
    // (kod CIAGLOSC_STRUKTURY) z listą osób, które zostałyby bez przełożonego.
    blockUser: (id, potwierdzamOsierocenie = false) =>
        api.put(`/admin/users/${id}/block`, null, { params: potwierdzamOsierocenie ? { potwierdzamOsierocenie: true } : {} }),
    unblockUser: (id) => api.put(`/admin/users/${id}/unblock`),
    // [KA-05 2026-09-26] Administrator: wyloguj konto ze wszystkich urządzeń (bez blokowania konta).
    wylogujKontoWszedzie: (id) => api.post(`/admin/users/${id}/revoke-sessions`),
    // Dane do modala blokady (dział główny + przełożony do podpowiedzi).
    getUserBlockInfo: (id) => api.get(`/admin/users/${id}/block-info`),
    // [RODZAJ KONTA 2026-08-30] JEDYNA droga zapisu oznaczenia „skrzynka techniczna / konto
    // niebędące pracownikiem". Osobny endpoint, bo zwykły PUT /admin/users/{id} celowo ignoruje
    // te pola — inaczej edycja danych kadrowych zdejmowałaby oznaczenie „przy okazji".
    // payload: { kind: 0|1|2, showInAddressBook: bool, note: string|null }
    setUserAccountKind: (id, payload) => api.put(`/admin/users/${id}/account-kind`, payload),
    /* [O-2, domkniete 2026-09-08] `potwierdzamOsierocenie` przekazuje SWIADOMA zgode administratora
       na wygaszenie konta, ktore jest czyims wpisanym przelozonym. Bez tej flagi serwer oddaje 409
       CIAGLOSC_STRUKTURY — a to jest dokladnie ten przypadek, ktory sie offboarduje najczesciej,
       wiec ekran bez drogi naprzod blokowal operacje na kontach, dla ktorych powstala. */
    deleteUser: (id, potwierdzamOsierocenie = false) =>
        api.delete(`/admin/users/${id}`, { params: potwierdzamOsierocenie ? { potwierdzamOsierocenie: true } : undefined }),
    getUserLogs: (id) => api.get(`/admin/users/${id}/logs`),
    getUserInfo: (id) => api.get(`/admin/users/${id}/info`),
    // Pojedynczy pracownik ze ŚWIEŻYMI danymi kadrowymi (daty, wymiar etatu, znacznik wersji).
    // Ekrany kadrowe nie mogą polegać na snapshocie z listy — patrz EmployeeWorkTimePage.
    getUser: (id) => api.get(`/admin/users/${id}`),
    getUserConfigurations: (id) => api.get(`/admin/users/${id}/configurations`),
    updateUserConfigurations: (id, payload) =>
        api.put(`/admin/users/${id}/configurations`, payload),
    getUsersForSelect: (params) => api.get("/admin/users", { params }),
    syncO365Users: () => api.post("/admin/users/sync-o365", null, { timeout: 300000 }),
    syncManagersToHeads: () => api.post("/admin/users/sync-managers", null, { timeout: 300000 }),

    // --- WSPÓLNA SYNCHRONIZACJA O365 (1 wskaźnik dla wszystkich paneli + publicznej książki) ---
    getO365SyncStatus: () => api.get("/integrations/o365/sync-status"),
    triggerO365Sync: (force = true) => api.post("/integrations/o365/sync", null, { params: { force }, timeout: 300000 }),

    // --- CONFIGURATIONS ---
    getAllConfigurations: (companyId) => api.get("/configurations", companyId != null ? { params: { companyId } } : undefined),
    createConfiguration: (configData) => api.post("/configurations", configData),
    updateConfiguration: (id, configData) =>
        api.put(`/configurations/${id}`, configData),
    deleteConfiguration: (id) => api.delete(`/configurations/${id}`),
    enableConfiguration: (id) => api.post(`/configurations/enable/${id}`),
    disableConfiguration: (id) => api.post(`/configurations/disable/${id}`),

    // --- CROSS-ORG (udostępnienia firma -> firma) ---
    getCrossOrgShares: (targetCompanyId) => api.get("/cross-org", targetCompanyId != null ? { params: { targetCompanyId } } : undefined),
    createCrossOrgShare: (data) => api.post("/cross-org", data),
    toggleCrossOrgShare: (id) => api.put(`/cross-org/${id}/toggle`),
    deleteCrossOrgShare: (id) => api.delete(`/cross-org/${id}`),

    // --- CATEGORIES ---
    // [G1] params: { module, handlingDepartmentId, includeInactive } — domyślnie tylko aktywne.
    getCategories: (params) => api.get("/categories", { params }),
    createCategory: (categoryData) => api.post("/categories", categoryData),
    updateCategory: (id, categoryData) =>
        api.put(`/categories/${id}`, categoryData),
    deleteCategory: (id) => api.delete(`/categories/${id}`),
    seedCategories: () => api.post("/categories/seed"),

    /* [zgłoszenie właściciela 2026-09-17] Słownik reguł przypisań zgłoszeń: „fraza X → kategoria Y".
       Reguła ustawia WYŁĄCZNIE kategorię — priorytet i czasy przychodzą z niej samej. */
    getRegulyPrzypisan: () => api.get("/categories/reguly-przypisan"),
    // [domknięcie F 2026-10-01] Sekcje „Zapotrzebowanie” i „Onboarding + Offboarding” jako karty /admin/categories.
    getSekcjeGlowneKategorii: () => api.get("/categories/sekcje-glowne"),
    dodajRegulePrzypisania: (dane) => api.post("/categories/reguly-przypisan", dane),
    zapiszRegulePrzypisania: (id, dane) => api.put(`/categories/reguly-przypisan/${id}`, dane),
    usunRegulePrzypisania: (id) => api.delete(`/categories/reguly-przypisan/${id}`),
    sprawdzRegulePrzypisania: (dane) => api.post("/categories/reguly-przypisan/sprawdz", dane),

    // --- DRIVES (Dynamic Settings) ---
    getDrives: (userId) => api.get("/dynamic-settings/drives", { params: { userId } }),
    /* [zgłoszenie właściciela 2026-09-16] Adres serwera plików — jedno ustawienie instalacji, z którego
       powstaje pełna ścieżka UNC. Bez niego kopiowanie dawało sam udział z nazwą, bez serwera. */
    getAdresSerweraDyskow: () => api.get("/dynamic-settings/drives/adres-serwera"),
    setAdresSerweraDyskow: (adres) => api.put("/dynamic-settings/drives/adres-serwera", { adres }),
    getDriveMasterAddresses: () => api.get("/dynamic-settings/drives/master-addresses"),
    // [J-6] Skrzynki (domeny) do wyboru w szufladzie dysku — WASKA projekcja {id, displayName} pod bramka
    // dyskow. Szuflada wolala wczesniej getAllConfigurations(), czyli kontroler stojacy w calosci za
    // AdminOnly: posiadacz klucza DriveManage dostawal 403, lista domen byla pusta i dodanie zasobu
    // nie dawalo sie dokonczyc.
    getDriveConfigurations: () => api.get("/dynamic-settings/drives/configurations"),
    getDriveAssignedUsers: () => api.get("/dynamic-settings/drives/assigned-users"),
    createDrive: (driveData) => api.post("/dynamic-settings/drives", driveData),
    updateDrive: (id, driveData) =>
        api.put(`/dynamic-settings/drives/${id}`, driveData),
    deleteDrive: (id) => api.delete(`/dynamic-settings/drives/${id}`),

    getDriveUsers: (id) => api.get(`/dynamic-settings/drives/${id}/users`),
    addUserToDrive: (id, data) =>
        api.post(`/dynamic-settings/drives/${id}/users`, data),
    removeUserFromDrive: (id, userId) =>
        api.delete(`/dynamic-settings/drives/${id}/users/${userId}`),

    /* [BH-1/BH-2 2026-09-19] Lista zasobow do wniosku, zawezana SPOLKA i RODZAJEM zasobu.
       Cytat wlasciciela: „dodaj opcje podzialu firmy do wyboru, poniewaz moze byc tak, ze dysk
       dotyczy innej firmy […] oraz dodaj opcje zlozenia dysku sieciowego sharepoint".
       Pusty parametr NIE moze pojechac jako `companyId=` ani `kind=` — serwer czyta wtedy `0`/pusty
       lancuch i wypada z galezi „bez zawezenia". Dlatego `undefined`: axios takich pol nie wysyla. */
    getAllDrivesGrouped: (search, companyId = null, kind = null) =>
        api.get("/my-tickets/all-drives-grouped", {
            params: {
                search,
                companyId: companyId ? Number(companyId) : undefined,
                kind: kind || undefined,
            },
        }),

    // --- ACCEPTANCE ---
    checkAcceptance: (code) => api.get(`/acceptance/${code}`),
    confirmAcceptance: (code, data) => api.post(`/acceptance/${code}`, data),

    updateAddressBookVisibility: (id, isVisible) =>
        api.patch(`/dynamic-settings/address-book/${id}/visibility`, { isVisible }),

    updateAddressBookContact: (id, data) =>
        api.patch(`/dynamic-settings/address-book/${id}/contact`, data),

    getAddressBookByEmail: (email) =>
        api.get("/dynamic-settings/address-book/by-email", { params: { email } }),

    // --- ONBOARDING ---
    getOnboardingRequests: (params) => api.get("/onboarding", { params }),
    getOnboardingRequest: (id) => api.get(`/onboarding/${id}`),
    createOnboardingRequest: (data) => api.post("/onboarding", data),
    updateOnboardingRequest: (id, data) => api.put(`/onboarding/${id}`, data),
    deleteOnboardingRequest: (id) => api.delete(`/onboarding/${id}`),
    getOnboardingEnums: () => api.get("/onboarding/enums"),
    changeOnboardingStatus: (id, status) =>
        api.patch(`/onboarding/${id}/status`, { status }),

    getOnboardingItems: (requestId) => api.get(`/onboarding/${requestId}/items`),
    addOnboardingItem: (requestId, data) =>
        api.post(`/onboarding/${requestId}/items`, data),
    updateOnboardingItem: (itemId, data) =>
        api.put(`/onboarding/items/${itemId}`, data),
    deleteOnboardingItem: (itemId) => api.delete(`/onboarding/items/${itemId}`),

    generateProtocol: (data) => api.post("/onboarding/generate-protocol", data),

    // --- KNOWLEDGE BASE ---
    getKnowledgeBaseArticles: (onlyVisible) =>
        api.get("/KnowledgeBase", { params: { onlyVisible } }),
    getKnowledgeBaseArticle: (id) => api.get(`/KnowledgeBase/${id}`),
    getKnowledgeBaseHistory: (id) => api.get(`/KnowledgeBase/${id}/history`),
    createKnowledgeBaseArticle: (data, onUploadProgress) => api.post("/KnowledgeBase", data, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
    }),
    updateKnowledgeBaseArticle: (id, data, onUploadProgress) =>
        api.put(`/KnowledgeBase/${id}`, data, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress,
        }),
    deleteKnowledgeBaseArticle: (id) => api.delete(`/KnowledgeBase/${id}`),
    toggleKnowledgeBaseVisibility: (id) => api.patch(`/KnowledgeBase/${id}/visibility`),
    uploadKnowledgeBaseImage: (formData) =>
        api.post("/KnowledgeBase/upload-image", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        }),
    // [OBSZAR 9, 2026-09-18] Załączniki, zdjęcia i nagrania artykułu — pola formularza „files" (wiele naraz).
    // Serwer waliduje typ i rozmiar (400 z komunikatem jako NAPIS w err.response.data — nie w .message).
    uploadKnowledgeBaseAttachments: (articleId, formData, onUploadProgress) =>
        api.post(`/KnowledgeBase/${articleId}/attachments`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress,
        }),
    deleteKnowledgeBaseAttachment: (attachmentId) => api.delete(`/KnowledgeBase/attachments/${attachmentId}`),
    /**
     * Adres pobrania/odtworzenia załącznika Bazy wiedzy. ZAWSZE przez API (bramka widoczności publikacji),
     * nigdy przez ścieżkę pliku na dysku — statyk /uploads/kb/attachments odpowiada 404.
     * `download: true` wymusza okno zapisu także dla zdjęć i nagrań (domyślnie idą inline do <img>/<video>).
     */
    knowledgeBaseAttachmentUrl: (attachmentOrId, { download = false } = {}) => {
        const id = typeof attachmentOrId === "object" && attachmentOrId ? attachmentOrId.id : attachmentOrId;
        return `${FILES_BASE_URL}/api/KnowledgeBase/attachments/${id}${download ? "?download=true" : ""}`;
    },
    // Kategorie Bazy wiedzy (admin definiuje; filtr chipów na /baza-wiedzy)
    getKnowledgeBaseCategories: () => api.get("/KnowledgeBase/categories"),
    getKnowledgeBaseCategoriesManage: () => api.get("/KnowledgeBase/categories/manage"),
    createKnowledgeBaseCategory: (data) => api.post("/KnowledgeBase/categories", data),
    updateKnowledgeBaseCategory: (id, data) => api.put(`/KnowledgeBase/categories/${id}`, data),
    deleteKnowledgeBaseCategory: (id) => api.delete(`/KnowledgeBase/categories/${id}`),

    // --- FAQ ---
    getFaqItems: () => api.get("/Faq"),
    getFaqItemsAdmin: () => api.get("/Faq/admin"),
    createFaqItem: (data) => api.post("/Faq", data),
    updateFaqItem: (id, data) => api.put(`/Faq/${id}`, data),
    deleteFaqItem: (id) => api.delete(`/Faq/${id}`),
    toggleFaqVisibility: (id) => api.patch(`/Faq/${id}/visibility`),

    // --- NEWS / AKTUALNOŚCI (SCALONE I POPRAWIONE) ---
    getNewsList: (searchQuery, page, pageSize, opts) =>
        api.get("/news", { params: { searchQuery, page, pageSize, adminView: opts && opts.adminView ? true : undefined } }),
    getNewsById: (id) => api.get(`/news/${id}`),
    getNewsBySlug: (slug) => api.get(`/news/slug/${slug}`),
    checkNewsSlug: (slug, excludeId) => api.get(`/news/check-slug`, { params: { slug, excludeId } }),
    createNews: (formData, onUploadProgress) => api.post("/news", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
    }),
    updateNews: (id, formData, onUploadProgress) => api.put(`/news/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
    }),
    deleteNews: (id) => api.delete(`/news/${id}`),
    toggleNewsVisibility: (id) => api.patch(`/news/${id}/visibility`),
    getNewsComments: (id) => api.get(`/news/${id}/comments`),
    addNewsComment: (id, data) => api.post(`/news/${id}/comments`, data),
    deleteNewsComment: (commentId) => api.delete(`/news/comments/${commentId}`),

    // --- MODERACJA KOMENTARZY (punkt R briefu 2026-08-31) ---
    // Backend istniał od 2026-08-29 i leżał nieużywany: front miał wyłącznie DELETE, który ukrywał
    // komentarz bez powodu i bez możliwości cofnięcia. Poniżej pełen komplet ścieżek moderacyjnych.
    hideNewsComment: (commentId, reason) => api.post(`/news/comments/${commentId}/hide`, { reason: reason || null }),
    unhideNewsComment: (commentId) => api.post(`/news/comments/${commentId}/unhide`),
    // ⚠️ NIEODWRACALNE — wiersz i całe poddrzewo odpowiedzi znikają z bazy (zostaje wpis w audycie).
    purgeNewsComment: (commentId) => api.delete(`/news/comments/${commentId}/permanent`),
    getHiddenNewsComments: (newsId, take = 20, skip = 0) =>
        api.get("/news/comments/hidden", { params: { newsId: newsId ?? undefined, take, skip } }),

    // Wyciszenia (shadow-ban) — sankcja NA OSOBIE, osobny kontroler i osobne uprawnienie.
    getCommentMutes: () => api.get("/comment-moderation/mutes"),
    getUserCommentMutes: (userId) => api.get(`/comment-moderation/mutes/${userId}`),
    muteCommentUser: (payload) => api.post("/comment-moderation/mutes", payload),
    unmuteCommentUser: (userId) => api.delete(`/comment-moderation/mutes/${userId}`),
    getCommentMuteDurations: () => api.get("/comment-moderation/durations"),

    getNewsCategories: () => api.get("/news/categories"),
    createNewsCategory: (data) => api.post("/news/categories", data),
    updateNewsCategory: (id, data) => api.put(`/news/categories/${id}`, data),
    deleteNewsCategory: (id) => api.delete(`/news/categories/${id}`),

    // --- DEPARTMENTS ---
    getDepartments: (searchQuery = "", pageNumber = 1, pageSize = 10) =>
        api.get("/departments", { params: { searchQuery, pageNumber, pageSize } }),
    getDepartmentsGroupped: (searchQuery = "", pageNumber = 1, pageSize = 10) =>
        api.get("/departments/groupped", { params: { searchQuery, pageNumber, pageSize } }),
    // [N-10, polecenie właściciela 2026-09-08] LISTA MPK — słownik kodów MPK działów i pracowników.
    // Odczyt otwarty dla zalogowanych (podpowiedzi w edytorach); zapis za PERM:AdminMpkManage.
    getMpkList: () => api.get("/admin/mpk"),
    getMpkCandidates: (q = "") => api.get("/admin/mpk/kandydaci", { params: { q } }),
    createMpk: (dto) => api.post("/admin/mpk", dto),
    updateMpk: (id, dto) => api.put(`/admin/mpk/${id}`, dto),
    /* [Polecenie właściciela 2026-09-20] „Umożliwiaj mi usuwanie MPK, ale zapisuj w historii status”.
       Kod UŻYWANY da się usunąć wyłącznie z `potwierdzenie` równym przepisanemu kodowi; bez niego serwer
       odmawia 409 i zwraca komplet liczb (ile kont, kart SIM, numerów, działów) — te same, po których
       idzie czyszczenie. `getMpkDeleteImpact` pokazuje je PRZED otwarciem okna potwierdzenia. */
    getMpkDeleteImpact: (id) => api.get(`/admin/mpk/${id}/skutki-usuniecia`),
    deleteMpk: (id, potwierdzenie = null) =>
        api.delete(`/admin/mpk/${id}`, potwierdzenie ? { params: { potwierdzenie } } : undefined),
    /* [Polecenie właściciela 2026-09-20] „opcja przełączania widoku, bo jest kilka MPK do 1 pracownika”:
       wiersz na OSOBĘ z wszystkimi jej kodami i źródłem każdego (konto / karta SIM / numer stacjonarny). */
    getMpkPoPracownikach: () => api.get("/admin/mpk/po-pracownikach"),
    /* [Polecenie właściciela 2026-09-20] „cała historia na userze dostępna też w eteczka” — JEDEN endpoint
       dla karty pracownika i dla e-teczki. Dwa endpointy dałyby dwie prawdy o tej samej osobie. */
    getMpkHistoriaOsoby: (userId) => api.get(`/admin/mpk/historia/osoba/${userId}`),
    syncMpk: () => api.post("/admin/mpk/sync"),
    assignMpk: (id, dto) => api.post(`/admin/mpk/${id}/przypisz`, dto),
    // [Polecenie właściciela 2026-09-15] Rozdział MPK działu (zbiorczy) i MPK pracownika (własny):
    // „uzycie” = kto/co ma ten kod (szuflada spod klikalnych liczb), „skutki” = ilu osób dotknęłoby
    // jawne nadpisanie kodów własnych. Oba na kluczu zapisu — niosą nazwiska.
    getMpkUsage: (kod) => api.get("/admin/mpk/uzycie", { params: { kod } }),
    getMpkAssignImpact: (id, dto) => api.post(`/admin/mpk/${id}/skutki`, dto),
    /* [polecenie właściciela 2026-09-15] „skoro mamy te numery MPK, to od razu możesz je zmapować z użytkownikiem”:
       PROPOZYCJE uzupełnienia MPK pracowników z zestawień telefonów (do przejrzenia) i zapis ZAZNACZONYCH. */
    getMpkPropozycjeZTelefonow: () => api.get("/admin/mpk/propozycje-z-telefonow"),
    saveMpkPropozycjeZTelefonow: (userIds) => api.post("/admin/mpk/propozycje-z-telefonow", { userIds }),
    // [R30, 2026-09-13] Eksport / import XLSX z mapowaniem — ten sam klucz co zapis (PERM:AdminMpkManage).
    exportMpk: () => api.get("/admin/mpk/eksport", { responseType: "blob" }),
    readMpkImport: (formData) =>
        api.post("/admin/mpk/import/wczytaj", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    checkMpkImport: (dto) => api.post("/admin/mpk/import/sprawdz", dto),
    applyMpkImport: (dto) => api.post("/admin/mpk/import/zastosuj", dto),
    // [2026-09-24] Zestawienie „Zatrudnienie wg MPK” (enova365): numer pracownika (p + 6 cyfr), kod MPK, forma zatrudnienia.
    readMpkZatrudnienie: (formData) =>
        api.post("/admin/mpk/import/zatrudnienie/wczytaj", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    planMpkZatrudnienie: (dto) => api.post("/admin/mpk/import/zatrudnienie/plan", dto),
    applyMpkZatrudnienie: (dto) => api.post("/admin/mpk/import/zatrudnienie/zastosuj", dto),
    getAllDepartmentsSimple: () => api.get("/departments/all"),
    getDepartmentNames: () => api.get("/departments/names"),
    getDepartmentById: (id) => api.get(`/departments/${id}`),
    getDepartment: (id) => api.get(`/departments/${id}`),
    getDepartmentDrives: (deptId) => api.get(`/departments/${deptId}/drives`),
    createDepartment: (data) => api.post("/departments", data),
    updateDepartment: (id, data) => api.put(`/departments/${id}`, data),
    deleteDepartment: (id) => api.delete(`/departments/${id}`),

    getPermissionDefinitions: () =>
        api.get("/departments/permissions/definitions"),

    // ── Uprawnienia i dostęp (panel /admin/access-control) ────────────────
    // Katalog uprawnień z metadanymi: moduł, rodzaj, poziom RO/RW, sposób egzekwowania, opis.
    getPermissionCatalog: () => api.get("/admin/access-control/catalog"),
    // REALNE liczniki posiadaczy każdego klucza + rozbicie źródła (indywidualne / dział / ranga).
    getPermissionMatrix: () => api.get("/admin/access-control/matrix"),
    // Efektywne uprawnienia jednego użytkownika ze wskazaniem, co pochodzi z działu.
    getUserEffectivePermissions: (userId) =>
        api.get(`/admin/access-control/users/${userId}/effective`),
    // Uprawnienia nadane działom (+ liczba objętych pracowników).
    getDepartmentPermissionGrants: () => api.get("/admin/access-control/departments"),
    setDepartmentPermissionGrants: (departmentId, data) =>
        api.put(`/admin/access-control/departments/${departmentId}`, data),

    // ── Imienny dostęp do modułu zgłoszeń / kategorii (konfigurator) ──────
    getTicketModuleAccess: (moduleId) => api.get(`/ticket-modules/${moduleId}/access`),
    setTicketModuleAccess: (moduleId, userId, data) =>
        api.put(`/ticket-modules/${moduleId}/access/${userId}`, data),

    // ── Kafelki administracyjne landingów modułów (per user) ──────────────
    // Panel: dynamiczna lista modułów + nadania imienne; landing: mój zestaw kafelków.
    getModuleTilesMatrix: () => api.get("/admin/access-control/module-tiles"),
    setModuleTileGrants: (pageCode, userId, tileCodes) =>
        api.put(`/admin/access-control/module-tiles/${pageCode}/users/${userId}`, { tileCodes }),
    getMyModuleTiles: (pageCode) => api.get("/admin-menu-visibility/my", { params: { pageCode } }),

    // ── Masowe kopiowanie uprawnień (jedna operacja serwerowa zamiast N PUT-ów) ──
    bulkCopyPermissions: (data) => api.post("/admin/users/bulk-permissions", data),

    // ── Dostęp DZIAŁU do modułów zgłoszeń (wejście / tworzenie) ───────────
    getDepartmentModules: (departmentId) =>
        api.get(`/admin/access-control/departments/${departmentId}/modules`),
    setDepartmentModuleAccess: (departmentId, moduleId, data) =>
        api.put(`/admin/access-control/departments/${departmentId}/modules/${moduleId}`, data),
    // Ta sama konfiguracja od strony MODUŁU (zakładka „Moduły obsługi") — obejmuje też
    // moduł wbudowany „hrManagement", gdzie nośnikiem jest uprawnienie działowe HrAccess.
    getSupportModuleDepartments: (pageCode) =>
        api.get(`/admin/access-control/support-modules/${encodeURIComponent(pageCode)}/departments`),
    setSupportModuleDepartment: (pageCode, departmentId, data) =>
        api.put(`/admin/access-control/support-modules/${encodeURIComponent(pageCode)}/departments/${departmentId}`, data),
    // [d) brief 2026-09-03] Wyszukiwarka działów do obsady modułu — serwer oddaje maks. 25 dopasowań,
    // nigdy całej struktury (panel dostawał wcześniej komplet działów firmy i filtrował go w przeglądarce).
    searchSupportModuleDepartments: (pageCode, q) =>
        api.get(`/admin/access-control/support-modules/${encodeURIComponent(pageCode)}/departments/search`,
            { params: { q } }),
    // [polecenie właściciela 2026-09-09] OSOBA UZUPEŁNIAJĄCA DZIAŁ w obsłudze modułu — para (dział, osoba) per moduł;
    // w zakresie tego modułu osoba liczy się jak członek działu (Wejście / Obsługa HR, tworzenie, domyślne kafelki),
    // bez zmiany jej przypisań w strukturze. Odjęcie = usunięcie pary.
    addSupportModuleDepartmentPerson: (pageCode, departmentId, userId) =>
        api.post(`/admin/access-control/support-modules/${encodeURIComponent(pageCode)}/departments/${departmentId}/people`, { userId }),
    removeSupportModuleDepartmentPerson: (pageCode, departmentId, userId) =>
        api.delete(`/admin/access-control/support-modules/${encodeURIComponent(pageCode)}/departments/${departmentId}/people/${userId}`),
    // Ograniczenia tworzenia zgłoszeń per moduł dla bieżącego użytkownika (kafelki portalu).
    getMyCreateRestrictions: () => api.get("/ticket-modules/my-create"),

    // `wszystkie` = jawny wyjątek dla PANELU ZARZĄDZANIA obsadą (/admin/departments): oddaje pełny stan
    // przypisań, także konta zablokowane i techniczne — bez tego nie da się ich odpiąć od działu.
    // Domyślnie (false) serwer odsiewa, bo ten sam endpoint zasila wybieracze osób (pkt 9 briefu 2026-09-01).
    getDepartmentUsers: (departmentId, pageNumber = 1, pageSize = 100, wszystkie = false) =>
        api.get(
            `/departments/${departmentId}/users?pageNumber=${pageNumber}&pageSize=${pageSize}${wszystkie ? "&wszystkie=true" : ""}`
        ),

    // [poz. 10d zgłoszenia 2026-09-04] Przełożony OBOWIĄZUJĄCY dla każdej osoby w dziale (plus kierownik
    // i jego zastępca, nawet spoza obsady) — liczony tą samą regułą, którą czytają akta osobowe i ścieżka
    // akceptacji wniosków. Panel działów pokazywał dotąd wyłącznie surowe pole „raportuje do", więc obok
    // kartoteki mówiącej co innego wyglądało to na sprzeczność aplikacji.
    getDepartmentPrzelozeni: (departmentId) => api.get(`/departments/${departmentId}/przelozeni`),

    // [A06-17] Usunięto getAssignmentDetails — wołało GET /departments/assignments/{id}, którego
    // serwer NIE MA (są tylko POST/PUT/DELETE). Żaden ekran z tego nie korzystał, ale pierwszy, który
    // by sięgnął, dostałby błąd „metoda niedozwolona" zamiast danych — czyli pustą kartę przypisania
    // bez żadnego wyjaśnienia. Po stronie serwera zniknęła bliźniacza, zawsze pusta metoda serwisu.

    assignUserToDepartment: (data) => api.post("/departments/assignments", data),
    updateUserDepartmentAssignment: (data) =>
        api.put("/departments/assignments", data),
    // [INTEGRACJA FALA 2 2026-09-05] JEDNE DRZWI do usuwania z działu.
    // BYŁO ŹLE: to samo DELETE /departments/assignments/{id} miało w tym pliku DWA imiona —
    // `removeUserFromDepartment` i `removeUserDepartmentAssignment`. Kartoteka (/admin/pracownicy)
    // i szuflada pracownika wołały drugie, panel dostępu, lista działów i /admin/users pierwsze.
    // SKUTEK DLA UŻYTKOWNIKA: gdyby ktoś dołożył do usuwania z działu kolejny krok (potwierdzenie,
    // wpis do dziennika, przebudowę przełożonego), poprawiłby jedno imię i połowa ekranów robiłaby
    // po staremu — usunięcie z kartoteki znaczyłoby co innego niż usunięcie z panelu dostępu.
    // NAPRAWA: został jeden `removeUserFromDepartment`; alias skasowany, oba wywołania przepięte.
    removeUserFromDepartment: (assignmentId) =>
        api.delete(`/departments/assignments/${assignmentId}`),
    reorderDepartmentUsers: (departmentId, items) =>
        api.put(`/departments/${departmentId}/users/reorder`, { items }),
    // Atomowe przeniesienie osoby między jednostkami (zachowuje primary, propaguje O365+AddressBook+SignalR).
    moveAssignment: (assignmentId, targetDepartmentId) =>
        api.put(`/departments/assignments/${assignmentId}/move`, { targetDepartmentId }),

    // --- ONBOARDING / REJESTRACJA (konfiguracja konta 1szego logowania) ---
    getRegistrationStatus: () => api.get("/registration/status"),
    getRegistrationPrefill: () => api.get("/registration/prefill"),
    submitRegistration: (data) => api.post("/registration/submit", data),
    getRegistrationConfig: () => api.get("/registration/config"),
    updateRegistrationConfig: (data) => api.put("/registration/config", data),
    /* [polecenie właściciela 2026-09-06] `scope` rozdziela DWA widoki tej samej strony:
       "mine" (/wnioski-rejestracji — tylko mój dział) i "all" (/admin/registration-requests — wszystko,
       wymaga klucza AdminRegistration; bez klucza serwer odmawia zamiast po cichu pokazać mniej). */
    getRegistrationRequests: (status, scope) => api.get("/registration/requests", { params: { status, scope } }),
    /* Czy mam co rozpatrywać i w jakiej roli — tą samą regułą, którą serwer liczy zakres. */
    getMyRegistrationReviewAccess: () => api.get("/registration/my-review-access"),
    approveRegistration: (id) => api.post(`/registration/requests/${id}/approve`),
    redirectRegistration: (id, data) => api.post(`/registration/requests/${id}/redirect`, data),
    resetRegistration: (id, data) => api.post(`/registration/requests/${id}/reset`, data),
    /* [polecenie właściciela 2026-09-21] Uzupełnienie danych za pracownika (dział, spółka, stanowisko,
       przełożony, numer, telefony) — OSOBNA czynność od zatwierdzenia, z bramką zatwierdzenia i śladem
       w dzienniku. Pole pominięte w `data` znaczy „nie zmieniaj”, nie „wyczyść”. */
    completeRegistrationRequest: (id, data) => api.post(`/registration/requests/${id}/complete`, data),

    // --- KANBAN ---
    getKanbanProjects: () => api.get("/Kanban/projects"),
    getKanbanProject: (id) => api.get(`/Kanban/projects/${id}`),
    createKanbanProject: (data) => api.post("/Kanban/projects", data),
    updateKanbanProject: (id, data) => api.put(`/Kanban/projects/${id}`, data),
    deleteKanbanProject: (id) => api.delete(`/Kanban/projects/${id}`),
    getKanbanAvailableDepartments: () => api.get("/Kanban/available-departments"),

    createKanbanColumn: (projectId, name) =>
        api.post(`/Kanban/projects/${projectId}/columns`, name),
    deleteKanbanColumn: (id) => api.delete(`/Kanban/columns/${id}`),
    updateKanbanColumn: (id, name) => api.put(`/Kanban/columns/${id}`, { name }),
    moveKanbanColumn: (data) => api.post("/Kanban/columns/move", data),

    createKanbanTask: (data) => api.post("/Kanban/tasks", data),
    updateKanbanTask: (id, data) => api.put(`/Kanban/tasks/${id}`, data),
    setKanbanTaskPriority: (id, priority) => api.patch(`/Kanban/tasks/${id}/priority/${priority}`),
    // [A10-20] Zmiana samego terminu karty. PUT /Kanban/tasks/{id} wymaga PEŁNEGO DTO (Title = [Required]),
    // więc wysłanie samego dueDate kończyło się 400 i przeciągnięcie karty w kalendarzu nic nie robiło.
    setKanbanTaskDueDate: (id, dueDate) => api.patch(`/Kanban/tasks/${id}/due-date`, { dueDate }),
    moveKanbanTask: (data) => api.post("/Kanban/tasks/move", data),
    deleteKanbanTask: (id) => api.delete(`/Kanban/tasks/${id}`),

    createKanbanSubitem: (data) => api.post("/Kanban/subitems", data),
    toggleKanbanSubitem: (id) => api.put(`/Kanban/subitems/${id}/toggle`),
    deleteKanbanSubitem: (id) => api.delete(`/Kanban/subitems/${id}`),
    getMyKanbanTasksWithDates: (params) => api.get("/Kanban/my-tasks-with-dates", { params }),

    // Kanban Trello features
    getKanbanTaskDetail: (id) => api.get(`/Kanban/tasks/${id}/detail`),
    addKanbanComment: (taskId, body) => api.post(`/Kanban/tasks/${taskId}/comments`, { body }),
    deleteKanbanComment: (id) => api.delete(`/Kanban/comments/${id}`),
    addKanbanAttachment: (taskId, file) => {
        const fd = new FormData(); fd.append("file", file);
        return api.post(`/Kanban/tasks/${taskId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    deleteKanbanAttachment: (id) => api.delete(`/Kanban/attachments/${id}`),
    getKanbanLabels: (projectId) => api.get(`/Kanban/projects/${projectId}/labels`),
    createKanbanLabel: (projectId, data) => api.post(`/Kanban/projects/${projectId}/labels`, data),
    updateKanbanLabel: (id, data) => api.put(`/Kanban/labels/${id}`, data),
    deleteKanbanLabel: (id) => api.delete(`/Kanban/labels/${id}`),
    addKanbanTaskLabel: (taskId, labelId) => api.post(`/Kanban/tasks/${taskId}/labels/${labelId}`),
    removeKanbanTaskLabel: (taskId, labelId) => api.delete(`/Kanban/tasks/${taskId}/labels/${labelId}`),
    getKanbanActivities: (taskId) => api.get(`/Kanban/tasks/${taskId}/activities`),

    // Kanban Board members
    getKanbanProjectMembers: (projectId) => api.get(`/Kanban/projects/${projectId}/members`),
    addKanbanProjectMember: (projectId, userId) => api.post(`/Kanban/projects/${projectId}/members`, { userId }),
    removeKanbanProjectMember: (projectId, userId) => api.delete(`/Kanban/projects/${projectId}/members/${userId}`),
    searchKanbanUsers: (query, departmentId) => api.get("/Kanban/users/search", { params: { query, departmentId } }),

    // Kanban Soft delete
    getKanbanDeletedProjects: () => api.get("/Kanban/projects/deleted"),
    restoreKanbanProject: (id) => api.post(`/Kanban/projects/${id}/restore`),
    deleteKanbanProjectPermanent: (id) => api.delete(`/Kanban/projects/${id}/permanent`),
    getKanbanBoardHistory: (id) => api.get(`/Kanban/projects/${id}/history`),
    getKanbanTaskLinks: (id) => api.get(`/Kanban/projects/${id}/links`),
    createKanbanTaskLink: (id, body) => api.post(`/Kanban/projects/${id}/links`, body),
    deleteKanbanTaskLink: (linkId) => api.delete(`/Kanban/links/${linkId}`),

    // --- SYGNALISTA ---
    logSygnalistaClick: () => api.post("/sygnalista/log-click"),
    /* [DWS-01-a] Dane pracodawcy dla strony Sygnalisty — pod prefiksem kanału, którego dziennik aktywności nie zapisuje
       (GET /api/company/my ze strony Sygnalisty był odciskiem wejścia w kanał zgłoszeń). */
    getSygnalistaFirma: () => api.get("/sygnalista/firma"),

    // --- GALERIA ---
    getGalleries: (page, pageSize, adminView) => api.get("/gallery", { params: { page, pageSize, adminView: adminView ? true : undefined } }),
    getGalleryById: (id) => api.get(`/gallery/${id}`),
    createGallery: (formData, onUploadProgress) => api.post("/gallery", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: onUploadProgress
    }),
    updateGallery: (id, formData, onUploadProgress) => api.put(`/gallery/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: onUploadProgress
    }),
    deleteGallery: (id) => api.delete(`/gallery/${id}`),
    deleteGalleryImage: (imageId) => api.delete(`/gallery/images/${imageId}`),
    toggleGalleryVisibility: (id) => api.patch(`/gallery/${id}/visibility`),
    // Pobranie wszystkich zdjęć galerii jako ZIP (tylko admin) — odpowiedź binarna.
    downloadGalleryAllZip: (id) => api.get(`/gallery/${id}/download-all`, { responseType: "blob" }),

    // --- GLOBALNE STATYSTYKI PUBLIKACJI (news/gallery/...) ---
    getPublicationStats: (type, id) => api.get(`/publication-stats/${type}/${id}`),
    registerPublicationView: (publicationType, publicationId, secondsOnPage) =>
        api.post("/publication-stats/view", { publicationType, publicationId, secondsOnPage }),
    registerPublicationCopy: (publicationType, publicationId) =>
        api.post("/publication-stats/copy", { publicationType, publicationId }),
    mutePublicationUser: (userId, publicationType, mute) =>
        api.post("/publication-stats/mute", { userId, publicationType, mute }),

    // --- STAN APLIKACJI (down-detector / SLA / logi) ---
    getSystemHealthStatus: () => api.get("/admin/system-health/status"),
    getSystemHealthSla: (hours) => api.get("/admin/system-health/sla", { params: { hours } }),
    getSystemHealthLogs: (level, search, take) =>
        api.get("/admin/system-health/logs", { params: { level, search, take } }),
    getSystemHealthCrashes: (take) => api.get("/admin/system-health/crashes", { params: { take } }),


    getContests: (page = 1, pageSize = 10, search = "") =>
        api.get(`/contests?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`),

    getContestById: (id) => api.get(`/contests/${id}`),

    createContest: (data, heroImage, regulationFile) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        if (heroImage) formData.append("heroImage", heroImage);
        if (regulationFile) formData.append("regulationFile", regulationFile);
        return api.post("/contests", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },

    updateContest: (id, data, heroImage, regulationFile) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        if (heroImage) formData.append("heroImage", heroImage);
        if (regulationFile) formData.append("regulationFile", regulationFile);
        return api.put(`/contests/${id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },

    deleteContest: (id) => api.delete(`/contests/${id}`),

    toggleContestVisibility: (id) => api.patch(`/contests/${id}/visibility`),

    getContestSubmissions: (contestId, page = 1, pageSize = 20, search = "") =>
        api.get(`/contests/${contestId}/submissions?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`),

    getContestSubmission: (submissionId) =>
        api.get(`/contests/submissions/${submissionId}`),

    setContestWinners: (contestId, dto) =>
        api.post(`/contests/${contestId}/winners`, dto),

    getContestWinnerLog: (contestId) =>
        api.get(`/contests/${contestId}/winner-log`),

    updateSubmissionNote: (submissionId, note) =>
        api.patch(`/contests/submissions/${submissionId}/note`, { note }),

    getContestStats: (contestId) =>
        api.get(`/contests/${contestId}/stats`),

    searchContestUsers: (query) =>
        api.get(`/contests/users/search?q=${encodeURIComponent(query)}`),


    submitCreativeContest: (contestId, textContent, files, onUploadProgress) => {
        const formData = new FormData();
        if (textContent) formData.append("textContent", textContent);
        if (files) {
            files.forEach(f => formData.append("files", f));
        }
        return api.post(`/contests/${contestId}/submit`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
                if (onUploadProgress && e.total) {
                    onUploadProgress(Math.round((e.loaded * 100) / e.total));
                }
            },
        });
    },

    submitQuizContest: (contestId, answers) =>
        api.post(`/contests/${contestId}/submit-quiz`, { answers }),

    getMyContestSubmission: (contestId) =>
        api.get(`/contests/${contestId}/my-submission`),

    getContestWinners: (contestId) =>
        api.get(`/contests/${contestId}/winners`),

    getContestResults: (contestId) =>
        api.get(`/contests/${contestId}/results`),

    // --- ANKIETY (SURVEYS) ---
    getSurveys: (page = 1, pageSize = 10, search = "") =>
        api.get(`/surveys?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`),
    getSurveyById: (id) => api.get(`/surveys/${id}`),
    createSurvey: (data) => api.post("/surveys", data),
    updateSurvey: (id, data) => api.put(`/surveys/${id}`, data),
    deleteSurvey: (id) => api.delete(`/surveys/${id}`),
    toggleSurveyVisibility: (id) => api.patch(`/surveys/${id}/visibility`),
    getSurveyResults: (id) => api.get(`/surveys/${id}/results`),
    exportSurveyResults: (id) => api.get(`/surveys/${id}/results/export`, { responseType: "blob" }),
    exportAllSurveys: (search = "") =>
        api.get(`/surveys/export?search=${encodeURIComponent(search)}`, { responseType: "blob" }),
    getMySurveys: () => api.get("/surveys/my"),
    getSurveyToFill: (id) => api.get(`/surveys/${id}/fill`),
    submitSurvey: (id, data) => api.post(`/surveys/${id}/submit`, data),
    hasSurveyResponded: (id) => api.get(`/surveys/${id}/has-responded`),

    // --- DOKUMENTY FIRMOWE ---
    getDocuments: (page, pageSize, category, search) =>
        api.get("/documents", { params: { page, pageSize, category, search } }),
    getDocumentById: (id) => api.get(`/documents/${id}`),
    createDocument: (data, files, onUploadProgress) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        if (files) files.forEach(f => formData.append("files", f));
        return api.post("/documents", formData, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress });
    },
    // PUT = tylko metadane (JSON). Pliki dodaje się jako nowe WERSJE.
    updateDocument: (id, data) => api.put(`/documents/${id}`, data),
    addDocumentVersion: (id, file, changeNote, onUploadProgress) => {
        const formData = new FormData();
        formData.append("file", file);
        if (changeNote) formData.append("changeNote", changeNote);
        return api.post(`/documents/${id}/versions`, formData, { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress });
    },
    // --- POLITYKI I PROCEDURY (E7 FUNDAMENT 2026-09-28) --- końcówki wspólne; pakiety dopisują własne POD tym blokiem
    // (Redakcja / Zatwierdzanie / Adresaci / Potwierdzenia / Przeglady), każdy pod własnym komentarzem-kotwicą.
    getPolitykiUstawienia: () => api.get("/polityki/ustawienia"),
    savePolitykiUstawienie: (klucz, wartosc) => api.put("/polityki/ustawienia", { klucz, wartosc }),
    getPolitykiObszary: (wszystkie = false) => api.get("/polityki/obszary", { params: { wszystkie } }),
    getPolitykiSlowniki: () => api.get("/polityki/slowniki"),
    // --- POLITYKI: pakiet ZATWIERDZANIE (E7 D23/D24/D27/D30; 2026-09-29) — obieg dokumentu (rundy i głosy), wysyłka rund,
    // „Do decyzji”, podgląd wersji z odciskiem, GŁOS (tylko POST), karta decyzji, głos w imieniu / decyzja z zewnątrz / inwentaryzacja
    // (multipart ze skanem), dowód głosu z katalogu zamkniętego. Publikacja ręczna = publishPolitykaWersja (REDAKCJA, jedna droga z blokadami).
    // PDF egzemplarza: getPolitykaWydaniePdf (rejestr wydań).
    getPolitykaObieg: (documentId) => api.get(`/polityki/${documentId}/obieg`),
    startPolitykaRunda: (documentId, dane) => api.post(`/polityki/${documentId}/rundy`, dane),
    startPolitykaWycofanie: (documentId, powod) => api.post(`/polityki/${documentId}/wycofanie`, { powod }),
    getPolitykaSkladZarzadu: (documentId) => api.get(`/polityki/${documentId}/sklad-zarzadu`),
    getPolitykiDoDecyzji: (wszystkie = false) => api.get("/polityki/do-decyzji", wszystkie ? { params: { wszystkie: true } } : undefined),
    getPolitykaRundaPodglad: (rundaId) => api.get(`/polityki/rundy/${rundaId}/podglad`),
    getPolitykaRundaPlik: (rundaId) => api.get(`/polityki/rundy/${rundaId}/podglad/plik`, { responseType: "blob" }),
    glosujPolitykaRunda: (rundaId, glos) => api.post(`/polityki/rundy/${rundaId}/glos`, glos),
    getPolitykaKartaDecyzji: (rundaId) => api.get(`/polityki/rundy/${rundaId}/karta`),
    glosWImieniuPolitykaRunda: (rundaId, formData) => api.post(`/polityki/rundy/${rundaId}/glos-w-imieniu`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
    rejestrujPolitykaDecyzjeZZewnatrz: (documentId, formData) => api.post(`/polityki/${documentId}/decyzje-z-zewnatrz`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
    inwentaryzujPolityke: (documentId, formData) => api.post(`/polityki/${documentId}/inwentaryzacja`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
    pobierzDowodGlosuPolityki: (glosId) => api.get(`/polityki/glosy/${glosId}/dowod`, { responseType: "blob" }),
    // --- POLITYKI: ADRESACI (E7 D20/D21, pakiet ADRESACI 2026-09-29) --- reguły niemutowalne (dodaj / wycofaj POST), podgląd
    //     „kogo obejmie”, uzgodnienie obowiązków (plan=true = tylko liczy), stan automatu, mapa wariantów i raport stanowisk.
    getPolitykaAdresaci: (documentId, zWycofanymi = false) => api.get(`/polityki/${documentId}/adresaci`, { params: { zWycofanymi } }),
    getPolitykaKogoObejmie: (documentId) => api.get(`/polityki/${documentId}/adresaci/kogo-obejmie`),
    previewPolitykaAdresat: (documentId, regula) => api.post(`/polityki/${documentId}/adresaci/podglad`, regula),
    addPolitykaAdresat: (documentId, regula) => api.post(`/polityki/${documentId}/adresaci`, regula),
    withdrawPolitykaAdresat: (documentId, adresatId, powod) => api.post(`/polityki/${documentId}/adresaci/${adresatId}/wycofaj`, { powod }),
    getPolitykiAdresaciSlownik: () => api.get("/polityki/adresaci/slownik"),
    uzgodnijPolitykaObowiazki: (documentId, plan = false) => api.post(`/polityki/${documentId}/obowiazki/uzgodnij`, null, { params: { plan } }),
    getPolitykiAutomat: () => api.get("/polityki/automat"),
    uruchomPolitykiAutomat: (plan = true) => api.post("/polityki/automat/uruchom", null, { params: { plan } }),
    getPolitykiRaportStanowisk: () => api.get("/polityki/stanowiska/raport"),
    getPolitykiWarianty: (zHistoria = false) => api.get("/polityki/stanowiska/warianty", { params: { zHistoria } }),
    addPolitykiWariant: (wariant) => api.post("/polityki/stanowiska/warianty", wariant),
    withdrawPolitykiWariant: (id, powod) => api.post(`/polityki/stanowiska/warianty/${id}/wycofaj`, { powod }),
    // [ADRESACI poprawka 3 2026-09-29] reguła imienna: wyszukiwarka zatrudnionych za PolitykiRedakcja (nie /itam/protocols/users-search)
    searchPolitykiAdresaciOsoby: (fraza, limit = 8) => api.get("/polityki/adresaci/osoby", { params: { fraza, limit } }),
    // --- POLITYKI: pakiet POTWIERDZENIA (E7 D22/D31, §3 kroki 9–11; 2026-09-29) — moje obowiązki (/do-potwierdzenia, kafel),
    // treść do potwierdzenia, POST potwierdzenia (nigdy PUT/DELETE), panel „otworzył / potwierdził / zaległe”, papier, storno, dowód.
    getMojeObowiazkiPolityk: () => api.get("/polityki/potwierdzenia/moje"),
    getPolitykaDoPotwierdzenia: (documentId) => api.get(`/polityki/potwierdzenia/dokument/${documentId}`),
    potwierdzPolityke: (documentId, dane) => api.post(`/polityki/potwierdzenia/dokument/${documentId}/potwierdz`, dane),
    getPanelPotwierdzenPolityki: (documentId) => api.get(`/polityki/potwierdzenia/dokument/${documentId}/panel`),
    // [poprawka 2 2026-09-29] ten sam panel dla posiadacza samego klucza rejestracji (papier, storno)
    getPanelRejestracjiPotwierdzen: (documentId) => api.get(`/polityki/potwierdzenia/dokument/${documentId}/rejestracja`),
    rejestrujPotwierdzeniePapierowe: (documentId, formData) => api.post(`/polityki/potwierdzenia/dokument/${documentId}/papier`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
    stornujPotwierdzeniePolityki: (potwierdzenieId, powod) => api.post(`/polityki/potwierdzenia/${potwierdzenieId}/storno`, { powod }),
    pobierzDowodPotwierdzenia: (potwierdzenieId) => api.get(`/polityki/potwierdzenia/${potwierdzenieId}/dowod`, { responseType: "blob" }),
    // [poprawka 3 2026-09-29] zaległości eskalowane do mnie (przełożony / właściciel) — cel dzwonka eskalacji (/do-potwierdzenia?widok=eskalacje)
    getEskalacjePolitykDoMnie: () => api.get("/polityki/potwierdzenia/eskalacje"),
    // --- POLITYKI: PRZEGLADY + CZYTELNIK + OBSZARY + EKSPORT (pakiet PRZEGLADY 2026-09-29, E7 D25/D29/§6/§9) ---
    getPolitykaPrzeglady: (documentId) => api.get(`/polityki/${documentId}/przeglady`),
    zaplanujPolitykaPrzeglad: (documentId, planowanyDo) => api.post(`/polityki/${documentId}/przeglady`, { planowanyDo }),
    zakonczPolitykaPrzeglad: (przegladId, dane) => api.post(`/polityki/przeglady/${przegladId}/zakoncz`, dane),
    zmienPolitykaCykl: (documentId, dane) => api.put(`/polityki/${documentId}/cykl`, dane),
    getPolitykaOs: (documentId) => api.get(`/polityki/${documentId}/os`),
    getPolitykaHistoria: (documentId) => api.get(`/polityki/${documentId}/historia`),
    getPolitykiObszaryStan: () => api.get("/polityki/obszary/stan"),
    createPolitykiObszar: (dane) => api.post("/polityki/obszary", dane),
    updatePolitykiObszar: (kod, dane) => api.put(`/polityki/obszary/${encodeURIComponent(kod)}`, dane),
    getPolitykiRejestrXlsx: () => api.get("/polityki/rejestr.xlsx", { responseType: "blob" }),
    getPolitykaDlaCzytelnika: (documentId) => api.get(`/polityki/czytelnik/${documentId}`),
    // [PRZEGLADY-poprawka 29.09, D22] wgląd czytelnika („otworzył” w panelu potwierdzeń) — tylko przy otwarciu treści
    zarejestrujOtwarciePolityki: (documentId) => api.post(`/polityki/czytelnik/${documentId}/otwarcie`),
    getPolitykaWydaniePdf: (wydanieId) => api.get(`/document-issues/${wydanieId}/pdf`, { responseType: "blob" }),
    // --- POLITYKI: pakiet REDAKCJA (E7 D18/D19/D29, §3 kroki 1–3; 2026-09-29) — rejestr, metryka, wersje robocze, treść
    // (EdytorTresci/PDF), „Zacznij od wzoru”, zamrożenie z odciskami, publikacja, plik wersji z katalogu zamkniętego (D28).
    getPolityki: (filtr = {}) => api.get("/polityki", { params: filtr }),
    // [REDAKCJA-poprawka 2] pelnaHistoria = cały dziennik (domyślnie 300 najnowszych + historiaLacznie).
    getPolityka: (id, pelnaHistoria = false) => (pelnaHistoria ? api.get(`/polityki/${id}`, { params: { pelnaHistoria: true } }) : api.get(`/polityki/${id}`)),
    createPolityka: (data) => api.post("/polityki", data),
    updatePolityka: (id, data) => api.put(`/polityki/${id}`, data),
    getPolitykaWersja: (id, versionId) => api.get(`/polityki/${id}/wersje/${versionId}`),
    // [REDAKCJA-poprawka 2] opcje: { zWersjiId (źródło, null = najnowsza), uniewaznijObieg (potwierdzenie D23) }.
    createPolitykaWersja: (id, opcje = {}) => api.post(`/polityki/${id}/wersje`, opcje),
    // [REDAKCJA-poprawka 2] odciskBazowy = odcisk ostatniej edycji znanej edytorowi ("" = bez edycji) — cudzy zapis w międzyczasie = 409.
    // [2026-09-30] `kreatorJson` — wzór z kreatora stron (bloki v2, jak wnioski HR): HTML składa serwer; null = zapis HTML z edytora.
    // [2026-09-30] `metryka` = ramka nagłówkowa konfiguratora ({title, kodDokumentu, podstawaPrawna}) zapisywana razem z treścią.
    savePolitykaTresc: (id, versionId, html, odciskBazowy = null, kreatorJson = null, metryka = null) => api.put(`/polityki/${id}/wersje/${versionId}/tresc`, { html, odciskBazowy, kreatorJson, metryka }),
    wydrukProbnyPolityki: (id, kreatorJson) => api.post(`/polityki/${id}/wydruk-probny`, { kreatorJson }, { responseType: "text" }),
    // [2026-09-30] „podgląd PDF jak druk” wzoru z biblioteki (kartki A4 z silnika wzorów pism, nic nie zapisuje).
    // [2026-09-30] Wydruk próbny KONFIGURATORA — dowolne bloki + ramka nagłówkowa, bez dokumentu w bazie (ten sam silnik co wzory pism HR).
    // [2026-09-30] Import istniejącej polityki (PDF/skan) przez OCR → bloki konfiguratora na wersji roboczej.
    importPolitykaOcr: (id, versionId, plik) => { const f = new FormData(); f.append("plik", plik); return api.post(`/polityki/${id}/wersje/${versionId}/import-ocr`, f, { headers: { "Content-Type": "multipart/form-data" } }); },
    // [2026-09-30] Które wejścia „Dokumenty” dotyczą zalogowanego (to nie są widoki dla każdego).
    getPolitykiMojeWejscia: () => api.get("/polityki/moje-wejscia"),
    // [2026-09-30] Ewidencja sprawdzenia procedury (odhaczone warunki) i autosprawdzanie widoczności dokumentu.
    savePolitykaEwidencja: (documentId, sprawdzone) => api.put(`/polityki/ewidencja/${documentId}`, { sprawdzone }),
    getPolitykaWidocznosc: (id) => api.get(`/polityki/${id}/widocznosc`),
    wydrukProbnyRoboczyPolityki: (dane) => api.post("/polityki/wydruk-probny", dane, { responseType: "text" }),
    wydrukProbnyWzoruPolityki: (kod) => api.post(`/polityki/biblioteka/${encodeURIComponent(kod)}/wydruk-probny`, {}, { responseType: "text" }),
    getPolitykaUstawienia: (id) => api.get(`/polityki/${id}/ustawienia`),
    savePolitykaUstawienie: (id, klucz, wartosc) => api.put(`/polityki/${id}/ustawienia`, { klucz, wartosc }),
    startPolitykaZWzoru: (id, versionId, wzorId) => api.post(`/polityki/${id}/wersje/${versionId}/z-wzoru`, { wzorId }),
    uploadPolitykaPlik: (id, versionId, plik) => {
        const formData = new FormData();
        formData.append("plik", plik);
        return api.post(`/polityki/${id}/wersje/${versionId}/plik`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    },
    freezePolitykaWersja: (id, versionId, stan = "do_zatwierdzenia") => api.post(`/polityki/${id}/wersje/${versionId}/zamroz`, { stan }),
    publishPolitykaWersja: (id, versionId) => api.post(`/polityki/${id}/wersje/${versionId}/opublikuj`),
    getPolitykaPlikWersji: (id, versionId) => api.get(`/polityki/${id}/wersje/${versionId}/plik`, { responseType: "blob" }),
    // [REDAKCJA-poprawka 2] documentId — wzory spółki dokumentu (+ wspólne); kadrowe tylko z HrAccess (serwer).
    getPolitykiWzory: (documentId = null) => api.get("/polityki/wzory", documentId ? { params: { documentId } } : undefined),
    // [2026-09-29] Mój Dział → Dokumenty → „Procedury i instrukcje” — zakres kierownika (serwer: IPolitykiDostep.KierowanieAsync).
    getPolitykiDzialu: (dzial = null) => api.get("/polityki/dzial", dzial ? { params: { dzial } } : undefined),
    getPolitykaDzialuOsoby: (documentId, dzial = null) => api.get(`/polityki/dzial/${documentId}/osoby`, dzial ? { params: { dzial } } : undefined),
    podgladPrzypisaniaPolitykiDzialu: (documentId, regula) => api.post(`/polityki/dzial/${documentId}/przypisania/podglad`, regula),
    przypiszPolitykeDzialowi: (documentId, regula) => api.post(`/polityki/dzial/${documentId}/przypisania`, regula),
    wycofajPrzypisaniePolitykiDzialu: (documentId, adresatId, powod) => api.post(`/polityki/dzial/${documentId}/przypisania/${adresatId}/wycofaj`, { powod }),
    // [2026-09-29] Biblioteka gotowych procedur (KSC / NIS2 / RODO / ISO), kreator z szkicem, karta procedury z kontrolami i dowodami, pokrycie spółek.
    getPolitykiBiblioteka: () => api.get("/polityki/biblioteka"),
    przyjmijWzorPolityki: (dane) => api.post("/polityki/biblioteka/przyjmij", dane),
    utworzPolitykeZKreatora: (dane) => api.post("/polityki/kreator", dane),
    getPolitykaKarta: (documentId) => api.get(`/polityki/${documentId}/karta`),
    dodajPolitykaKontrole: (documentId, dane) => api.post(`/polityki/${documentId}/kontrole`, dane),
    zmienPolitykaKontrole: (documentId, kontrolaId, dane) => api.put(`/polityki/${documentId}/kontrole/${kontrolaId}`, dane),
    wylaczPolitykaKontrole: (documentId, kontrolaId, powod) => api.post(`/polityki/${documentId}/kontrole/${kontrolaId}/wylacz`, { powod }),
    zapiszWykonanieKontroli: (documentId, kontrolaId, formData) => api.post(`/polityki/${documentId}/kontrole/${kontrolaId}/wykonania`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
    pobierzDowodKontroli: (documentId, dowodId) => api.get(`/polityki/${documentId}/dowody/${dowodId}`, { responseType: "blob" }),
    getPolitykiPokrycieSpolek: () => api.get("/polityki/spolki"),
    getPolitykiSzkice: () => api.get("/polityki/szkice"),
    getPolitykiSzkic: (id) => api.get(`/polityki/szkice/${id}`),
    createPolitykiSzkic: (dane) => api.post("/polityki/szkice", dane),
    savePolitykiSzkic: (id, dane) => api.put(`/polityki/szkice/${id}`, dane),
    deletePolitykiSzkic: (id) => api.post(`/polityki/szkice/${id}/usun`),
    deleteDocument: (id) => api.delete(`/documents/${id}`),
    toggleDocumentVisibility: (id) => api.patch(`/documents/${id}/visibility`),
    getDocumentCategories: () => api.get("/documents/categories"),
    downloadDocumentVersion: (versionId) =>
        api.get(`/documents/versions/${versionId}`, { responseType: "blob" }),


    // --- KOMUNIKATY ---
    getPendingAnnouncements: () => api.get("/announcements/pending"),
    // [BANER] Aktywne paski ogloszeniowe u gory aplikacji (okno czasowe, per user).
    getTopBarAnnouncements: () => api.get("/announcements/topbar"),
    getMyHistoryAnnouncements: () => api.get("/announcements/history"),
    markAnnouncementRead: (id) => api.post(`/announcements/${id}/read`),

    // Admin
    getAnnouncements: (page) => api.get("/announcements", { params: { page } }),
    getAnnouncementDetails: (id) => api.get(`/announcements/${id}`),
    createAnnouncement: (data, image) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        if (image) formData.append("image", image);
        return api.post("/announcements", formData);
    },
    updateAnnouncement: (id, data, image) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        if (image) formData.append("image", image);
        return api.put(`/announcements/${id}`, formData);
    },
    deleteAnnouncement: (id) => api.delete(`/announcements/${id}`),
    // [2026-10-04] „Zakończ teraz” — pasek przestaje się wyświetlać, komunikat i statystyki zostają.
    zakonczPasekKomunikatu: (id) => api.post(`/announcements/${id}/zakoncz-pasek`),
    // [S15 2026-09-14] searchAnnouncementUsers USUNIĘTE razem z GET /announcements/users/search — edytor komunikatu
    // wskazuje osoby wspólnym wyborem odbiorców (searchPeople). Nowe metody tego obszaru: src/api/modules/komunikaty.js.

    // --- KONTAKT - ADMIN ---
    createContactTopic: (data) => api.post("/contact/topics", data),
    updateContactTopic: (id, data) => api.put(`/contact/topics/${id}`, data),
    deleteContactTopic: (id) => api.delete(`/contact/topics/${id}`),

    createContactSubcategory: (data) => api.post("/contact/subcategories", data),
    updateContactSubcategory: (id, data) => api.put(`/contact/subcategories/${id}`, data),
    deleteContactSubcategory: (id) => api.delete(`/contact/subcategories/${id}`),

    getUsersForAdminSelect: () => api.get("/contact/users-simple"),

    // --- KONTAKT (PUBLIC) ---
    // [FORMULARZ 2026-07-29] includeInactive = panel admina (widzi też wyłączone tematy).
    getContactTopics: (params) => api.get("/contact/topics", { params }),
    getContactRecipients: (search) => api.get("/contact/recipients", { params: { search } }),
    sendContactForm: (formData) => api.post("/contact/send", formData, {
        headers: { "Content-Type": "multipart/form-data" }
    }),

    // --- COMPANY (FIRMY) ---
    getCompanies: () => api.get("/company"),
    // Dane firmy przypisanej do zalogowanego użytkownika — źródło prawdy dla nazwy firmy
    // i danych rejestrowych na podstronach. Konfiguracja: /admin/integrations →
    // „Skrzynki i tożsamość". NIE hardkodować nazw firm w komponentach.
    getMyCompany: () => api.get("/company/my"),
    // Zapis języka interfejsu NA KONCIE — aplikacja wstaje w nim na każdym urządzeniu.
    setMyLanguage: (language) => api.put("/users/me/language", { language }),
    createCompany: (data) => api.post("/company", data),
    updateCompany: (id, data) => api.put(`/company/${id}`, data),
    // [S-3, polecenie wlasciciela 2026-09-09] Kandydaci na reprezentanta spolki (Zarzad z tytulem wg KRS; z `q` — wyszukiwarka).
    getCompanyRepresentativeCandidates: (q = "", companyId = null) =>
        api.get("/company/representative-candidates", { params: { q: q || undefined, companyId: companyId ?? undefined } }),
    deleteCompany: (id) => api.delete(`/company/${id}`),
    // [O-8] Biura spółki — słownik miast BIURA („Mój profil" → miasto biura, książka adresowa). PUT = cała lista naraz.
    getCompanyOffices: (id) => api.get(`/company/${id}/offices`),
    saveCompanyOffices: (id, biura) => api.put(`/company/${id}/offices`, biura),

    // --- USER PROFILE (Bio) ---
    getMyProfile: () => api.get("/profile"),
    // [O-8] Słownik biur do wyboru miasta biura w „Mój profil" (biura własnej spółki najpierw, `mine`).
    getProfileOffices: () => api.get("/profile/offices"),
    getUserProfile: (userId) => api.get(`/profile/${userId}`),
    updateMyProfile: (data) => api.put("/profile", data),
    uploadMyAvatar: (image) => api.post("/profile/avatar", { image }),
    // Awatary WIELU osob naraz (mapa userId -> data-URL) — dla list; patrz components/common/UserAvatar.
    getUserAvatarsBatch: (userIds) => api.post("/profile/avatars/batch", userIds),
    deleteMyAvatar: () => api.delete("/profile/avatar"),

    getJuryContests: () =>
        api.get("/contests/jury/my-contests"),

    getJurySubmissions: (contestId) =>
        api.get(`/contests/${contestId}/jury/submissions`),

    submitJuryVote: (contestId, dto) =>
        api.post(`/contests/${contestId}/jury/vote`, dto),

    getJuryProgress: (contestId) =>
        api.get(`/contests/${contestId}/jury/progress`),

    getAdminScoreMatrix: (contestId) =>
        api.get(`/contests/${contestId}/admin/scores`),






    // --- TRANSLATION LANGUAGES ---
    getTranslationLanguages: () => api.get("/languages"),
    getTranslationLanguagesAll: () => api.get("/languages/all"),
    createTranslationLanguage: (data) => api.post("/languages", data),
    updateTranslationLanguage: (id, data) => api.put(`/languages/${id}`, data),
    deleteTranslationLanguage: (id) => api.delete(`/languages/${id}`),

    // ─── NOTIFICATIONS ───
    getNotifications: (page = 1, pageSize = 20) => api.get(`/notifications?page=${page}&pageSize=${pageSize}`),
    getUnreadCount: () => api.get("/notifications/unread-count"),
    // [OBSZAR 20, 2026-09-18] Strona NIEPRZECZYTANYCH + ich łączna liczba z JEDNEGO zapytania ({ items, unreadTotal }).
    // Dzwonek dociąga tym wszystko, co liczy licznik — także pozycje starsze niż 7 dni.
    getUnreadNotifications: (page = 1, pageSize = 20) => api.get(`/notifications/unread?page=${page}&pageSize=${pageSize}`),

    // ─── DASHBOARD (agregaty pulpitu) ───
    getDashboardActivity: (limit = 15) => api.get("/dashboard/activity", { params: { limit } }),
    getLoginHistory: (limit = 10) => api.get("/dashboard/login-history", { params: { limit } }),
    // Indywidualny układ pulpitu (personalizacja per user): odczyt / zapis / reset do domyślnego.
    getDashboardLayout: () => api.get("/dashboard/layout"),
    saveDashboardLayout: (layoutJson) => api.put("/dashboard/layout", { layoutJson }),
    resetDashboardLayout: () => api.delete("/dashboard/layout"),
    // Rozszerzony profil pracowniczy (typ pracy/tryb/stanowisko/firma/typ konta) + zużycie O365 (skrzynka/OneDrive).
    getMyWorkProfile: () => api.get("/users/me/work-profile"),
    getMyO365Storage: () => api.get("/users/me/o365-storage"),

    // ─── ANALITYKA CZASU PRACY (admin-only odczyt/sterowanie + ingest aktywności) ───
    // [S21] `companyId` — podział na spółkę (tenant per firma); brak = wszystkie konta.
    getWorkTimeOverview: (from, to, departmentId, companyId) =>
        api.get("/admin/worktime/overview", { params: { from, to, departmentId, companyId } }),
    getWorkTimeUsers: (from, to, search, onlyActive, departmentId, companyId) =>
        api.get("/admin/worktime/users", { params: { from, to, search, onlyActive, departmentId, companyId } }),
    getWorkTimeUserDetail: (userId, from, to) =>
        api.get(`/admin/worktime/users/${userId}`, { params: { from, to } }),
    getWorkTimeUserProfile: (userId) =>
        api.get(`/admin/worktime/users/${userId}/profile`),
    // Doczytywanie sekcji raportu porcjami ("pokaz wiecej"). Odpowiedz zbiorcza zwraca tylko pierwsza
    // strone kazdej listy — bez tego widok byl naraz ciezki (jeden wielki payload) i niepelny (twardy LIMIT).
    getWorkTimeUserSection: (userId, section, params) =>
        api.get(`/admin/worktime/users/${userId}/sections/${section}`, { params }),
    getWorkTimeUserWorkLog: (userId, from, to) =>
        api.get(`/admin/worktime/users/${userId}/worklog`, { params: { from, to } }),
    // [analityka1005] Office 365 z dziennika audytu — panel (od/do = dni polskie „yyyy-MM-dd”, włącznie).
    getWorkTimeO365Stan: (companyId) => api.get("/admin/worktime/o365/stan", { params: { companyId } }),
    getWorkTimeO365Przeglad: (od, doDnia, departmentId, companyId) =>
        api.get("/admin/worktime/o365/przeglad", { params: { od, do: doDnia, departmentId, companyId } }),
    getWorkTimeO365Osoby: (od, doDnia, departmentId, companyId, search) =>
        api.get("/admin/worktime/o365/osoby", { params: { od, do: doDnia, departmentId, companyId, search } }),
    getWorkTimeO365Osoba: (userId, od, doDnia) => api.get(`/admin/worktime/o365/osoby/${userId}`, { params: { od, do: doDnia } }),
    getWorkTimeSettings: () => api.get("/admin/worktime/settings"),
    setWorkTimeTracking: (enabled) => api.put("/admin/worktime/settings", { enabled }),
    setWorkTimeAreas: (areas) => api.put("/admin/worktime/areas", { areas }),

    // [A02-16][A18-17] Geolokalizacja logowań (adresy IP wychodzą do zewnętrznego dostawcy).
    // Domyślnie WYŁĄCZONA — panel administratora pokazuje, co oznacza włączenie.
    getLoginGeoConfig: () => api.get("/login-geo-config"),
    setLoginGeoEnabled: (enabled) => api.put("/login-geo-config", { enabled }),
    // [polecenie właściciela 2026-09-13] Wersja anonimowa na nagranie: lista osób z maską (admin),
    // pytanie „czy mam maskę" (każdy zalogowany, raz na sesję) i słownik do maskowania (tylko osoba z listy).
    // [OBSZAR 18, 2026-09-18] Podgląd konta („zaloguj jako" BEZ wchodzenia na konto) — tylko ranga administratora
    // ORAZ imienny klucz PodgladKontaAdmin (obie bramki po stronie serwera). Dane osobowe wracają już zamaskowane.
    /* [BC-13 2026-09-18 → BG-1 2026-09-19] JEDEN worek zawężeń zamiast dwóch osobnych argumentów.
       Dawny przełącznik „pokaż zablokowane" jest teraz wartością `stan` (czynne / wszystkie /
       zablokowane) — właściciel: filtry mają być JEDNYM mechanizmem, nie przełącznikiem obok listy.
       Pola puste/domyślne NIE idą do adresu: serwer i tak je przyjmie domyślnie, a krótki adres
       zostawia w dzienniku sieci czytelny ślad tego, co naprawdę zawęża. */
    getPodgladKontaLista: (filtry = {}) => {
        const { q, stan, dzial, bezDzialu, podDzialy, stanowisko, logowanie } = filtry;
        return api.get("/admin/podglad-konta/konta", {
            params: {
                ...(q ? { q } : {}),
                ...(stan && stan !== "czynne" ? { stan } : {}),
                ...(dzial ? { dzial } : {}),
                ...(bezDzialu ? { bezDzialu: true } : {}),
                ...(podDzialy === false ? { podDzialy: false } : {}),
                ...(stanowisko !== null && stanowisko !== undefined && stanowisko !== "" ? { stanowisko } : {}),
                ...(logowanie && logowanie !== "dowolne" ? { logowanie } : {}),
            },
        });
    },
    getPodgladKonta: (userId) => api.get(`/admin/podglad-konta/${userId}`),
    /* [BC-10/BC-11, PRZEBUDOWA 2026-09-19] NAKŁADKA „OGLĄDAM KONTO X".
       `startPodgladSesji` NIE podmienia już żadnego ciasteczka i nie wydaje tokenu — zakłada wiersz
       sesji i oddaje policzony WIDOK tamtej osoby (uprawnienia, moduły, zakładki, kolory), który front
       nakłada na powłokę. Administrator przez cały czas jest zalogowany na siebie; `zakonczPodgladSesji`
       tylko gasi nakładkę. `_retry: true` zostaje: te dwa żądania nie mają być ratowane odświeżaniem
       sesji, bo czytelna odpowiedź serwera jest tu ważniejsza niż ponowna próba. */
    /* [polecenie wlasciciela 2026-09-19] Przy wejsciu mowimy serwerowi, GDZIE jestesmy — zeby wyjscie
       moglo tu wrocic. Adres idzie na serwer, a nie do przegladarki, bo pamiec karty ginie przy
       odswiezeniu, przy wejsciu z drugiej karty i przy wygasnieciu nakladki po 30 minutach. */
    startPodgladSesji: (userId, sciezkaPowrotu = null) =>
        api.post(`/podglad-sesja/start/${userId}`,
            { sciezkaPowrotu: sciezkaPowrotu ?? (typeof window !== "undefined"
                ? window.location.pathname + window.location.search : null) },
            { _retry: true }),
    zakonczPodgladSesji: () => api.post("/podglad-sesja/zakoncz", {}, { _retry: true }),
    getPodgladSesjaStan: () => api.get("/podglad-sesja/stan"),
    getPodgladSesjaWidok: () => api.get("/podglad-sesja/widok"),
    getMojePodgladySesji: () => api.get("/podglad-sesja/moje"),
    getPodgladSesjaZdarzenia: (sesjaId) => api.get(`/podglad-sesja/${sesjaId}/zdarzenia`),
    getTrybNagraniaKonfiguracja: () => api.get("/nagranie/konfiguracja"),
    setTrybNagraniaKonfiguracja: (dane) => api.put("/nagranie/konfiguracja", dane),
    getMojTrybNagrania: () => api.get("/nagranie/moj-tryb"),
    getSlownikNagrania: () => api.get("/nagranie/slownik"),
    // [polecenie właściciela 2026-09-19] Ochrona treści — suwaki F12 / podgląd źródła / kopiowanie /
    // menu kontekstowe / znak wodny. `ustawienia` pyta każdy zalogowany (co nałożyć na swoją przeglądarkę),
    // `konfiguracja` to panel administratora.
    getOchronaTresci: () => api.get("/ochrona-tresci/ustawienia"),
    getOchronaTresciKonfiguracja: () => api.get("/ochrona-tresci/konfiguracja"),
    setOchronaTresciKonfiguracja: (dane) => api.put("/ochrona-tresci/konfiguracja", dane),
    syncWorkTimePresence: () => api.post("/admin/worktime/sync", {}),
    // [S8] `format` = "pdf" | "xlsx" (domyślnie xlsx); nazwa pliku wraca w Content-Disposition.
    exportWorkTimeReport: (from, to, search, departmentId, companyId, format = "xlsx") =>
        api.get("/admin/worktime/export", { params: { from, to, search, departmentId, companyId, format }, responseType: "blob" }),
    exportWorkTimeUser: (userId, from, to, format = "xlsx") =>
        api.get(`/admin/worktime/users/${userId}/export`, { params: { from, to, format }, responseType: "blob" }),
    // Ingest WŁASNEJ aktywności (każdy zalogowany) — backend bierze userId z tokenu.
    trackActivity: (page) => api.post("/worktime/ping", { page }),

    markNotificationAsRead: (id) => api.put(`/notifications/${id}/read`),
    markAllNotificationsAsRead: () => api.put("/notifications/read-all"),
    deleteNotification: (id) => api.delete(`/notifications/${id}`),
    // Preferencje powiadomień per user — user wybiera CO chce dostawać (per typ).
    getNotificationPreferences: () => api.get("/notifications/preferences"),
    updateNotificationPreference: (typeCode, enabled) =>
        api.put("/notifications/preferences", { typeCode, enabled }),
    // Kanały dostarczania per user — aplikacja / toast Windows / e-mail (trzy NIEZALEŻNE przełączniki).
    // E-mail ogólny ze skrzynki systemowej MPR; e-mail powiadomień ticketowych ze skrzynki działu zgłoszenia.
    getNotificationChannel: () => api.get("/notifications/channel"),
    updateNotificationChannel: (appEnabled, emailEnabled, windowsEnabled) =>
        api.put("/notifications/channel", { appEnabled, emailEnabled, windowsEnabled }),

    // ─── NOTIFICATION SETTINGS (Admin) ───
    getNotificationSettings: () => api.get("/notification-settings"),
    updateNotificationSetting: (settingId, data) => api.put(`/notification-settings/${settingId}`, data),
    sendTestNotification: (typeCode) => api.post(`/notification-settings/test/${typeCode}`),
    // [POWIADOMIENIA 2026-09-02] Skrzynki nadawcze powiadomień + stan ostatniej wysyłki (LastSend*) — do
    // pokazania przy każdym typie z kanałem e-mail w /admin/notifications.
    getNotificationSenders: () => api.get("/notification-settings/senders"),
    // Skrzynka systemowa powiadomień per firma
    getSystemMailboxes: () => api.get("/notification-settings/system-mailboxes"),
    setSystemMailbox: (companyId, configurationId) =>
        api.put("/notification-settings/system-mailbox", { companyId, configurationId }),
    /* [zlecenie wlasciciela 2026-09-11] KATALOG KANALOW DOSTARCZANIA per typ (aplikacja / e-mail / SMS)
       razem ze stanem SAMEJ BRAMKI SMS. Osobne wywolanie od /notification-settings, bo laczy dwa zrodla:
       szablony `SmsTemplates` (czy typ ma tresc SMS i czy jest wlaczona) oraz wiersz bramki w
       `IntegrationSettings` (czy jest login, haslo i nadawca).
       Pole `sms.dziala` liczy SERWER — front NIGDY nie wyprowadza go z `sms.wlaczony`. Pomiar z produkcji
       2026-09-11: 21 szablonow, wszystkie `IsEnabled = 0`, bramka `IsEnabled = 1` przy `ConfigJson = NULL`,
       wiec wlaczony szablon NIE WYSLALBY nic — a kropka „wlaczony" sugerowalaby, ze wysyla. */
    getKanalyPowiadomien: () => api.get("/admin/notifications/kanaly"),
    /* Wlaczenie szablonu przy NIESKONFIGUROWANEJ bramce jest dozwolone — odpowiedz wraca wtedy
       z `dziala: false` i polskim `powod`, ktory panel wypisuje przy typie. */
    ustawSmsTypu: (kod, wlaczony) =>
        api.post(`/admin/notifications/sms/${encodeURIComponent(kod)}`, { wlaczony }),

    // ─── INTEGRATION HUB ───
    getIntegrationStatus: () => api.get("/integration-hub/status"),
    getIntegrationSettings: () => api.get("/integration-hub/settings"),
    updateIntegrationSetting: (id, data) => api.put(`/integration-hub/settings/${id}`, data),
    // [S-2, polecenie właściciela 2026-09-09] Połączenia PER SPÓŁKA modułów „per firma” (Enova, Skalemex).
    getCompanyConnections: (moduleCode = null) =>
        api.get("/integration-hub/company-connections", { params: { moduleCode: moduleCode || undefined } }),
    saveCompanyConnection: (moduleCode, companyId, data) =>
        api.put(`/integration-hub/company-connections/${moduleCode}/${companyId}`, data),
    getDepartmentMailboxes: () => api.get("/integration-hub/department-mailboxes"),
    // [KONFIGURATOR 2026-07-28] Ręczne CRUD-y agentów desków usunięte z /admin/integrations
    // („tego nie używamy") — obsada modułów żyje w /admin/access-control → „Moduły obsługi".
    // Deski widoczne dla agenta (przełącznik desku w /zgloszenia).
    getTicketDesks: () => api.get("/tickets/desks"),
    // [G0 konfigurator modułów] Słownik modułów zgłoszeń (Purpose/Module/Category.Module).
    getTicketModules: (params) => api.get("/ticket-modules", { params }),
    createTicketModule: (data) => api.post("/ticket-modules", data),
    // [poz. 19] Kreator nowego desku — JEDNO żądanie zakłada moduł, wejście działu, obsadę,
    // skrzynkę, okno i kategorie (patrz TicketModulesController.CreateDeskWizard).
    createDeskWizard: (data) => api.post("/ticket-modules/desk-wizard", data),
    updateTicketModule: (id, data) => api.put(`/ticket-modules/${id}`, data),
    deleteTicketModule: (id) => api.delete(`/ticket-modules/${id}`),
    // [P13a, polecenie właściciela 2026-10-01] Synchronizację obsady z ekranu zdjęto (przycisk „Aktualizuj osoby
    // z dostępem” w /admin/categories) — POST sync-agents woła już tylko kreator desku po stronie serwera.
    // [G2] Moduły zgłoszeń bieżącego użytkownika — dynamiczne przyciski „Obsługa …".
    getMyTicketModules: () => api.get("/ticket-modules/my"),
    // [polecenie właściciela 2026-09-15] Progi ostrzeżeń przed terminem — globalne i per moduł
    // (AppSettings, nie kolumny modułu: próg ma też wariant globalny dla spraw spoza modułów).
    getAlertyTerminow: () => api.get("/ticket-modules/alerty-terminow"),
    updateAlertyTerminow: (data) => api.put("/ticket-modules/alerty-terminow", data),
    // „Uruchom teraz" — jeden cykl bez czekania na zegar (deduplikacja obowiązuje, więc nic się nie zdubluje).
    uruchomAlertyTerminow: () => api.post("/ticket-modules/alerty-terminow/uruchom-teraz"),
    // [G5] Decyzja akceptacyjna zapotrzebowania (zatwierdź/odrzuć — odpowiedź ze skrzynki desku).
    requisitionDecision: (id, data) => api.post(`/tickets/${id}/requisition-decision`, data),
    createDepartmentMailbox: (data) => api.post("/integration-hub/department-mailboxes", data),
    updateDepartmentMailbox: (id, data) => api.put(`/integration-hub/department-mailboxes/${id}`, data),
    deleteDepartmentMailbox: (id) => api.delete(`/integration-hub/department-mailboxes/${id}`),
    getSmsConfig: () => api.get("/integration-hub/sms/config"),
    updateSmsConfig: (data) => api.put("/integration-hub/sms/config", data),
    testSms: (phone) => api.post("/integration-hub/sms/test", { phone }),
    /* [polecenie wlasciciela 2026-09-17] LISTA kont bramki SMS + przypisanie spolek do konta
       („moge dodac 1 lub 2 per firma, albo z jednej bramki korzysta kilka firm”). Haslo NIE wraca
       z serwera — przychodzi tylko `passwordSet` i podpowiedz z czterech ostatnich znakow, a puste
       haslo w zapisie znaczy „nie zmieniam”. */
    getSmsAccounts: () => api.get("/integration-hub/sms/accounts"),
    createSmsAccount: (data) => api.post("/integration-hub/sms/accounts", data),
    updateSmsAccount: (id, data) => api.put(`/integration-hub/sms/accounts/${id}`, data),
    deleteSmsAccount: (id) => api.delete(`/integration-hub/sms/accounts/${id}`),
    setSmsAccountCompanies: (id, companyIds) =>
        api.put(`/integration-hub/sms/accounts/${id}/companies`, { companyIds }),
    // [S-1, polecenie wlasciciela 2026-09-09] Katalog komunikatow SMS (kiedy i co wysylamy) + dziennik wysylek.
    getSmsTemplates: () => api.get("/integration-hub/sms/templates"),
    updateSmsTemplate: (code, data) => api.put(`/integration-hub/sms/templates/${encodeURIComponent(code)}`, data),
    getSmsMessages: (take = 50) => api.get("/integration-hub/sms/messages", { params: { take } }),
    retrySmsMessage: (id) => api.post(`/integration-hub/sms/messages/${id}/retry`),

    // ─── TASKS (TODO) ───
    getMyTasks: (params) => api.get("/tasks", { params }),
    getTaskStats: (params) => api.get("/tasks/stats", { params }),
    // Zadania przypisane mi z innych modułów (Kanban, Projekty) — read-only.
    getExternalTasks: () => api.get("/tasks/external"),
    // [ZADANIA GRAFIKU 2026-08-31] Zadania przypięte do MOICH dni grafiku + ślady po skasowanych.
    // Czytane z UserTasks (nie z dnia grafiku), więc widać je także wtedy, gdy dzień jest szkicem
    // przełożonego albo jest zatwierdzony (jego komórka w kalendarzu jest wtedy zablokowana).
    getMyScheduleTasks: (params) => api.get("/tasks/schedule", { params }),
    createTask: (data) => api.post("/tasks", data),
    updateTask: (id, data) => api.put(`/tasks/${id}`, data),
    deleteTask: (id) => api.delete(`/tasks/${id}`),
    toggleTask: (id) => api.put(`/tasks/${id}/toggle`),
    getTaskTeamMembers: () => api.get("/tasks/team-members"),
    searchTaskUsers: (query) => api.get("/tasks/users/search", { params: { query } }),
    shareTask: (taskId, sharedWithUserId) => api.post(`/tasks/${taskId}/share`, { sharedWithUserId }),
    unshareTask: (taskId, sharedUserId) => api.delete(`/tasks/${taskId}/share/${sharedUserId}`),
    syncMicrosoftTodo: () => api.post("/tasks/sync-microsoft-todo"),

    // ─── MICROSOFT INTEGRATIONS ───
    getMicrosoftIntegrations: () => api.get("/integrations/microsoft"),
    getMicrosoftAuthUrl: (type, returnUrl) => api.get("/integrations/microsoft/authorize", { params: { type, returnUrl } }),
    getMicrosoftAutoConnect: (loginHint, returnUrl) => api.get("/integrations/microsoft/auto-connect", { params: { loginHint, returnUrl } }),
    disconnectMicrosoftIntegration: (type) => api.delete(`/integrations/microsoft/${type}`),
    // Pełne odłączenie konta Microsoft — czyści WSZYSTKIE typy i cache tokenów (w tym wspólny _all).
    disconnectAllMicrosoftIntegrations: () => api.delete("/integrations/microsoft"),
    // Twardy reset (self): odłącza wszystko + ustawia wymuszenie ponownej zgody; zwraca { msLogoutUrl } (wylogowanie z Office).
    resetMicrosoftConnection: () => api.post("/integrations/microsoft/reset"),
    // Wymuszenie resetu O365 konkretnego usera (admin) — przy najbliższym łączeniu wymagana ponowna zgoda.
    adminResetUserO365: (userId) => api.post(`/admin/users/${userId}/reset-o365`),

    // ── Branding / tożsamość aplikacji ──
    getBranding: () => api.get("/branding"),
    updateBranding: (dto) => api.put("/branding", dto),
    // [BRANDING PER FIRMA] domyslna paleta akcentu per spolka (panel /admin/branding)
    getBrandingCompanies: () => api.get("/branding/companies"),
    setCompanyAccent: (companyId, accent) => api.put(`/branding/companies/${companyId}/accent`, { accent }),
    uploadBrandingAsset: (kind, file) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.post(`/branding/asset/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    deleteBrandingAsset: (kind) => api.delete(`/branding/asset/${kind}`),
    // Diagnostyka połączenia Microsoft 365 (admin): czego brakuje, co działa.
    // [S-2, 2026-09-09] Diagnostyka PER SPÓŁKA (tenant spółki); bez companyId — najstarsza aktywna konfiguracja.
    getMicrosoftDiagnostics: (companyId = null) =>
        api.get("/integrations/microsoft/diagnostics", { params: { companyId: companyId ?? undefined } }),
    // [POCZTA 2026-09-01] Realna wysyłka testowa ze wskazanej skrzynki — odpowiedź zawiera powód
    // niepowodzenia prosto z Microsoft Graph. toEmail puste = adres zalogowanego administratora.
    sendMicrosoftTestMail: (mailboxId, toEmail) =>
        api.post("/integrations/microsoft/diagnostics/test-mail", { mailboxId, toEmail: toEmail || null }),
    // [POCZTA 2026-09-04] Tani stan wysyłki (bez pytania Microsoftu) — baner awarii poczty na stronie
    // głównej panelu. Pełna diagnostyka odpytuje Graph i trwa sekundy, więc do banera się nie nadaje.
    getMailHealth: () => api.get("/integrations/microsoft/diagnostics/mail-health"),
    // Zdrowie połączenia per-user: czy token ma wszystkie wymagane scope'y (brakujące = brak zgody admina).
    getMicrosoftTokenScopes: () => api.get("/integrations/microsoft/token-scopes"),
    // Konfiguracja O365 (ClientId, TenantId) z aktywnej konfiguracji.
    getO365Config: () => api.get("/users/o365-config"),

    // ─── CALENDAR (Outlook / Teams / Schedule) ───
    getOutlookEvents: (params) => api.get("/calendar/outlook-events", { params }),
    createOutlookEvent: (data) => api.post("/calendar/outlook-events", data),
    deleteOutlookEvent: (eventId) => api.delete(`/calendar/outlook-events/${eventId}`),
    updateOutlookEvent: (eventId, data) => api.patch(`/calendar/outlook-events/${eventId}`, data),
    getCalendarConnectionStatus: () => api.get("/calendar/connection-status"),
    getSharedCalendars: () => api.get("/calendar/shared-calendars"),
    // CRUD w kalendarzu WSPÓŁDZIELONYM (zespołowym) — zaproszenia do uczestników rozsyła Outlook.
    createSharedCalendarEvent: (calendarId, data) =>
        api.post(`/calendar/shared-calendars/${encodeURIComponent(calendarId)}/events`, data),
    updateSharedCalendarEvent: (calendarId, eventId, data) =>
        api.patch(`/calendar/shared-calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`, data),
    deleteSharedCalendarEvent: (calendarId, eventId) =>
        api.delete(`/calendar/shared-calendars/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`),
    getSharedCalendarEvents: (calId, params) => api.get(`/calendar/shared-calendars/${calId}/events`, { params }),
    // Udostępnianie MOJEGO kalendarza (Graph calendarPermissions — synchronizowane w O365)
    getCalendarPermissions: () => api.get("/calendar/permissions"),
    shareMyCalendar: (body) => api.post("/calendar/permissions", body),
    updateCalendarPermission: (permissionId, email, role) => api.patch(`/calendar/permissions/${permissionId}`, { email, role }),
    revokeCalendarPermission: (permissionId) => api.delete(`/calendar/permissions/${permissionId}`),
    getDepartmentTickets: (params) => api.get("/calendar/department-tickets", { params }),
    getMySchedule: (params) => api.get("/calendar/schedule", { params }),
    getTeamSchedule: (params) => api.get("/calendar/team-schedule", { params }),
    getUserSchedule: (userId, params) => api.get(`/calendar/schedule/${userId}`, { params }),
    // Godziny pracy z O365 (mailboxSettings.workingHours) — grafik + publikacja w zespole
    getMyWorkHours: () => api.get("/calendar/work-hours"),
    getUserWorkHours: (userId) => api.get(`/calendar/work-hours/${userId}`),
    getTeamWorkHours: () => api.get("/calendar/team-work-hours"),
    upsertSchedule: (data) => api.post("/calendar/schedule", data),
    bulkUpsertSchedule: (data) => api.post("/calendar/schedule/bulk", data),
    /* [polecenie właściciela 2026-09-06, pkt 4] Praca zdalna i poza biurem idą OSOBNĄ drogą:
       jedno żądanie składa wniosek o zgodę i dopiero po jego przyjęciu zapisuje dni. Zwykły
       `upsertSchedule` zostaje dla pracy w biurze, która niczyjej zgody nie wymaga. */
    createWorkLocationRequest: (data) => api.post("/calendar/work-location-request", data),
    // Grafik PODWŁADNEGO (przełożony/admin) — backend guard scope (Forbid poza zespołem). Jedno źródło WorkSchedules.
    upsertUserSchedule: (userId, data) => api.post(`/calendar/schedule/${userId}`, data),
    bulkUpsertUserSchedule: (userId, data) => api.post(`/calendar/schedule/${userId}/bulk`, data),
    deleteUserSchedule: (userId, date) => api.delete(`/calendar/schedule/${userId}`, { params: { date } }),
    // Zatwierdzanie (LOCK) grafiku podwładnego per zakres dni — po akceptacji propozycji na spotkaniu.
    approveUserSchedule: (userId, from, to) => api.post(`/calendar/schedule/${userId}/approve`, { from, to }),
    revertUserSchedule: (userId, from, to) => api.post(`/calendar/schedule/${userId}/revert`, { from, to }),
    // Wydruk PROPOZYCJI grafiku (PDF) — blob, żeby przeszedł interceptor JWT (nawigacja przeglądarki by go nie dołączyła).
    getScheduleProposalPdf: (params) => api.get("/calendar/schedule/proposal-pdf", { params, responseType: "blob" }),
    // WIDOK HR (org-wide, read-only) — grafiki całej firmy w zakresie dat. Gate HR po stronie backendu.
    getHrSchedule: (params) => api.get("/calendar/schedule/hr-view", { params }),
    // [GRAFIKI 2026-08-29] /obsluga-hr/grafiki: grafiki pogrupowane per DZIAŁ (HR = wszystkie działy,
    // przełożony z drzewa = własna gałąź; 403 dla pozostałych — bramka serwerowa, nie kluczowa).
    getDepartmentSchedules: (params) => api.get("/calendar/schedule/departments", { params }),
    // [PUNKT 2.1 2026-08-31] Ostrzeżenia urlopowe do siatki grafiku („Uwaga: X dni zaległego urlopu").
    // Terminy przedawnienia liczy WYŁĄCZNIE HrService (to samo źródło co e-teczka) — front tylko maluje.
    getScheduleLeaveWarnings: () => api.get("/calendar/schedule/leave-warnings"),
    // [PUNKT 2.4 2026-08-31] Mapka urlopowa MIESIĄCA (pozycje + gotowy słownik dzień→status), razem
    // z planami OCZEKUJĄCYMI, których /calendar/vacation-range-events nie zwraca.
    getVacationMonthMap: (year, month) => api.get("/calendar/vacation-ranges/month", { params: { year, month } }),
    // Pracownik SKŁADA własny grafik (draft→submitted) do przełożonego z drzewa działów.
    submitScheduleRange: (data) => api.post("/calendar/schedule/submit", data),
    // Przełożony ODSYŁA propozycję grafiku do poprawy (submitted→draft) z powodem.
    declineScheduleSubmission: (userId, data) => api.post(`/calendar/schedule/${userId}/decline-submission`, data),
    // Zadania daily w ramach grafiku (opcja zaawansowana) — przełożony przypisuje pracownikowi na dzień grafiku.
    getScheduleTasks: (userId, date) => api.get(`/calendar/schedule/${userId}/tasks`, { params: { date } }),
    addScheduleTask: (userId, data) => api.post(`/calendar/schedule/${userId}/tasks`, data),
    deleteScheduleTask: (taskId) => api.delete(`/calendar/schedule/tasks/${taskId}`),
    assignTaskFromCalendar: (data) => api.post("/calendar/assign-task", data),
    // Osoby do przydzielenia zadania Z KALENDARZA — zespół „w dół" ORAZ JA SAM (lista /tasks/team-members
    // wyklucza autora, więc nie dało się zaplanować zadania sobie).
    getCalendarAssignableUsers: () => api.get("/calendar/assignable-users"),
    getBirthdays: (params) => api.get("/calendar/birthdays", { params }),
    getVacationEvents: (year) => api.get("/calendar/vacation-events", { params: { year } }),
    getElearningDeadlines: () => api.get("/calendar/elearning-deadlines"),
    // Urlop — konkretne zakresy dat (realne daty + workflow zatwierdzania)
    getMyVacationRanges: (year) => api.get("/calendar/vacation-ranges", { params: { year } }),
    // Wspólna lista „moich wniosków urlopowych": wnioski HR (System A) + zakresy z grafiku (System B).
    getMyVacationRequests: (year) => api.get("/calendar/my-vacation-requests", { params: { year } }),
    getVacationRangeEvents: (year) => api.get("/calendar/vacation-range-events", { params: { year } }),
    createVacationRange: (body) => api.post("/calendar/vacation-ranges", body),
    // Przełożony planuje urlop pracownikowi z grafiku działu (od razu zatwierdzony + sync do Outlooka).
    planVacationForEmployee: (body) => api.post("/calendar/vacation-ranges/plan", body),
    deleteVacationRange: (id) => api.delete(`/calendar/vacation-ranges/${id}`),
    getPendingVacationRanges: () => api.get("/calendar/vacation-ranges/pending"),
    approveVacationRange: (id) => api.patch(`/calendar/vacation-ranges/${id}/approve`),
    rejectVacationRange: (id, comment) => api.patch(`/calendar/vacation-ranges/${id}/reject`, { comment }),

    // ─── SPECIAL CALENDARS ───
    getSpecialCalendars: () => api.get("/special-calendars"),
    getSpecialCalendarById: (id) => api.get(`/special-calendars/${id}`),
    createSpecialCalendar: (data) => api.post("/special-calendars", data),
    updateSpecialCalendar: (id, data) => api.put(`/special-calendars/${id}`, data),
    deleteSpecialCalendar: (id) => api.delete(`/special-calendars/${id}`),
    toggleSpecialCalendarVisibility: (id) => api.patch(`/special-calendars/${id}/visibility`),
    getMySpecialCalendars: () => api.get("/special-calendars/my"),
    subscribeSpecialCalendar: (id) => api.post(`/special-calendars/${id}/subscribe`),
    unsubscribeSpecialCalendar: (id) => api.delete(`/special-calendars/${id}/subscribe`),
    syncSpecialCalendarToO365: (id) => api.post(`/special-calendars/${id}/sync-o365`),
    getSpecialCalendarEvents: (id, params) => api.get(`/special-calendars/${id}/events`, { params }),
    createSpecialCalendarEvent: (id, data) => api.post(`/special-calendars/${id}/events`, data),
    updateSpecialCalendarEvent: (id, eventId, data) => api.put(`/special-calendars/${id}/events/${eventId}`, data),
    deleteSpecialCalendarEvent: (id, eventId) => api.delete(`/special-calendars/${id}/events/${eventId}`),

    // ─── SYSTEM MESSAGE TEMPLATES = PRZYPOMNIENIA AUTOMATYCZNE (Admin) [OBSZAR 16 2026-09-18] ───
    getSystemMessageTemplates: () => api.get("/admin/system-messages"),
    getSystemMessageTemplate: (id) => api.get(`/admin/system-messages/${id}`),
    updateSystemMessageTemplate: (id, data) => api.put(`/admin/system-messages/${id}`, data),
    /** Dziennik „co i do kogo poszło” jednego przypomnienia (najnowsze, domyślnie 100). */
    getSystemMessageDeliveries: (id, take = 100) => api.get(`/admin/system-messages/${id}/deliveries`, { params: { take } }),
    /** Wysyłka próbna przypomnienia do OSOBY ZALOGOWANEJ kanałami wzoru. */
    sendSystemMessageTest: (id) => api.post(`/admin/system-messages/${id}/test`),

    // ─── SHAREPOINT / MY FILES ───
    getSharePointConnectionStatus: () => api.get("/sharepoint/connection-status"),
    getOneDriveRoot: () => api.get("/sharepoint/onedrive"),
    getOneDriveDriveId: () => api.get("/sharepoint/onedrive/drive-id"),
    getOneDriveFolderByPath: (path) => api.get("/sharepoint/onedrive/folder", { params: { path } }),
    getDriveItemChildren: (driveId, itemId) => api.get(`/sharepoint/drives/${driveId}/items/${itemId}/children`),
    getSharedWithMe: () => api.get("/sharepoint/shared-with-me"),
    getSharedByMe: () => api.get("/sharepoint/shared-by-me"),
    getSharePointSites: () => api.get("/sharepoint/sites"),
    searchSharePointSites: (query) => api.get("/sharepoint/sites/search", { params: { query } }),
    getSiteLibraries: (siteId) => api.get(`/sharepoint/sites/${siteId}/libraries`),
    getLibraryItems: (driveId) => api.get(`/sharepoint/libraries/${driveId}/items`),
    // Pliki zespołu/grupy Teams (domyślna biblioteka dokumentów grupy) — zwraca { driveId, items }
    getGroupDrive: (groupId) => api.get(`/sharepoint/groups/${encodeURIComponent(groupId)}/drive`),
    uploadToOneDrive: (file, driveId, parentItemId) => {
        const formData = new FormData();
        formData.append("file", file);
        const params = {};
        if (driveId) params.driveId = driveId;
        if (parentItemId) params.parentItemId = parentItemId;
        return api.post("/sharepoint/onedrive/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
            params,
        });
    },
    moveOneDriveItem: (driveId, itemId, destinationFolderId) =>
        api.patch(`/sharepoint/drives/${driveId}/items/${itemId}/move`, { destinationFolderId }),
    createOneDriveFolder: (driveId, parentItemId, name) =>
        api.post(`/sharepoint/drives/${driveId}/items/${parentItemId}/folder`, { name }),
    createOneDriveFile: (driveId, parentItemId, name) =>
        api.post(`/sharepoint/drives/${driveId}/items/${parentItemId}/file`, { name }),
    renameDriveItem: (driveId, itemId, name) =>
        api.patch(`/sharepoint/drives/${driveId}/items/${itemId}/rename`, { name }),
    deleteDriveItem: (driveId, itemId) =>
        api.delete(`/sharepoint/drives/${driveId}/items/${itemId}`),
    createShareLink: (driveId, itemId, type = "view", scope = "organization") =>
        api.post(`/sharepoint/drives/${driveId}/items/${itemId}/share-link`, { type, scope }),
    getItemPermissions: (driveId, itemId) =>
        api.get(`/sharepoint/drives/${driveId}/items/${itemId}/permissions`),
    inviteToItem: (driveId, itemId, email, role = "read") =>
        api.post(`/sharepoint/drives/${driveId}/items/${itemId}/invite`, { email, role }),

    // ─── E-LEARNING ───
    getELearningCourses: (page = 1, pageSize = 20, search = "", status = null) =>
        api.get("/elearning", { params: { page, pageSize, search: search || undefined, status: status ?? undefined } }),
    getELearningCourseById: (id) => api.get(`/elearning/${id}`),
    createELearningCourse: (data, coverImage, lessonFilesMap) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        if (coverImage) formData.append("coverImage", coverImage);
        if (lessonFilesMap) {
            Object.entries(lessonFilesMap).forEach(([key, file]) => {
                formData.append(key, file);
            });
        }
        return api.post("/elearning", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
    updateELearningCourse: (id, data, coverImage, lessonFilesMap) => {
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        if (coverImage) formData.append("coverImage", coverImage);
        if (lessonFilesMap) {
            Object.entries(lessonFilesMap).forEach(([key, file]) => {
                formData.append(key, file);
            });
        }
        return api.put(`/elearning/${id}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
    deleteELearningCourse: (id) => api.delete(`/elearning/${id}`),
    toggleELearningVisibility: (id) => api.patch(`/elearning/${id}/visibility`),
    getELearningStats: () => api.get("/elearning/stats"),
    downloadELearningAttachment: (attachmentId) =>
        api.get(`/elearning/attachments/${attachmentId}`, { responseType: "blob" }),

    // E-Learning — podgląd materiału w lekcji.
    // Pobranie przez XHR (cookies lecą z withCredentials → brak problemu SameSite/iframe),
    // ?inline=true: omija blokadę pobierania dla nie-adminów + konwersję Office→PDF (Gotenberg).
    // Frontend renderuje z blob: URL — działa z istniejącym backendem, bez tokenów.
    getELearningAttachmentBlob: (attachmentId) =>
        api.get(`/elearning/attachments/${attachmentId}`, {
            params: { inline: true },
            responseType: "blob",
        }),
    // Surowy plik (np. PPTX) do renderu w przeglądarce — omija konwersję Gotenberg.
    // config: pozwala przekazać onDownloadProgress (pasek ładowania prezentacji).
    getELearningAttachmentRaw: (attachmentId, config = {}) =>
        api.get(`/elearning/attachments/${attachmentId}`, {
            params: { inline: true, raw: true },
            responseType: "arraybuffer",
            ...config,
        }),

    // E-Learning — Enrollment & Progress
    enrollELearningCourse: (courseId) => api.post(`/elearning/${courseId}/enroll`),
    getELearningProgress: (courseId) => api.get(`/elearning/${courseId}/progress`),
    updateLessonProgress: (courseId, lessonId, data) =>
        api.put(`/elearning/${courseId}/lessons/${lessonId}/progress`, data),
    // A07-11: meldunek startu podejścia — serwer zapamiętuje moment rozpoczęcia i zwraca czas pozostały
    // (limit czasu przestał być wyłącznie licznikiem w przeglądarce).
    startELearningQuiz: (courseId) => api.post(`/elearning/${courseId}/quiz/start`),
    submitELearningQuiz: (courseId, submission) =>
        api.post(`/elearning/${courseId}/quiz/submit`, submission),

    // E-Learning — Certificates & User Courses
    downloadELearningCertificate: (courseId) =>
        api.get(`/elearning/${courseId}/certificate`, { responseType: "blob" }),

    getELearningCertificateData: (courseId) =>
        api.get(`/elearning/${courseId}/certificate-data`),

    resetMyELearningCourse: (courseId) =>
        api.post(`/elearning/${courseId}/reset-my`),

    // [B-18] courseId zawęża zgłoszenia do wybranego szkolenia (zakładka „Test wiedzy").
    getELearningPendingGrading: (courseId) =>
        api.get(`/elearning/pending-grading`, { params: { courseId: courseId || undefined } }),

    gradeELearningOpenAnswers: (courseId, dto) =>
        api.post(`/elearning/${courseId}/grade`, dto),
    getMyELearningCourses: () => api.get("/elearning/my-courses"),

    // E-Learning Admin Training Panel
    // [B-18] Bez courseId — zbiorczo; z courseId — liczby jednego szkolenia (to samo źródło co tabela kursów).
    getAdminTrainingStats: (courseId) =>
        api.get("/elearning/admin/stats", { params: { courseId: courseId || undefined } }),
    getAdminCourseReports: () => api.get("/elearning/admin/reports"),
    // [O-15] extra: { mandatory, overdue, limit } — filtry „obowiązkowe" / „po terminie" i jawny limit wierszy.
    getAdminAllEnrollments: (search, status, courseId, department, position, extra = {}) =>
        api.get("/elearning/admin/enrollments", { params: { search, status, courseId, department, position, ...extra } }),
    getElearningEnrollmentFilterOptions: () =>
        api.get("/elearning/admin/enrollments/filter-options"),
    exportElearningEnrollments: (params) =>
        api.get("/elearning/admin/enrollments/export", { params, responseType: "blob" }),
    getAdminCourseEnrollments: (courseId) =>
        api.get(`/elearning/admin/${courseId}/enrollments`),
    getAdminQuizAnalytics: (courseId) =>
        api.get(`/elearning/admin/${courseId}/quiz-analytics`),
    resetUserEnrollment: (courseId, userId) =>
        api.post(`/elearning/admin/${courseId}/users/${userId}/reset`),

    // --- SYGNALISTA (Whistleblower) ---
    getSygnalistaReports: (params) => api.get("/sygnalista/reports", { params }),
    getSygnalistaReport: (reportId) => api.get(`/sygnalista/reports/${reportId}`),
    updateSygnalistaStatus: (reportId, data) => api.put(`/sygnalista/reports/${reportId}/status`, data),
    addSygnalistaMessage: (reportId, data) => api.post(`/sygnalista/reports/${reportId}/messages`, data),
    // upToMessageId = Id ostatniej wiadomości widocznej na ekranie (nie oznaczamy nowszych).
    markSygnalistaMessagesRead: (reportId, upToMessageId) =>
        api.post(`/sygnalista/reports/${reportId}/messages/read`, null, {
            params: upToMessageId ? { upToMessageId } : undefined,
        }),
    // Załączniki korespondencji — plik trafia do katalogu współdzielonego z aplikacją
    // sygnalisty, więc zgłaszający pobierze go swoim kodem śledzenia.
    uploadSygnalistaMessageAttachment: (reportId, messageId, file, onUploadProgress) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.post(`/sygnalista/reports/${reportId}/messages/${messageId}/attachments`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress,
        });
    },
    getSygnalistaMessageAttachment: (attachmentId) =>
        api.get(`/sygnalista/message-attachments/${attachmentId}`, { responseType: "blob" }),
    getSygnalistaAudit: (reportId) => api.get(`/sygnalista/reports/${reportId}/audit`),
    getSygnalistaCategories: () => api.get("/sygnalista/categories"),
    getSygnalistaStats: () => api.get("/sygnalista/stats"),
    /* [SYGNALISTA] Dział obsługujący — WYŁĄCZNIE OZNACZENIE ORGANIZACYJNE. Ten komentarz mówił
       wcześniej „członkowie dostają dostęp automatycznie
       po zalogowaniu" — TO JUŻ NIEPRAWDA i było groźne: dokładnie ta automatyka była luką
       (przesunięcie kadrowe z Office 365 otwierało komuś zgłoszenia naruszeń). Dostęp daje
       wyłącznie imienne nadanie klucza SygnalistaManage. [O-12, 2026-09-08] Dział NIE decyduje też
       o powiadomieniach: sygnał o nowej wiadomości od zgłaszającego (z numerem sprawy, także mailem)
       idzie wyłącznie do posiadaczy nieodebranego klucza — patrz SygnalistaNewMessageBackgroundService.
       Ekranem tego ustawienia jest /admin/access-control → „Moduły obsługi" → „Sygnalista",
       który chodzi po accessApi (polityka AdminAccessManage). Te dwa wywołania zostają jako
       wejście AdminOnly do TEGO SAMEGO klucza AppSettings — nie ma dwóch źródeł prawdy. */
    getSygnalistaHandlingDepartment: () => api.get("/sygnalista-config/handling-department"),
    setSygnalistaHandlingDepartment: (departmentId) =>
        api.put("/sygnalista-config/handling-department", { departmentId }),
    getSygnalistaAttachment: (attachmentId) => api.get(`/sygnalista/attachments/${attachmentId}`, { responseType: "blob" }),
    // [SPK-09] Z numerem sprawy lista „Przypisz” zawiera tylko osoby, które TĘ sprawę zobaczą (obsada jej spółki).
    getSygnalistaHrUsers: (reportId) =>
        api.get("/sygnalista/hr-users", { params: reportId ? { reportId } : undefined }),
    // [SPK-09, decyzja właściciela 2026-09-27] Obsada spółek — kto z obsługi prowadzi zgłoszenia której spółki
    // (koordynator compliance, klucz SygnalistaObsadaSpolek). Zapis = pełna lista osób jednej spółki.
    getSygnalistaObsadaSpolek: () => api.get("/sygnalista/obsada-spolek"),
    // Trzeci argument (skrzynka e-mail obsługi spółki) wysyłany TYLKO, gdy koordynator go zmienił: pominięty = serwer
    // zostawia skrzynkę bez zmian, pusty tekst = mail z treścią zgłoszeń wraca na wspólną skrzynkę kanału.
    zapiszSygnalistaObsadeSpolki: (companyId, userIds, ...skrzynka) =>
        api.put(`/sygnalista/obsada-spolek/${companyId}`,
            skrzynka.length > 0 ? { userIds, adresSkrzynki: skrzynka[0] } : { userIds }),

    // --- TERMS OF SERVICE / REGULAMIN ---
    // [2026-09-20] Publiczny dokument prawny (regulamin / rodo / cookies) — czytany też BEZ logowania
    // (stopka i strona logowania), więc bez interceptora wymuszającego sesję nic tu nie przeszkadza:
    // końcówka jest [AllowAnonymous], a token, jeśli jest, po prostu jedzie ciasteczkiem.
    getPublicznyDokument: (rodzaj) => api.get(`/terms/public/${encodeURIComponent(rodzaj)}`),

    // [PRZEBUDOWA ACCESS-CONTROL 2026-09-20] Nadania MODUŁOWE — klucze dziedziczone przez każdego
    // członka modułu obsługi (segment „Uprawnienia modułu" w zakładce Moduły obsługi).
    getSupportModulePermissions: (pageCode) =>
        api.get(`/admin/access-control/support-modules/${encodeURIComponent(pageCode)}/permissions`),
    setSupportModulePermissions: (pageCode, permissions) =>
        api.put(`/admin/access-control/support-modules/${encodeURIComponent(pageCode)}/permissions`, { permissions }),
    getPendingTerms: () => api.get("/terms/pending"),
    acceptTerms: (id) => api.post(`/terms/${id}/accept`),
    getTermsHistory: () => api.get("/terms/history"),
    // Admin
    getTermsVersions: () => api.get("/terms/admin"),
    getTermsVersionDetail: (id) => api.get(`/terms/admin/${id}`),
    publishNewTermsVersion: (data) => api.post("/terms/admin", data),
    getActiveTerms: () => api.get("/terms/admin/active"),
    /* [zlecenie właściciela 2026-09-08] POPRAWKA istniejącej wersji — tytuł i treść, bez nowej wersji
       i bez zerowania akceptacji. Serwer odpowiada 409 `wersja_zaakceptowana`, jeśli wersję ktoś już
       przyjął, a wywołanie nie niesie `potwierdzamMimoAkceptacji`. */
    updateTermsVersion: (id, data) => api.put(`/terms/admin/${id}`, data),
    // [polecenie właściciela 2026-09-14] ukrycie/przywrócenie wersji (odwracalne) i skasowanie (z akceptacjami).
    setTermsVersionHidden: (id, hidden) => api.put(`/terms/admin/${id}/widocznosc`, { hidden }),
    deleteTermsVersion: (id) => api.delete(`/terms/admin/${id}`),

    // --- ADMIN PIN ---
    verifyAdminPin: (pin) => api.post("/health/verify-admin-pin", { pin }),
    adminPinStatus: () => api.get("/health/admin-pin-status"),
    /* [PIN-ZARZAD, decyzja właściciela 2026-09-27] „pin zostaw tylko dla admina 5 cyfry to jest ok". Zmiana WŁASNEGO PIN-u
       na 5-cyfrowy — ekran PIN-u proponuje ją po podaniu starego, 4-cyfrowego (serwer: RequireAdminPinFilter + ranga). */
    // [PIN-ZARZAD poprawka 2026-09-28] Serwer wymaga obecnego PIN-u — samo ciasteczko otwartej sesji nie zmieni PIN-u.
    /* [PIN-ZARZAD poprawka 2 2026-09-28] `opcje.pinSystemowy` — w rozruchu wymień PIN SYSTEMOWY (ten, którym się weszło),
       zamiast zakładać pierwszy PIN indywidualny (to blokowało pozostałych administratorów). */
    zmienWlasnyPinAdministratora: (pin, obecnyPin, opcje) => api.put("/health/admin-pin",
        opcje?.pinSystemowy ? { pin, obecnyPin, pinSystemowy: true } : { pin, obecnyPin }),
    setAdminPin: (userId, pin) => api.put(`/admin/users/${userId}/admin-pin`, { pin }),
    clearAdminPin: (userId) => api.delete(`/admin/users/${userId}/admin-pin`),

    // --- MODULE VISIBILITY ---
    // [STOPKI] Builder wzorow stopek e-mail (panel admina).
    getSignatureFonts: () => api.get("/email-signatures/fonts"),
    getSignatureTemplates: () => api.get("/email-signatures/templates"),
    getSignatureTemplate: (id) => api.get(`/email-signatures/templates/${id}`),
    createSignatureTemplate: (payload) => api.post("/email-signatures/templates", payload),
    updateSignatureTemplate: (id, payload) => api.put(`/email-signatures/templates/${id}`, payload),
    deleteSignatureTemplate: (id) => api.delete(`/email-signatures/templates/${id}`),
    previewSignatureTemplate: (id, values) => api.post(`/email-signatures/templates/${id}/preview`, values ?? null),
    previewSignatureDraft: (payload, forEditor = false) =>
        api.post("/email-signatures/templates/preview-draft", payload, { params: { forEditor } }),
    getSignatureCompanyData: (companyId) => api.get(`/email-signatures/company-data/${companyId}`),
    getSignatureAssets: () => api.get("/email-signatures/assets"),
    saveSignatureAsset: (formData) => api.post("/email-signatures/assets", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    deleteSignatureAsset: (id) => api.delete(`/email-signatures/assets/${id}`),
    getSignatureUsers: (search, take = 100) => api.get("/email-signatures/users", { params: { search, take } }),
    previewSignatureForUser: (userId) => api.get(`/email-signatures/users/${userId}/preview`),
    saveSignatureForUser: (userId, payload, pushToO365 = true) =>
        api.put(`/email-signatures/users/${userId}`, payload, { params: { pushToO365 } }),
    getStoredSignature: (userId) => api.get(`/email-signatures/users/${userId}/stored`),
    // Log zmian stopek — od 2026-08-30 STRONICOWANY: domyślnie ostatnie 5, reszta na żądanie
    // („wczytaj więcej"). Odpowiedź: { items, hasMore, skip }.
    getSignatureAudit: (userId, take = 5, skip = 0) =>
        api.get("/email-signatures/audit", { params: { userId, take, skip } }),

    // [STOPKI] Widok pracownika — wylacznie wlasna stopka (userId bierze sie z tokenu).
    // Awatar rozmowcy Teams — blob, bo API uwierzytelnia naglowkiem JWT (zwykly <img> dostalby 401).
    getTeamsChatAvatar: (graphUserId) =>
        api.get(`/teams-chat/avatar/${encodeURIComponent(graphUserId)}`, { responseType: "blob" }),

    getMySignature: () => api.get("/my-signature"),
    /**
     * [N 2026-08-31] Pobranie MOICH danych wprost z Microsoft Graph → książka adresowa → konto,
     * a potem świeża stopka. Wołane przy KAŻDYM wejściu na /moja-stopka (bez `force`, z odstępem
     * po stronie serwera) oraz przyciskiem „Odśwież z Office 365" (force = pomija odstęp).
     * Odpowiedź: { refreshed, changed, skipped, message, refreshedAt, signature }.
     */
    refreshMySignatureFromO365: (force = false) =>
        api.post("/my-signature/refresh", null, { params: force ? { force: true } : {} }),
    /** [N] Wymuszone tłumaczenie stanowiska PL → EN ze słownika (przycisk przy polu „Stanowisko"). */
    translateMySignatureJobTitle: (jobTitle) => api.post("/my-signature/translate-title", { jobTitle }),
    saveMySignature: (payload) => api.put("/my-signature", payload),
    // [BRIEF 2026-09-01 poz. 19] Dane pochodzące z Office 365 poprawia IT — pracownik składa
    // zgłoszenie („jest → ma być"), a nie zapisuje lokalnego nadpisania nad katalogiem.
    requestMySignatureDataChange: (payload) => api.post("/my-signature/data-change-request", payload),
    // [STOPKI / WDROŻENIE] przeniesione z app/stopka
    getMySignatureCompanies: () => api.get("/my-signature/companies"),
    // english = stopka polsko-angielska (powitanie + stanowisko "PL (EN)"), jak w app/stopka.
    getMySignaturePreview: (companyId, english = false) =>
        api.get("/my-signature/preview", { params: { ...(companyId ? { companyId } : {}), ...(english ? { english: true } : {}) } }),
    getMySignatureInstallPackage: (companyId, english = false) =>
        api.get("/my-signature/install-package", {
            params: { ...(companyId ? { companyId } : {}), ...(english ? { english: true } : {}) },
            responseType: "blob",
        }),
    sendMySignatureToMe: (companyId) => api.post("/my-signature/send-to-me", { companyId: companyId ?? null }),
    trackMySignature: (action) => api.post("/my-signature/track", { action }),
    publishSignatureTemplate: (id) => api.post(`/email-signatures/templates/${id}/publish`),
    distributeSignatures: () => api.post("/email-signatures/distribute"),
    getSignatureCoverage: () => api.get("/email-signatures/coverage"),

    getModuleVisibility: () => api.get("/module-visibility"),
    updateModuleVisibility: (id, data) => api.put(`/module-visibility/${id}`, data),

    // --- ADMIN MENU VISIBILITY ---
    getAdminMenuVisibility: (pageCode) => api.get("/admin-menu-visibility", { params: pageCode ? { pageCode } : {} }),
    updateAdminMenuVisibility: (id, data) => api.put(`/admin-menu-visibility/${id}`, data),
    // Upsert po (pageCode, tileCode) — kafelek z katalogu aplikacji może nie mieć jeszcze wiersza.
    setAdminMenuVisibilityByCode: (data) => api.put("/admin-menu-visibility/by-code", data),

    // --- TEAMS CHAT ---
    getTeamsChatConnectionStatus: () => api.get("/teams-chat/connection-status"),
    getTeamsChatUnreadCount: () => api.get("/teams-chat/unread-count"),
    markAllTeamsChatsRead: () => api.post("/teams-chat/mark-all-read", {}),
    markTeamsChatRead: (chatId) => api.post(`/teams-chat/chats/${encodeURIComponent(chatId)}/read`, {}),
    getTeamsChats: (top = 30) => api.get("/teams-chat/chats", { params: { top } }),
    /**
     * [TEAMS-MEDIA] Bajty obrazka inline z rozmowy Teams (proxy backendu).
     *
     * BYŁO ZLE: obrazek ladowal tag <img> z adresu wzglednego wobec korzenia. Przegladarka rozwiazywala
     * go wzgledem origin FRONTU, wiec w srodowisku deweloperskim (front :3000, API :5001) trafial
     * w prozne miejsce, a przy froncie na innej domenie niz API nie mial czym sie uwierzytelnic.
     * SKUTEK DLA UZYTKOWNIKA: w widgecie czatu Teams zdjecia i zrzuty ekranu w ogole sie nie wczytywaly.
     * NAPRAWA: bajty pobiera ten sam klient HTTP, co reszta aplikacji — zna adres API i wysyla
     * ciasteczka. Konsument robi z nich adres blob: (patrz utils/teamsInlineMedia.js).
     *
     * @param {string} sciezka sciezka wzgledem /api, np. "/teams-chat/chats/…/hosted-contents/…"
     */
    getTeamsHostedContent: (sciezka) => api.get(sciezka, { responseType: "blob" }),
    getTeamsChatMessages: (chatId, top = 30, skipToken = null) =>
        api.get(`/teams-chat/chats/${encodeURIComponent(chatId)}/messages`, { params: { top, skipToken } }),
    sendTeamsChatMessage: (chatId, content) =>
        api.post(`/teams-chat/chats/${encodeURIComponent(chatId)}/messages`, { content }),
    unsendTeamsChatMessage: (chatId, messageId) =>
        api.delete(`/teams-chat/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}`),
    sendTeamsChatAttachments: (chatId, content, files) => {
        const fd = new FormData();
        if (content) fd.append("content", content);
        (files || []).forEach((f) => fd.append("files", f));
        // NIE ustawiamy Content-Type ręcznie — axios doda multipart/form-data z poprawnym boundary
        // (ręczne ustawienie bez boundary psuło parsowanie po stronie serwera → pliki puste).
        return api.post(`/teams-chat/chats/${encodeURIComponent(chatId)}/messages/attachments`, fd);
    },
    createTeamsReminder: (data) => api.post("/teams-chat/reminders", data),
    getTeamsPins: (chatId) => api.get(`/teams-chat/chats/${encodeURIComponent(chatId)}/pins`),
    pinTeamsMessage: (data) => api.post("/teams-chat/pins", data),
    unpinTeamsMessage: (chatId, messageId) => api.delete("/teams-chat/pins", { params: { chatId, messageId } }),
    createTeamsChat: (data) => api.post("/teams-chat/chats", data),
    searchTeamsUsers: (q, top = 10) =>
        api.get("/teams-chat/users/search", { params: { q, top } }),
    getTeamsJoinedTeams: () => api.get("/teams-chat/joined-teams"),
    getTeamChannels: (teamId) => api.get(`/teams-chat/teams/${encodeURIComponent(teamId)}/channels`),
    getTeamsPresence: () => api.get("/teams-chat/presence"),

    // ─── HR REQUESTS ───
    // [A15-23] BYŁO ZLE: stał tu nieaktualny komentarz zapowiadający, że endpointy zaczną działać
    // dopiero gdy backend będzie gotowy, a do tego czasu dane wniosków leżą po stronie przeglądarki.
    // SKUTEK DLA CZŁOWIEKA: dotyka programisty — czytał, że wnioski pracownicze trzymane są
    // lokalnie, i szukał ich stanu w pamięci przeglądarki zamiast w bazie.
    // NAPRAWA: opis stanu faktycznego. Wnioski HR chodzą przez /hr-requests po stronie serwera
    // i to jest jedyne źródło prawdy dla modułu.
    getHrRequests: (params) => api.get("/hr-requests", { params }),
    getHrRequest: (id) => api.get(`/hr-requests/${id}`),
    // Dokument wniosku HR (kanoniczny szablon, logo z brandingu, A4) — PDF (blob) + podgląd HTML (WYSIWYG)
    getHrRequestPdf: (id) => api.get(`/hr-requests/${id}/pdf`, { responseType: "blob" }),
    getHrRequestPreviewHtml: (id) => api.get(`/hr-requests/${id}/preview-html`, { responseType: "text" }),
    createHrRequest: (data) => api.post("/hr-requests", data),
    cancelHrRequest: (id, data) => api.post(`/hr-requests/${id}/cancel`, data),
    changeHrRequestStatus: (id, data) => api.patch(`/hr-requests/${id}/status`, data),
    getHrBalances: () => api.get("/hr-requests/balances"),
    getHrTeczkaDocs: (params) => api.get("/hr-requests/teczka", { params }),
    getHrAuditLog: (params) => api.get("/hr-requests/audit-log", { params }),
    // Wsadowa rejestracja interakcji pracownika (klik/otwarcie/zlozenie) — do audytu HR.
    logHrInteractions: (data) => api.post("/hr-requests/interactions", data),
    // Rejestracja wyswietlenia wniosku (timeline "VIEWED", dedup 5 min po stronie backendu).
    trackHrView: (id) => api.post(`/hr-requests/${id}/track-view`),

    // HR Requests — additional
    getHrPendingApprovals: (params) => api.get("/hr-requests/pending", { params }),
    // [zgłoszenie właściciela 2026-09-03, poz. 1d] Ile wniosków czeka na moją decyzję JAKO KADR.
    // Kolejka „Mój Dział" ich nie pokazuje (to nie kompetencja przełożonego) — ekran musi umieć
    // powiedzieć, dokąd poszły, zamiast pozwolić uznać, że zniknęły.
    getHrPendingKadroweCount: () => api.get("/hr-requests/pending/kadrowe"),
    getHrTeamRequests: (params) => api.get("/hr-requests/team", { params }),
    // ── [brief właściciela 2026-08-31, pkt 4.2 i 4.3] ─────────────────────────────────────────
    // KROPKA KOMENTARZY: agregat „ile jest dla mnie nowego" + gaszenie po otwarciu wątku.
    // Kropka NIE może liczyć się z `commentCount` (wtedy nigdy by nie zgasła) — serwer liczy
    // nieodczytane przez TĘ osobę i tylko on wie, kiedy ostatnio je czytała.
    getHrCommentActivity: () => api.get("/hr-requests/comment-activity"),
    markHrCommentsRead: (id) => api.post(`/hr-requests/${id}/comments/read`),
    // ZA KOGO WOLNO ZŁOŻYĆ WNIOSEK — lista liczona serwerowo z zasięgu „Mój Dział". Świadomie NIE
    // budujemy jej z książki adresowej ani z /admin/users: tamte listy nie znają bramki zapisu,
    // więc przeglądarka pokazywałaby osoby, na których POST i tak kończy się 403.
    // `scope` = ekran, z ktorego pyta kreator. "dept" -> serwer liczy liste zasiegiem „Moj Dzial"
    // i NIE stosuje skrotu kadrowego ani rangi administratora (zgloszenie wlasciciela 2026-09-01, pkt 6).
    getHrOnBehalfCandidates: (search, scope) => api.get("/hr-requests/on-behalf-candidates", { params: { search: search || undefined, scope: scope || undefined } }),
    getHrTeamStatusCounts: () => api.get("/hr-requests/team/status-counts"),
    fileHrToTeczka: (id) => api.post(`/hr-requests/${id}/file-to-teczka`),
    // [OBSŁUGA WNIOSKÓW 2026-08-29] „Widzi panel kadr" ≠ „może decydować" — front buduje z tego viewerRole,
    // żeby nie pokazywać akcji, które serwer odrzuci (admin bez mapowania roli dostawał 403 dopiero po kliknięciu).
    getMyHrRole: () => api.get("/hr-requests/my-hr-role"),
    // ── [brief 2026-08-30, pkt 4] ZLECENIA WYPEŁNIENIA WNIOSKU (HR kieruje wniosek do osób) ──
    // scope: "mine" (skierowane do mnie) | "created" (wystawione przeze mnie) | "all" (kadry).
    getHrAssignments: (params) => api.get("/hr-requests/assignments", { params }),
    getHrAssignment: (id) => api.get(`/hr-requests/assignments/${id}`),
    createHrAssignments: (data) => api.post("/hr-requests/assignments", data),
    cancelHrAssignment: (id, reason) => api.post(`/hr-requests/assignments/${id}/cancel`, { reason }),
    // Otwarcie formularza ze zlecenia: status → „w opracowaniu" i jednocześnie sprawdzenie, czy zlecenie
    // wciąż żyje (409, gdy kadry je wycofały albo wniosek już poszedł).
    startHrAssignment: (id) => api.post(`/hr-requests/assignments/${id}/start`),
    // Decyzja zbiorcza na wiązce: jeden ruch, N osobnych decyzji (wnioski bez prawa decyzji wracają w „skipped").
    decideHrBundle: (bundleId, data) => api.post(`/hr-requests/bundles/${bundleId}/decide`, data),

    // ── [brief 2026-08-30, pkt 5] UMOWY I HISTORIA ZATRUDNIENIA ──
    getEmploymentContracts: (userId) => api.get(`/employment-contracts/${userId}`),
    getExpiringEmploymentContracts: (days) => api.get("/employment-contracts/expiring", { params: { days } }),
    createEmploymentContract: (data) => api.post("/employment-contracts", data),
    updateEmploymentContract: (id, data) => api.put(`/employment-contracts/${id}`, data),
    deleteEmploymentContract: (id) => api.delete(`/employment-contracts/${id}`),

    // ── [BE-6b, polecenie właściciela 2026-09-19] ZMIANY DANYCH KADROWYCH — KOLEJKA KADR ──
    // „takie zmiany są raportowane do HR, że XXX zmienił jakąś daną w systemie — HR ma to wiedzieć
    //  i ma mieć status z tego”. Źródłem jest ISTNIEJĄCY dziennik UserAuditLogs (typ KADRY_ZMIANA);
    // serwis nie kopiuje treści zmian nigdzie indziej — osobno trzymany jest wyłącznie STATUS przeglądu.
    getZmianyKadrowe: (params) => api.get("/kadry/zmiany", { params }),
    getZmianyKadrowePodsumowanie: (dni) => api.get("/kadry/zmiany/podsumowanie", { params: { dni } }),
    getZmianyKadroweRozjazdy: () => api.get("/kadry/zmiany/rozjazdy"),
    getZmianyKadrowePola: () => api.get("/kadry/zmiany/pola"),
    ustawStatusZmianyKadrowej: (data) => api.post("/kadry/zmiany/status", data),

    // ── [brief 2026-08-30, pkt 6] SPRAWY KADROWE — poufność i krąg dopuszczonych ──
    // Lista i szczegół sprawy idą zwykłymi endpointami zgłoszeń; TU są wyłącznie czynności poufnościowe.
    getHrCaseAccess: (ticketId) => api.get(`/hr-cases/${ticketId}/access`),
    grantHrCaseAccess: (ticketId, data) => api.post(`/hr-cases/${ticketId}/access`, data),
    revokeHrCaseAccess: (ticketId, userId) => api.delete(`/hr-cases/${ticketId}/access/${userId}`),
    setHrCaseConfidentiality: (ticketId, data) => api.put(`/hr-cases/${ticketId}/confidentiality`, data),

    // Komentarze na wniosku (wątek stron + akceptantów; kasowanie tylko własnych, miękkie).
    getHrRequestComments: (id) => api.get(`/hr-requests/${id}/comments`),
    addHrRequestComment: (id, data) => api.post(`/hr-requests/${id}/comments`, data),
    deleteHrRequestComment: (id, commentId) => api.delete(`/hr-requests/${id}/comments/${commentId}`),

    // HR Config
    getHrCategories: () => api.get("/hr-config/categories"),
    createHrCategory: (data) => api.post("/hr-config/categories", data),
    updateHrCategory: (id, data) => api.put(`/hr-config/categories/${id}`, data),
    deleteHrCategory: (id) => api.delete(`/hr-config/categories/${id}`),

    getHrRequestTypes: () => api.get("/hr-config/request-types"),

    // ── [DELEGACJE 2026-09-12] Rozliczenia zatwierdzonych wniosków, stawki, kursy NBP (api/delegacje) ──
    getRozliczeniaMoje: () => api.get("/delegacje/rozliczenia/moje"),
    getRozliczeniaZespol: () => api.get("/delegacje/rozliczenia/zespol"),
    getRozliczeniaKolejka: (status) => api.get("/delegacje/rozliczenia/kolejka", { params: status ? { status } : {} }),
    // [X3 2026-09-14] Przegląd: czeka na mnie / wszystkie wnioski delegacji / podsumowanie; załączniki całego rozliczenia.
    getRozliczeniaPrzeglad: () => api.get("/delegacje/rozliczenia/przeglad"),
    dodajZalacznikRozliczenia: (id, plik, opis) => {
        const fd = new FormData();
        fd.append("plik", plik);
        if (opis) fd.append("opis", opis);
        return api.post(`/delegacje/rozliczenia/${id}/zalaczniki`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    pobierzZalacznikRozliczenia: (id, zalacznikId) => api.get(`/delegacje/rozliczenia/${id}/zalaczniki/${zalacznikId}`, { responseType: "blob" }),
    usunZalacznikRozliczenia: (id, zalacznikId) => api.delete(`/delegacje/rozliczenia/${id}/zalaczniki/${zalacznikId}`),
    getRozliczeniaPracownika: (employeeId) => api.get(`/delegacje/rozliczenia/pracownik/${employeeId}`),
    getRozliczenie: (id) => api.get(`/delegacje/rozliczenia/${id}`),
    przeliczRozliczenie: (id, dane) => api.post(`/delegacje/rozliczenia/${id}/przelicz`, dane),
    zapiszRozliczenie: (id, dane) => api.put(`/delegacje/rozliczenia/${id}`, dane),
    zlozRozliczenie: (id, dane) => api.post(`/delegacje/rozliczenia/${id}/zloz`, dane),
    oznaczRozliczenieRozliczone: (id, dto) => api.post(`/delegacje/rozliczenia/${id}/rozliczone`, dto || {}),
    zapiszZaliczkeRozliczenia: (id, dto) => api.post(`/delegacje/rozliczenia/${id}/zaliczka`, dto),
    dodajPlikPozycjiRozliczenia: (id, pozycjaId, plik) => {
        const fd = new FormData();
        fd.append("plik", plik);
        return api.post(`/delegacje/rozliczenia/${id}/pozycje/${pozycjaId}/plik`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    },
    pobierzPlikPozycjiRozliczenia: (id, pozycjaId) =>
        api.get(`/delegacje/rozliczenia/${id}/pozycje/${pozycjaId}/plik`, { responseType: "blob" }),
    getStawkiDelegacji: (tylkoAktualne = false) => api.get("/delegacje/stawki", { params: { tylkoAktualne } }),
    zapiszStawkeDelegacji: (dto) => api.post("/delegacje/stawki", dto),
    potwierdzStawkeDelegacji: (id) => api.post(`/delegacje/stawki/${id}/potwierdz`),
    usunStawkeDelegacji: (id) => api.delete(`/delegacje/stawki/${id}`),
    getKursyNbp: (params) => api.get("/delegacje/kursy", { params: params || {} }),
    zapiszKursNbp: (dto) => api.post("/delegacje/kursy", dto),
    pobierzKursZNbp: (waluta, data) => api.post("/delegacje/kursy/nbp", null, { params: { waluta, data } }),
    // [R38 2026-09-13] Ostatni pobrany kurs per waluta; [R37] zatwierdzone, nierozliczone delegacje — start rozliczenia.
    getKursyOstatnie: () => api.get("/delegacje/kursy/ostatnie"),
    getRozliczeniaDoRozliczenia: (employeeId) =>
        api.get("/delegacje/rozliczenia/do-rozliczenia", { params: employeeId ? { employeeId } : {} }),
    // [R39 2026-09-13] Zgodność pól i wzoru pisma — szczegóły niezgodności (które pole, gdzie, co zmienić).
    getZgodnoscWzorow: () => api.get("/hr-config/zgodnosc-wzorow"),
    getZgodnoscWzoru: (id) => api.get(`/hr-config/zgodnosc-wzorow/${id}`),
    createHrRequestType: (data) => api.post("/hr-config/request-types", data),
    updateHrRequestType: (id, data) => api.put(`/hr-config/request-types/${id}`, data),
    deleteHrRequestType: (id) => api.delete(`/hr-config/request-types/${id}`),
    // Historia wersji typu wniosku (podglad + przywrocenie)
    getHrRequestTypeVersions: (id) => api.get(`/hr-config/request-types/${id}/versions`),
    restoreHrRequestTypeVersion: (id, version) => api.post(`/hr-config/request-types/${id}/versions/${version}/restore`),
    // Symulacja sciezki akceptacji ("Pokaz na przykladzie", read-only)
    simulateHrApprovalPath: (data) => api.post("/hr-config/simulate-approval-path", data),
    // Reset wniosku jednorazowego (HR odblokowuje ponowne zlozenie)
    resetHrOneTime: (data) => api.post("/hr-config/reset-one-time", data),
    // Biblioteka klauzul (pouczenia do wzorow pism)
    getHrClauseDefinitions: (includeInactive = false) => api.get("/hr-config/clause-definitions", { params: { includeInactive } }),
    saveHrClauseDefinition: (data) => api.post("/hr-config/clause-definitions", data),
    deleteHrClauseDefinition: (id) => api.delete(`/hr-config/clause-definitions/${id}`),
    // Warianty umow (baza urlopu per forma zatrudnienia)
    getHrContractVariants: (includeInactive = false) => api.get("/hr-config/contract-variants", { params: { includeInactive } }),
    saveHrContractVariant: (data) => api.post("/hr-config/contract-variants", data),
    deleteHrContractVariant: (id) => api.delete(`/hr-config/contract-variants/${id}`),
    // [S-6, polecenie właściciela 2026-09-09] Zakładki per forma zatrudnienia: macierz wariant × moduł + polityka „bez formy”.
    getHrContractGates: () => api.get("/hr-config/contract-gates"),
    saveHrContractGates: (data) => api.put("/hr-config/contract-gates", data),
    // Biblioteka nazwanych sciezek akceptacji (konfigurator HR)
    getHrApprovalPathPresets: (includeInactive = false) => api.get("/hr-config/approval-path-presets", { params: { includeInactive } }),
    saveHrApprovalPathPreset: (data) => api.post("/hr-config/approval-path-presets", data),
    deleteHrApprovalPathPreset: (id) => api.delete(`/hr-config/approval-path-presets/${id}`),
    // Podglad wzoru pisma na danych probnych (WYSIWYG dla edytora bloku)
    renderHrTemplatePreview: (data) => api.post("/hr-requests/render-template-preview", data, { responseType: "text" }),
    // Dane pracodawcy (nazwa/adres/NIP) do chrome edytora wzoru
    getCompanyInfo: () => api.get("/hr-requests/employer-preview"),
    // Biblioteka danych pracownika: prefill formularza (wrazliwe zamaskowane) + widok kartoteki HR
    getHrFieldPrefill: (userId) => api.get(`/hr-requests/prefill/${userId}`),
    getHrDataLibrary: (userId) => api.get(`/hr-requests/data-library/${userId}`),
    // [brief 2026-08-30, pkt 6] Typy wnioskow, ktore pracownikowi REALNIE PRZYSLUGUJA,
    // pogrupowane w kategorie. Zawezenie liczy serwer (kartoteka, salda, jednorazowosc,
    // typy managerskie) — przegladarka nie dostaje pozycji, ktorych nie wolno zlozyc,
    // wiec wyszukiwarka nie ma czego obejsc (audyt M-12).
    getHrAvailableTypes: (forUserId) => api.get("/hr-requests/available-types", { params: forUserId ? { forUserId } : undefined }),
    // [brief 2026-08-29/30, pkt 3] Spolki, DO KTORYCH pracownik moze skierowac wniosek.
    // Jedna pozycja = nie pytamy; kilka = pracownik wybiera i wybor idzie w companyId.
    getHrMyCompanies: () => api.get("/hr-requests/companies"),
    // Slownik wlasciwosci pola (zakresy wyboru osoby, semantyka, klucze techniczne,
    // katalog pol pochodnych) — listy sa EGZEKWOWANE serwerowo, nie duplikujemy ich w kodzie.
    getHrFieldSchema: () => api.get("/hr-config/field-schema"),
    // Pola pochodne dla podanej (jeszcze niezapisanej) definicji pol: { available, present }
    getHrDerivedFields: (fieldsJson) => api.post("/hr-config/derived-fields", { fieldsJson }),

    // Katalog pol formularza (definiowany w systemie)
    getHrFieldDefinitions: (includeInactive = false) => api.get("/hr-config/field-definitions", { params: { includeInactive } }),
    createHrFieldDefinition: (data) => api.post("/hr-config/field-definitions", data),
    updateHrFieldDefinition: (id, data) => api.put(`/hr-config/field-definitions/${id}`, data),
    deleteHrFieldDefinition: (id) => api.delete(`/hr-config/field-definitions/${id}`),

    getHrBalancePools: () => api.get("/hr-config/balance-pools"),
    createHrBalancePool: (data) => api.post("/hr-config/balance-pools", data),
    updateHrBalancePool: (id, data) => api.put(`/hr-config/balance-pools/${id}`, data),
    deleteHrBalancePool: (id) => api.delete(`/hr-config/balance-pools/${id}`),

    getHrUserBalances: (userId, year) => api.get(`/hr-config/balances/${userId}`, { params: { year } }),
    updateHrUserBalance: (data) => api.put("/hr-config/balances", data),
    // HR: przypnij/odepnij pulę urlopu pracownikowi (per rok)
    setHrPoolAssignment: (data) => api.patch("/hr-config/balances/assignment", data),

    // Definicja czasu pracy (daty kadrowe + godziny auto + proporcja urlopu)
    getHrWorkTimeDefinition: (userId, year) => api.get(`/hr-config/worktime-definition/${userId}`, { params: { year } }),
    getMyHrWorkTimeDefinition: (year) => api.get("/hr-config/my-worktime-definition", { params: { year } }),
    getMyHrBalances: (year) => api.get("/hr-config/my-balances", { params: { year } }),

    // ══════════════════════════════════════════════════════════
    //  HR EVALUATIONS
    // ══════════════════════════════════════════════════════════
    createHrEvaluation: (data) => api.post("/hr-requests/evaluations", data),
    getHrEvaluations: (employeeId) => api.get("/hr-requests/evaluations", { params: { employeeId } }),
    getHrEvaluationStats: (employeeIds) => api.get("/hr-requests/evaluations/stats", { params: { employeeIds: Array.isArray(employeeIds) ? employeeIds.join(",") : employeeIds } }),
    updateHrEvaluation: (id, data) => api.put(`/hr-requests/evaluations/${id}`, data),
    approveHrEvaluation: (id) => api.patch(`/hr-requests/evaluations/${id}/approve`),
    // [brief właściciela 2026-08-30] USUNIĘTE: sendHrEvaluationToHr. Endpoint /send-to-hr żyje jeszcze na
    // serwerze, ale odpowiada 410 z powodem — to WYGASZONY kanał „prześlij samą notatkę do HR", który
    // niczego w kadrach nie zakładał. Ocena idzie do HR jako wniosek (createHrRequest + evaluationNoteIds).
    deleteHrEvaluation: (id) => api.delete(`/hr-requests/evaluations/${id}`),
    // SKRZYNKA KADR na ZASZŁOŚCI z wygaszonego kanału „prześlij samą notatkę do HR": notatki z datą
    // przekazania, które nie stały się żadnym wnioskiem. Bez tego ekranu endpointy nie miały konsumenta,
    // a kadry nie miały gdzie zobaczyć notatek, które przełożeni realnie im wysłali.
    // status: "pending" (domyślnie, do przyjęcia) | "filed" (przyjęte do akt) | "all".
    getHrEvaluationsInbox: (params) => api.get("/hr-requests/evaluations/hr-inbox", { params }),
    fileHrEvaluation: (id) => api.post(`/hr-requests/evaluations/${id}/file`),

    // ══════════════════════════════════════════════════════════
    //  DEPARTMENT TODOS
    // ══════════════════════════════════════════════════════════
    getDepartmentTodos: (deptId) => api.get(`/departments/${deptId}/todos`),
    createDepartmentTodo: (deptId, data) => api.post(`/departments/${deptId}/todos`, data),
    updateDepartmentTodo: (deptId, id, data) => api.put(`/departments/${deptId}/todos/${id}`, data),
    toggleDepartmentTodo: (deptId, id) => api.patch(`/departments/${deptId}/todos/${id}/complete`),
    deleteDepartmentTodo: (deptId, id) => api.delete(`/departments/${deptId}/todos/${id}`),

    // ══════════════════════════════════════════════════════════
    //  REMOTE WORK REQUESTS
    // ══════════════════════════════════════════════════════════
    getRemoteWorkRequests: (deptId, params) => api.get(`/departments/${deptId}/remote-work/requests`, { params }),
    createRemoteWorkRequest: (deptId, data) => api.post(`/departments/${deptId}/remote-work/requests`, data),
    approveRemoteWork: (deptId, id) => api.patch(`/departments/${deptId}/remote-work/requests/${id}/approve`),
    rejectRemoteWork: (deptId, id, data) => api.patch(`/departments/${deptId}/remote-work/requests/${id}/reject`, data),
    getRemoteWorkStats: (deptId) => api.get(`/departments/${deptId}/remote-work/stats`),

    // ══════════════════════════════════════════════════════════
    //  VACATION PLANS
    // ══════════════════════════════════════════════════════════
    /* [BLAD Z DZIENNIKA PRODUKCJI 2026-09-12] Koncowki ponizej sa DZIALOWE i stoja za bramka przelozonego
       (`CanManageDepartmentAsync`) — pierwszym argumentem jest identyfikator DZIALU, nie osoby. Warstwa
       uslug kadrowych we froncie podstawiala tam identyfikator zalogowanej osoby, planu albo caly obiekt, przez co adres
       sklejal sie jako `/departments/3/...`, `/departments/undefined/...` albo `/departments/[object Object]/...`
       i serwer odrzucal zadanie na wiazaniu modelu (6 ostrzezen w dzienniku jednego dnia).
       Do pytania „czy JA mam zatwierdzony plan" sluzy `getMojPlanUrlopowy` — wlasne dane, bez identyfikatora. */
    getVacationPlans: (deptId, year) => api.get(`/departments/${deptId}/vacation-plans`, { params: { year } }),
    getMojPlanUrlopowy: (rok) => api.get("/moj-plan-urlopowy", { params: { rok } }),
    saveVacationPlan: (deptId, data) => api.post(`/departments/${deptId}/vacation-plans`, data),
    getPendingVacationPlans: (deptId) => api.get(`/departments/${deptId}/vacation-plans/pending`),
    approveVacationPlan: (deptId, id) => api.patch(`/departments/${deptId}/vacation-plans/${id}/approve`),
    rejectVacationPlan: (deptId, id, data) => api.patch(`/departments/${deptId}/vacation-plans/${id}/reject`, data),

    // ══════════════════════════════════════════════════════════
    //  E-TECZKA
    // ══════════════════════════════════════════════════════════
    // Documents
    createTeczkaDocument: (data) => api.post("/eteczka/documents", data),
    deleteTeczkaDocument: (id) => api.delete(`/eteczka/documents/${id}`),

    // Files
    uploadETeczkaFile: (formData) => api.post("/eteczka/files", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    getETeczkaFiles: (params) => api.get("/eteczka/files", { params }),
    downloadETeczkaFile: (id) => api.get(`/eteczka/files/${id}/download`, { responseType: "blob" }),
    deleteETeczkaFile: (id) => api.delete(`/eteczka/files/${id}`),
    updateETeczkaFileOcr: (id, data) => api.patch(`/eteczka/files/${id}/ocr`, data),
    registerETeczkaFileView: (id) => api.patch(`/eteczka/files/${id}/view`),

    // Trainings
    getETeczkaTrainings: (params) => api.get("/eteczka/trainings", { params }),
    createETeczkaTraining: (data) => api.post("/eteczka/trainings", data),
    updateETeczkaTraining: (id, data) => api.put(`/eteczka/trainings/${id}`, data),
    deleteETeczkaTraining: (id) => api.delete(`/eteczka/trainings/${id}`),

    // Medical Exams
    getETeczkaMedicalExams: (params) => api.get("/eteczka/medical-exams", { params }),
    createETeczkaMedicalExam: (data) => api.post("/eteczka/medical-exams", data),
    updateETeczkaMedicalExam: (id, data) => api.put(`/eteczka/medical-exams/${id}`, data),

    // PPK
    getETeczkaPpk: (employeeId) => api.get("/eteczka/ppk", { params: { employeeId } }),
    upsertETeczkaPpk: (data) => api.put("/eteczka/ppk", data),

    // Employee Profile
    // Lista pracowników WIDOCZNYCH w E-Teczce (kadry/admin: wszyscy, przełożony: zespół).
    // Wcześniej moduł ciągnął listę z /admin/users (AdminOnly) — nie-admin z nadanym ModuleETeczka
    // dostawał 403 i pusty picker, czyli moduł nie działał mimo nadanego uprawnienia.
    getETeczkaEmployees: () => api.get("/eteczka/employees"),
    getETeczkaProfile: (userId) => api.get(`/eteczka/profile/${userId}`),
    updateETeczkaProfile: (userId, data) => api.put(`/eteczka/profile/${userId}`, data),

    // Audit Log
    getETeczkaAuditLog: (params) => api.get("/eteczka/audit-log", { params }),

    // ══════════════════════════════════════════════════════════
    // LISTY PROJEKTOWE (PL-PROJECTS) — odwzorowanie Microsoft Lists
    // (zastąpiło moduł "Zarządzanie projektem" 2026-07-03)
    // ══════════════════════════════════════════════════════════

    // Projekty
    plGetProjects: () => api.get("/pl-projects"),
    plGetDeletedProjects: () => api.get("/pl-projects/deleted"),
    plCreateProject: (data) => api.post("/pl-projects", data),
    plGetProject: (id) => api.get(`/pl-projects/${id}`),
    plUpdateProject: (id, data) => api.put(`/pl-projects/${id}`, data),
    plDeleteProject: (id) => api.delete(`/pl-projects/${id}`),
    plRestoreProject: (id) => api.post(`/pl-projects/${id}/restore`),
    // Kopiowanie projektu (opcje: includeItems/keepAssignments/includeMembers/includeAutomations)
    plCopyProject: (id, data) => api.post(`/pl-projects/${id}/copy`, data || {}),

    // [P3] Zapowiedź projektu dla osoby z linku BEZ dostępu (GET {id} zwraca jej 404) + prośby o dostęp.
    // approve/reject odpowiadają PEŁNĄ kartą projektu (PlProjectDetailsDto) — podmienić nią stan strony.
    plGetProjectPreview: (id) => api.get(`/pl-projects/${id}/preview`),
    plRequestAccess: (id, data) => api.post(`/pl-projects/${id}/access-requests`, data || {}),
    plGetAccessRequests: (id) => api.get(`/pl-projects/${id}/access-requests`),
    plApproveAccessRequest: (requestId, data) => api.post(`/pl-projects/access-requests/${requestId}/approve`, data || {}),
    plRejectAccessRequest: (requestId, data) => api.post(`/pl-projects/access-requests/${requestId}/reject`, data || {}),

    // [FIRMY W PROJEKCIE] Wymiar OPISOWY i STATYSTYCZNY — nie bramka dostępu. Firma nie zawęża
    // ani widoczności projektów, ani puli osób, które wolno dodać do projektu.
    // my-companies = firmy ZALOGOWANEGO (konto ∪ działy); jedna pozycja → nie pytaj, backend ustawi sam.
    plGetMyCompanies: () => api.get("/pl-projects/my-companies"),
    // Firmy uczestniczące projektu: odczyt dla każdego, kto projekt widzi; zapis — właściciel/admin
    // (front pokazuje akcje po PlProjectDetailsDto.canManageCompanies). POST/DELETE oddają PEŁNĄ listę.
    plGetProjectCompanies: (projectId) => api.get(`/pl-projects/${projectId}/companies`),
    plAddProjectCompany: (projectId, companyId) => api.post(`/pl-projects/${projectId}/companies`, { companyId }),
    plRemoveProjectCompany: (projectId, companyId) => api.delete(`/pl-projects/${projectId}/companies/${companyId}`),

    // Szablony (admin tworzy → user wybiera przy tworzeniu)
    plGetTemplates: () => api.get("/pl-projects/templates"),
    plSaveAsTemplate: (id, data) => api.post(`/pl-projects/${id}/save-as-template`, data || {}),
    plCreateFromTemplate: (templateId, data) => api.post(`/pl-projects/templates/${templateId}/create`, data),
    plDeleteTemplate: (templateId) => api.delete(`/pl-projects/templates/${templateId}`),
    // Szablon FIRMOWY widzi cala firma; prywatny — tylko wlasciciel. Mianuje wylacznie admin.
    plSetTemplateGlobal: (templateId, isGlobal) =>
        api.post(`/pl-projects/templates/${templateId}/set-global`, { isGlobal }),

    // Członkowie + aktywność
    plAddMember: (projectId, data) => api.post(`/pl-projects/${projectId}/members`, data),
    plRemoveMember: (projectId, userId) => api.delete(`/pl-projects/${projectId}/members/${userId}`),
    // Aktywnosc idzie PARTIAMI po 15: kursor beforeId (Id malejaco) jest odporny na wpisy
    // dopisywane w trakcie przegladania, w przeciwienstwie do przesuniecia liczbowego.
    plGetActivity: (projectId, { beforeId = null, take = 15 } = {}) =>
        api.get(`/pl-projects/${projectId}/activity`, {
            params: { take, ...(beforeId ? { beforeId } : {}) },
        }),

    // Listy
    plCreateList: (projectId, data) => api.post(`/pl-projects/${projectId}/lists`, data),
    plUpdateList: (listId, data) => api.put(`/pl-projects/lists/${listId}`, data),
    plDeleteList: (listId) => api.delete(`/pl-projects/lists/${listId}`),
    plCopyList: (listId, data) => api.post(`/pl-projects/lists/${listId}/copy`, data || {}),
    plSetListCompleted: (listId, isCompleted) => api.post(`/pl-projects/lists/${listId}/complete`, { isCompleted }),

    // Statusy list
    plCreateStatus: (listId, data) => api.post(`/pl-projects/lists/${listId}/statuses`, data),
    plUpdateStatus: (statusId, data) => api.put(`/pl-projects/statuses/${statusId}`, data),
    plDeleteStatus: (statusId) => api.delete(`/pl-projects/statuses/${statusId}`),
    plReorderStatuses: (listId, statusIds) => api.post(`/pl-projects/lists/${listId}/statuses/reorder`, { statusIds }),

    // Pozycje list
    plCreateItem: (listId, data) => api.post(`/pl-projects/lists/${listId}/items`, data),
    plUpdateItem: (itemId, data) => api.put(`/pl-projects/items/${itemId}`, data),
    plMoveItem: (itemId, statusId, sortOrder) => api.post(`/pl-projects/items/${itemId}/move`, { statusId, sortOrder }),
    plDeleteItem: (itemId) => api.delete(`/pl-projects/items/${itemId}`),
    plGetItem: (itemId) => api.get(`/pl-projects/items/${itemId}`),
    // [P7] Akceptacja przeniesienia na „Zrobione" (właściciel projektu/admin) — odpowiedź to świeży PlItemDto.
    plApproveItemDone: (itemId) => api.post(`/pl-projects/items/${itemId}/approve-done`),
    plRejectItemDone: (itemId, data) => api.post(`/pl-projects/items/${itemId}/reject-done`, data || {}),

    // Komentarze + załączniki pozycji.
    // [P11] `parentCommentId` = odpowiedź na wskazany komentarz TEJ pozycji. Serwer SPŁASZCZA
    // odpowiedź na odpowiedź do rodzica, więc interfejs rysuje najwyżej jeden poziom wcięcia.
    plAddItemComment: (itemId, body, parentCommentId = null) =>
        api.post(`/pl-projects/items/${itemId}/comments`, { body, parentCommentId }),
    // [P13] Kasowanie WYŁĄCZNIE własnego wpisu — cudzy kończy się 403 (od tego jest ukrywanie).
    plDeleteItemComment: (commentId) => api.delete(`/pl-projects/comments/${commentId}`),
    // [P13] Moderacja: ukrycie chowa wpis (razem z odpowiedziami) przed członkami, ale nie kasuje go.
    plHideItemComment: (commentId, reason = null) =>
        api.post(`/pl-projects/comments/${commentId}/hide`, { reason }),
    plUnhideItemComment: (commentId) => api.post(`/pl-projects/comments/${commentId}/unhide`),
    /**
     * ⚠️ [E2 2026-08-30] OPIS JEST WYMAGANY — serwer odrzuca wgranie bez niego kodem 400.
     * To zmiana ŁAMIĄCA wobec poprzedniej wersji tej funkcji (sam plik): „każdy dodany załącznik
     * ma mieć sporządzony opis" (właściciel produktu). Backend i front muszą jechać RAZEM.
     */
    plAddItemAttachment: (itemId, file, { description = "", groupId = null } = {}) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("description", description ?? "");
        if (groupId != null) formData.append("groupId", String(groupId));
        return api.post(`/pl-projects/items/${itemId}/attachments`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    },
    plDeleteItemAttachment: (attachmentId) => api.delete(`/pl-projects/attachments/${attachmentId}`),

    // ═══════════════════════════════════════════════════════════════
    // [E1..E9 2026-08-30] ZAKŁADKA „ZAŁĄCZNIKI" W WITRYNIE PROJEKTU
    //
    // Dwa RODZAJE załączników jadą tymi samymi drogami, ale innymi trasami:
    //   scope 0 = załącznik CAŁEGO projektu   → /pl-projects/project-attachments/{id}/…
    //   scope 1 = załącznik POZYCJI listy     → /pl-projects/attachments/{id}/…
    // Widok zbiorczy trzyma oba w jednej liście (pole `scope` z DTO), więc funkcje niżej
    // przyjmują `scope` i same wybierają trasę — inaczej każdy wołający musiałby to powtarzać.
    // ═══════════════════════════════════════════════════════════════

    /** [E1] CAŁA zawartość zakładki jednym żądaniem (teczki + pliki projektu + pliki pozycji). */
    plGetAttachmentsOverview: (projectId, { refreshOneDrive = false } = {}) =>
        api.get(`/pl-projects/${projectId}/attachments/overview`, { params: { refreshOneDrive } }),

    plAddProjectAttachment: (projectId, file, { description = "", groupId = null } = {}) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("description", description ?? "");
        if (groupId != null) formData.append("groupId", String(groupId));
        return api.post(`/pl-projects/${projectId}/attachments`, formData, { headers: { "Content-Type": "multipart/form-data" } });
    },

    plDeleteAttachment: (scope, attachmentId) =>
        api.delete(`/pl-projects/${Number(scope) === 1 ? "attachments" : "project-attachments"}/${attachmentId}`),

    /** [E2/E5] Opis + teczka. `groupId: null` BEZ `clearGroup` znaczy „nie ruszaj teczki". */
    plUpdateAttachment: (scope, attachmentId, data) =>
        api.put(`/pl-projects/${Number(scope) === 1 ? "attachments" : "project-attachments"}/${attachmentId}`, data),

    /** [E9] Podmiana pliku — nowa wersja w miejscu (opis, teczka i odebrane prawa zostają). */
    plReplaceAttachment: (scope, attachmentId, file) => {
        const formData = new FormData();
        formData.append("file", file);
        return api.post(
            `/pl-projects/${Number(scope) === 1 ? "attachments" : "project-attachments"}/${attachmentId}/replace`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } },
        );
    },

    /**
     * ⚠️ JEDYNA POPRAWNA DROGA DO BAJTÓW ZAŁĄCZNIKA PROJEKTU.
     * Front NIE MOŻE budować adresu z `filePath` (FILES_BASE_URL + ścieżka): statyk ma własną,
     * słabszą bramkę i taki odnośnik omija uprawnienia per załącznik z E6.
     * `inline: true` → podgląd w oknie; `inline: false` → pobranie (przy poziomie „tylko podgląd"
     * serwer odpowie 403 i to jest zamierzone).
     */
    plGetAttachmentBlob: (scope, attachmentId, { inline = false } = {}) =>
        api.get(
            `/pl-projects/${Number(scope) === 1 ? "attachments" : "project-attachments"}/${attachmentId}/content`,
            { responseType: "blob", params: { inline } },
        ),

    // ── [E5] Teczki załączników (byt PER PROJEKT; usunięcie teczki NIE kasuje plików) ──
    plGetAttachmentGroups: (projectId) => api.get(`/pl-projects/${projectId}/attachment-groups`),
    plCreateAttachmentGroup: (projectId, data) => api.post(`/pl-projects/${projectId}/attachment-groups`, data),
    plUpdateAttachmentGroup: (groupId, data) => api.put(`/pl-projects/attachment-groups/${groupId}`, data),
    plDeleteAttachmentGroup: (groupId) => api.delete(`/pl-projects/attachment-groups/${groupId}`),

    // ── [E6] Prawa do jednego załącznika (lista WYKLUCZEŃ; tylko właściciel projektu) ──
    plGetAttachmentPermissions: (scope, attachmentId) =>
        api.get(`/pl-projects/${Number(scope) === 1 ? "attachments" : "project-attachments"}/${attachmentId}/permissions`),
    plSetAttachmentPermission: (scope, attachmentId, data) =>
        api.put(`/pl-projects/${Number(scope) === 1 ? "attachments" : "project-attachments"}/${attachmentId}/permissions`, data),

    // ── [E4/P4c] OneDrive ──
    /** Przypina plik z OneDrive/SharePoint i udostępnia go IMIENNIE członkom projektu w O365. */
    plAddProjectOneDriveAttachment: (projectId, { driveId, itemId, allowWrite = true, description = "", groupId = null }) =>
        api.post(`/pl-projects/${projectId}/attachments/onedrive`, { driveId, itemId, allowWrite, description, groupId }),
    /**
     * [ROZSTRZYGNIĘCIE 7] „Odśwież dostępy" — DOSYŁA imienne zaproszenia obecnym członkom zespołu.
     * Nikomu niczego NIE ODBIERA: kto odszedł z projektu, zachowuje dostęp nadany w Office 365.
     *
     * ⚠️ BEZ `allowWrite` NIE WYSYŁAMY TEGO POLA W OGÓLE — serwer użyje wtedy roli z chwili przypięcia.
     * Domyślne `allowWrite = true` (tak tu było) sprawiało, że odświeżenie dostępów po to, żeby
     * dopuścić jedną nową osobę, nadawało CAŁEMU zespołowi prawo NADPISANIA pliku na prywatnym dysku
     * właściciela — także wtedy, gdy przypinający świadomie zostawił sam odczyt.
     */
    plRefreshOneDriveAccess: (attachmentId, { allowWrite } = {}) =>
        api.post(
            `/pl-projects/project-attachments/${attachmentId}/onedrive/refresh-access`,
            typeof allowWrite === "boolean" ? { allowWrite } : {},
        ),

    // ── [P4b] Pobranie zbiorcze (ZIP). ⚠️ TYLKO WŁAŚCICIEL — zwykły członek dostanie 403,
    //    dlatego przycisk pokazujemy wyłącznie przy `canDownloadAll` z widoku zbiorczego.
    //    Bliźniaczy endpoint dla JEDNEJ pozycji (`items/{id}/attachments/zip`) świadomie NIE ma
    //    tu opakowania: PlItemDetailsDto nie niesie flagi „mogę pobrać wszystko", więc przycisk
    //    w szufladzie byłby zgadywanką kończącą się odmową 403 u zwykłego członka. Zbiorcze
    //    pobranie i tak obejmuje pliki pozycji. ──
    plDownloadProjectAttachmentsZip: (projectId) =>
        api.get(`/pl-projects/${projectId}/attachments/zip`, { responseType: "blob" }),

    // Podzadania pozycji (checklista TODO)
    plAddSubtask: (itemId, data) => api.post(`/pl-projects/items/${itemId}/subtasks`, data),
    plUpdateSubtask: (subtaskId, data) => api.put(`/pl-projects/subtasks/${subtaskId}`, data),
    plToggleSubtask: (subtaskId) => api.post(`/pl-projects/subtasks/${subtaskId}/toggle`),
    plDeleteSubtask: (subtaskId) => api.delete(`/pl-projects/subtasks/${subtaskId}`),

    // Połączenia pozycji (zależności: 0=FS, 1=Blokuje, 2=Powiązane, 3=SS, 4=FF, 5=SF)
    plGetLinks: (projectId) => api.get(`/pl-projects/${projectId}/links`),
    plCreateLink: (projectId, data) => api.post(`/pl-projects/${projectId}/links`, data),
    // Body CZĘŚCIOWE: pole pominięte = bez zmian, `label: ""` czyści opis, `lagDays: null` zwłokę.
    plUpdateLink: (linkId, data) => api.put(`/pl-projects/links/${linkId}`, data),
    plDeleteLink: (linkId) => api.delete(`/pl-projects/links/${linkId}`),

    // KANWA POWIĄZAŃ. `scope=0` = cały projekt, `scope=1` + `listId` = jedna macierz
    // (scope=1 BEZ listId kończy się błędem 400 — patrz plGraphLayout.zapytanieGrafu).
    plGetProjectGraph: (projectId, { scope = 0, listId = null } = {}) =>
        api.get(`/pl-projects/${projectId}/graph`, { params: { scope, ...(listId ? { listId } : {}) } }),
    plGetProjectCanvas: (projectId, { scope = 0, listId = null } = {}) =>
        api.get(`/pl-projects/${projectId}/canvas`, { params: { scope, ...(listId ? { listId } : {}) } }),
    // ⚠️ ZAPIS ZBIORCZY: węzły nieobecne w `nodes` są KASOWANE — wysyłamy pełny stan zakresu.
    plSaveProjectCanvas: (projectId, data) => api.put(`/pl-projects/${projectId}/canvas`, data),

    // Automatyzacje (canManage)
    plGetAutomations: (projectId) => api.get(`/pl-projects/${projectId}/automations`),
    plCreateAutomation: (projectId, data) => api.post(`/pl-projects/${projectId}/automations`, data),
    plUpdateAutomation: (automationId, data) => api.put(`/pl-projects/automations/${automationId}`, data),
    plDeleteAutomation: (automationId) => api.delete(`/pl-projects/automations/${automationId}`),

    // Kamienie milowe
    plCreateMilestone: (projectId, data) => api.post(`/pl-projects/${projectId}/milestones`, data),
    plUpdateMilestone: (milestoneId, data) => api.put(`/pl-projects/milestones/${milestoneId}`, data),
    plDeleteMilestone: (milestoneId) => api.delete(`/pl-projects/milestones/${milestoneId}`),

    // Wątek komentarzy przy kamieniu milowym (licznik jedzie w PlMilestoneDto.commentsCount,
    // treści dociągamy dopiero po rozwinięciu wątku).
    plGetMilestoneComments: (milestoneId) => api.get(`/pl-projects/milestones/${milestoneId}/comments`),
    plAddMilestoneComment: (milestoneId, body) => api.post(`/pl-projects/milestones/${milestoneId}/comments`, { body }),
    plDeleteMilestoneComment: (commentId) => api.delete(`/pl-projects/milestone-comments/${commentId}`),

    // Role członków (0=Właściciel, 1=Członek) — tylko Owner/admin
    plSetMemberRole: (projectId, userId, role) => api.put(`/pl-projects/${projectId}/members/${userId}/role`, { role }),

    // Eksporty witryny (blob → pobranie pliku)
    plExportPdf: (projectId) => api.get(`/pl-projects/${projectId}/export/pdf`, { responseType: "blob" }),
    plExportXlsx: (projectId) => api.get(`/pl-projects/${projectId}/export/xlsx`, { responseType: "blob" }),
    // [P9] ZESTAWIENIE ZBIORCZE — jeden dokument z listą projektów. Trasa LITERALNA
    // (`export/pdf` bez id), więc nie koliduje z raportem pojedynczego projektu.
    // Dokument obejmuje wyłącznie projekty widoczne dla pytającego; filtry mogą tylko zawęzić.
    plExportProjectsDigestPdf: ({ groupId = null, companyId = null, status = null } = {}) =>
        api.get("/pl-projects/export/pdf", {
            responseType: "blob",
            params: {
                ...(groupId ? { groupId } : {}),
                ...(companyId != null ? { companyId } : {}),
                ...(status != null ? { status } : {}),
            },
        }),

    // Administracja (admin type=3 / ProjectAdminAll) — wszystkie projekty, także usunięte
    plAdminGetAll: () => api.get("/pl-projects/admin/all"),

    // ── [ADMIN 2026-08-29] Operacje globalne na KAŻDYM projekcie (klucz ProjectGlobalAdmin) ──
    // ⚠️ Karta admina to JEDYNE wejście działające dla projektu w KOSZU i UKRYTEGO — zwykłe
    // GET /pl-projects/{id} przechodzi przez filtr !IsDeleted i oddaje 404 nawet administratorowi.
    plAdminGetProject: (id) => api.get(`/pl-projects/admin/projects/${id}`),
    // [ZAKRES 2026-09-03] PODGLĄD treści cudzego projektu (listy z pozycjami, kamienie, skład, dziennik)
    // — wyłącznie DO ODCZYTU, także dla projektu w koszu i ukrytego. Odkąd /projekty pokazuje wyłącznie
    // projekty, do których użytkownik ma dostęp, to JEST wejście administracji w cudzy projekt.
    // ⚠️ Każde otwarcie przez osobę spoza projektu zostawia wpis „WGLĄD ADMINISTRATORA" w dzienniku
    // projektu (widzą go właściciele) i w globalnym audycie aplikacji.
    plAdminPreviewProject: (id) => api.get(`/pl-projects/admin/projects/${id}/preview`),
    // Ukrycie: projekt znika WSZYSTKIM, także swoim właścicielom i ich koszowi — zostaje tylko w panelu.
    plAdminHideProject: (id, reason = null) =>
        api.post(`/pl-projects/admin/projects/${id}/hide`, { reason }),
    plAdminUnhideProject: (id) => api.post(`/pl-projects/admin/projects/${id}/unhide`),
    // ⚠️ TRWAŁE usunięcie — nieodwracalne, kasuje też pliki z dysku. `confirmName` musi być DOKŁADNĄ
    // nazwą projektu: bariera stoi po stronie serwera, okno frontu tylko ją odwzorowuje.
    // Metoda POST (nie DELETE) świadomie — ciało żądania bywa ucinane przy DELETE.
    plAdminPurgeProject: (id, confirmName) =>
        api.post(`/pl-projects/admin/projects/${id}/purge`, { confirmName }),
    // Przeniesienie własności = zmiana GOSPODARZA, nie przepisanie historii: autorstwo wpisów,
    // komentarzy i dziennika zostaje przy osobach, które je zrobiły (także odeszłych i zablokowanych).
    plAdminTransferOwnership: (id, newOwnerUserId, reason = null) =>
        api.post(`/pl-projects/admin/projects/${id}/transfer-ownership`, { newOwnerUserId, reason }),

    // [P9] GRUPY PROJEKTÓW (mega-projekty). Zasada soczewki: każda odpowiedź zawiera
    // WYŁĄCZNIE projekty widoczne dla pytającego i liczby policzone tylko z nich —
    // różni użytkownicy celowo widzą różne liczby tej samej grupy.
    plGetGroups: () => api.get("/pl-project-groups"),
    // [ADMIN] WSZYSTKIE grupy (także w koszu) — panel /admin/projects-admin wpina projekt do grupy,
    // której administrator sam nie musi być członkiem, więc zwykłe /pl-project-groups mu nie wystarczy.
    // `state` filtruje SERWEROWO: "all" (domyślnie, także kosz) | "active" | "deleted".
    // Wywołanie BEZ parametru zostaje zgodne wstecz — nieznana wartość to 400 z serwera.
    plAdminGetGroups: (state) =>
        api.get("/pl-project-groups/admin/all", state ? { params: { state } } : undefined),

    // ── [ADMIN GRUP 2026-08-30] Operacje globalne na KAŻDEJ grupie (admin ∨ ProjectGlobalAdmin) ──
    // ⚠️ Podglądowy ProjectAdminAll otwiera wyłącznie odczyt listy wyżej — te trzy odpowiedzą mu 403.
    // Kosz grupy ODPINA jej projekty do stanu „bez grupy" (GroupId=NULL); projektów nie rusza.
    plAdminTrashGroup: (id, reason = null) =>
        api.post(`/pl-project-groups/admin/groups/${id}/delete`, { reason }),
    // Przywrócenie oddaje SAMĄ SKORUPĘ grupy — projekty zostały odpięte i wracają tylko ręcznym wpięciem.
    plAdminRestoreGroup: (id) => api.post(`/pl-project-groups/admin/groups/${id}/restore`),
    // ⚠️ TRWAŁE usunięcie grupy — nieodwracalne. `confirmName` musi być DOKŁADNĄ nazwą grupy:
    // bariera stoi po stronie serwera, okno frontu tylko ją odwzorowuje. POST, nie DELETE — ciało
    // żądania bywa ucinane przy DELETE, a bez potwierdzenia operacja nie może się wykonać.
    plAdminPurgeGroup: (id, confirmName) =>
        api.post(`/pl-project-groups/admin/groups/${id}/purge`, { confirmName }),

    plGetDeletedGroups: () => api.get("/pl-project-groups/deleted"),
    plCreateGroup: (data) => api.post("/pl-project-groups", data),
    plGetGroup: (id) => api.get(`/pl-project-groups/${id}`),
    plUpdateGroup: (id, data) => api.put(`/pl-project-groups/${id}`, data),
    plDeleteGroup: (id) => api.delete(`/pl-project-groups/${id}`),
    plRestoreGroup: (id) => api.post(`/pl-project-groups/${id}/restore`),
    plAddGroupProjects: (id, projectIds) => api.post(`/pl-project-groups/${id}/projects`, { projectIds }),
    plRemoveGroupProject: (id, projectId) => api.delete(`/pl-project-groups/${id}/projects/${projectId}`),
    // Duplikat grupy — ZAWSZE kopiuje też jej projekty (pusta kopia łamałaby regułę „grupa łączy ≥2
    // projekty"). Kopiowane są wyłącznie projekty kopiującego; cudze wracają nazwami w skippedProjectNames.
    // Klonuje N projektów w jednym żądaniu, więc bywa wolne — wołający musi pokazać stan „kopiuję…".
    plCopyGroup: (id, data) => api.post(`/pl-project-groups/${id}/copy`, data || {}),
    plAddGroupMember: (id, data) => api.post(`/pl-project-groups/${id}/members`, data),
    plRemoveGroupMember: (id, userId) => api.delete(`/pl-project-groups/${id}/members/${userId}`),
    plGetGroupTimeline: (id) => api.get(`/pl-project-groups/${id}/timeline`),
    plGetGroupAnalytics: (id) => api.get(`/pl-project-groups/${id}/analytics`),
    // [P9] Zestawienie zbiorcze zawężone do grupy — ten sam dokument co `plExportProjectsDigestPdf`.
    plExportGroupDigestPdf: (id) => api.get(`/pl-project-groups/${id}/export/pdf`, { responseType: "blob" }),

    api: api,
};

// ═══ [FUNDAMENT G/H/I/J/K 2026-09-02, §6.1] JEDYNE miejsce doczepiania warstw API modułów ═══════════════
// Metody modułów (src/api/modules/<modul>.js → attach(api)) lądują na tym samym obiekcie apiService, więc
// strony wołają je jak dotąd: apiService.nazwaMetody(...). Lista modułów: src/api/modules/index.js.
// Kolizja z metodą rdzenia albo między modułami rzuca przy starcie — nic nie nadpisuje się po cichu.
// Ten plik po FALA 0a jest ZAMKNIĘTY: nowe metody pakietu G/H/I/J/K trafiają do plików modułów.
Object.assign(apiService, attachApiModules(api, Object.keys(apiService)));

export default apiService;